/**
 * RefineCodeWorkspace.jsx
 * VS Code-style workbench for Refine Code — standalone, wired to backend.
 *
 * Three phases:
 *   select   → source picker (Build Services run + optional Refactor Code plan)
 *   running  → job is executing (poll /pre-flight/status/{job_id})
 *   workbench → 3-pane IDE: file tree | Monaco editor | context panel
 *
 * Visual strategy:
 *   - Reuses ws-* CSS classes from WorkspaceView.css for the IDE panels
 *   - Reuses rcv-* CSS classes from RefineCodeView.css for select / running phases
 *   - Thin rcw-* CSS layer for anything unique to this component
 *
 * WorkspaceView.jsx is NEVER touched.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Editor, { DiffEditor } from '@monaco-editor/react'
import { Allotment } from 'allotment'
import 'allotment/dist/style.css'
import {
  ChevronRight, ChevronLeft, ChevronDown, Search, X, Download, ArrowRight, ArrowLeft,
  LayoutGrid, FileCode, FolderOpen, Folder, Code2, GitCompare, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, Loader2, Info,
  Check, Clipboard, ClipboardCheck, ExternalLink,
  Play, ToggleLeft, ToggleRight, Sparkles, Layers,
  Coffee, Database, Box, Puzzle, Globe, Settings, FileText, Package, Save,
} from 'lucide-react'
import { SiSpring } from 'react-icons/si'
import {
  VscSymbolInterface, VscSymbolClass,
  VscDatabase, VscSettingsGear, VscFileCode,
} from 'react-icons/vsc'
import { FaJava, FaGlobe } from 'react-icons/fa'
import { useAIResolveAnalyze } from '../../hooks/useAIResolveAnalyze'
import { getAPIBaseUrl } from '../../environment'
import { extractAIPatch } from '../../utils/aiJsonParser'
import { findEnclosingMethod, buildMethodStub } from '../../utils/javaStructureParser'
import AIResponseModal from '../editor/AIResponseModal'
import { generateJavaFromCobol } from '../../services/aiService'
import '../views/WorkspaceView.css'
import './RefineCodeView.css'
import './RefineCodeWorkspace.css'

// ─── Auth helpers ─────────────────────────────────────────────────────────────

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('modernizeit_token')
  const headers = { ...extra }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} }
}

// ─── Java outline parser (mirrors WorkspaceView parseOutlineItems) ─────────────

function parseOutlineItems(content) {
  if (!content || typeof content !== 'string') return []
  const lines = content.split('\n')
  const items = []
  for (let i = 0; i < lines.length; i++) {
    const ln = i + 1
    const trim = lines[i].trim()
    if (!trim || trim.startsWith('//') || trim.startsWith('*')) continue

    // Class / interface / enum
    const classMatch = trim.match(/^(?:public|private|protected|abstract|final|static|\s)*?(class|interface|enum)\s+(\w+)/)
    if (classMatch) {
      items.push({ type: classMatch[1] === 'interface' ? 'interface' : 'class', name: classMatch[2], line: ln })
      continue
    }

    // Method (heuristic: has return type + name + parens, not a flow keyword)
    const methodMatch = trim.match(/^(?:@Override\s+)?(?:public|private|protected|static|final|synchronized|abstract|\s)+[\w<>\[\]]+\s+(\w+)\s*\(/)
    if (methodMatch) {
      const name = methodMatch[1]
      if (!['if', 'for', 'while', 'switch', 'catch', 'try', 'new', 'return', 'throw'].includes(name)) {
        items.push({ type: 'method', name: name + '()', line: ln })
      }
    }
  }
  return items
}

// ─── COBOL outline parser ─────────────────────────────────────────────────────
// Parses paragraph and section names from raw COBOL source.
// COBOL fixed format: Area A = columns 8–11 (0-indexed 7–10).
// Paragraph/section names start in Area A, are uppercase/digit/hyphen, end with ".".
// Division/section keywords are excluded — only procedure-level names are returned.

const COBOL_SKIP = new Set([
  'IDENTIFICATION', 'ENVIRONMENT', 'DATA', 'PROCEDURE',
  'CONFIGURATION', 'INPUT-OUTPUT', 'FILE-CONTROL', 'I-O-CONTROL',
  'FILE', 'WORKING-STORAGE', 'LOCAL-STORAGE', 'LINKAGE', 'SCREEN',
  'REPORT', 'COMMUNICATION', 'SPECIAL-NAMES', 'REPOSITORY',
  'OBJECT-COMPUTER', 'SOURCE-COMPUTER', 'SELECT', 'FD', 'SD',
  // IDENTIFICATION DIVISION entries
  'PROGRAM-ID', 'AUTHOR', 'DATE-WRITTEN', 'DATE-COMPILED',
  'INSTALLATION', 'SECURITY', 'REMARKS',
])

function parseCobolOutline(content) {
  if (!content || typeof content !== 'string') return []
  const lines = content.split('\n')
  const items = []
  let inProcedureDivision = false
  for (let i = 0; i < lines.length; i++) {
    const ln  = i + 1
    const raw = lines[i]
    if (!raw || !raw.trim()) continue

    // Detect PROCEDURE DIVISION to limit scope to actual paragraphs.
    // Some COBOL files have no sequence numbers; check the whole trimmed line.
    if (/PROCEDURE\s+DIVISION/i.test(raw)) { inProcedureDivision = true; continue }
    // Reset on other DIVISION headers
    if (!inProcedureDivision && /DIVISION/i.test(raw)) continue

    // Skip comment lines — col 7 (0-indexed 6) = '*' or '/' in fixed format,
    // or trimmed line starting with '*' for free-format files.
    if (raw.length > 6 && (raw[6] === '*' || raw[6] === '/')) continue
    const trim = raw.trim()
    if (trim.startsWith('*') || trim.startsWith('/')) continue

    // Paragraph/section names start in Area A (cols 8–11, 0-indexed 7–10).
    // Use a generous range (4–14) to handle files with/without sequence numbers.
    const col = raw.search(/\S/)
    if (col < 4 || col > 14) continue

    // Match paragraph: NAME. or NAME SECTION. on a line by itself.
    // Names may start with digits (000-MAIN-CONTROL) or letters (FIND-CUSTOMER).
    const m = trim.match(/^([A-Z0-9][A-Z0-9-]*)(\s+SECTION)?\.\s*$/)
    if (!m) continue
    const name      = m[1]
    const isSection = !!m[2]
    if (COBOL_SKIP.has(name)) continue
    if (/DIVISION/i.test(trim)) continue

    items.push({ name: isSection ? name + ' SECTION' : name, line: ln, isSection })
  }

  // If nothing found inside PROCEDURE DIVISION (e.g. COBOL copybook / data file),
  // fall back to a broader pass without the division gate — catches some edge cases.
  if (items.length === 0) {
    for (let i = 0; i < lines.length; i++) {
      const ln  = i + 1
      const raw = lines[i]
      if (!raw || !raw.trim()) continue
      if (raw.length > 6 && (raw[6] === '*' || raw[6] === '/')) continue
      const trim = raw.trim()
      if (trim.startsWith('*')) continue
      const col = raw.search(/\S/)
      if (col < 4 || col > 14) continue
      const m = trim.match(/^([A-Z0-9][A-Z0-9-]*)(\s+SECTION)?\.\s*$/)
      if (!m) continue
      const name = m[1]
      if (COBOL_SKIP.has(name)) continue
      if (/DIVISION/i.test(trim)) continue
      items.push({ name: m[2] ? name + ' SECTION' : name, line: ln, isSection: !!m[2] })
    }
  }

  return items
}

// ─── Java IDE navigation index builder ───────────────────────────────────────
// Builds definition and reference maps from a single Java file's source text.
// This is a heuristic parser — it covers the common generated-Java patterns
// (class/interface/enum, constructors, methods, fields) without a full AST.
//
// Returns:
//   defs: { [symbolName]: [{ line, col }] }   — declaration locations
//   refs: { [symbolName]: [{ line, col }] }   — usage locations (non-declaration)

const NAV_SKIP = new Set([
  'if','for','while','switch','catch','try','new','return','throw','else','do',
  'finally','assert','import','package','class','interface','enum','record',
  'public','private','protected','static','final','abstract','synchronized',
  'volatile','transient','native','default','extends','implements','instanceof',
  'this','super','null','true','false','void','int','long','double','float',
  'boolean','char','byte','short','String','Object','var',
])

function buildJavaNavIndex(content) {
  if (!content || typeof content !== 'string') return { defs: Object.create(null), refs: Object.create(null) }
  const lines = content.split('\n')

  // Use Object.create(null) — null-prototype dict — so inherited Object.prototype
  // properties (toString, valueOf, constructor, hasOwnProperty, …) are never
  // present and can never collide with Java symbol names of the same name.
  const defs = Object.create(null)  // name → [{line, col}]

  // Helper: safe array check before calling .some()
  const getArr = (map, key) => Array.isArray(map[key]) ? map[key] : null

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1
    const raw  = lines[i]
    const trim = raw.trim()
    if (!trim || trim.startsWith('//') || trim.startsWith('*') || trim.startsWith('/*')) continue

    // Class / interface / enum / record
    const typeMatch = raw.match(/\b(class|interface|enum|record)\s+(\w+)/)
    if (typeMatch) {
      const name = typeMatch[2]
      if (!NAV_SKIP.has(name)) {
        const col = raw.indexOf(name, raw.indexOf(typeMatch[1])) + 1
        if (!getArr(defs, name)) defs[name] = []
        defs[name].push({ line: lineNum, col })
      }
    }

    // Method / constructor:  modifiers* returnType name(
    const methodMatch = raw.match(/(?:(?:public|private|protected|static|final|synchronized|abstract|native|default|override)\s+)*(?:[\w<>\[\].,\s]+\s+)?(\w+)\s*\(/)
    if (methodMatch) {
      const name = methodMatch[1]
      if (name.length > 1 && !NAV_SKIP.has(name) && /^[a-zA-Z_]/.test(name)) {
        const parenIdx = raw.indexOf(name + '(')
        if (parenIdx !== -1) {
          const col = parenIdx + 1
          const arr = getArr(defs, name)
          if (!arr) defs[name] = []
          // Avoid duplicates (same line/col)
          if (!defs[name].some(d => d.line === lineNum && d.col === col)) {
            defs[name].push({ line: lineNum, col })
          }
        }
      }
    }

    // Field: indent + [modifiers] Type name [= | ; | ,]   (not inside method args)
    if (!methodMatch) {
      const fieldMatch = raw.match(/^(\s{2,})(?:(?:private|public|protected|static|final|volatile|transient)\s+)*[\w<>\[\]]+\s+(\w+)\s*(?:[=;,])/)
      if (fieldMatch) {
        const name = fieldMatch[2]
        if (name.length > 1 && !NAV_SKIP.has(name)) {
          const col = raw.lastIndexOf(name) + 1
          const arr = getArr(defs, name)
          if (!arr) defs[name] = []
          if (!defs[name].some(d => d.line === lineNum && d.col === col)) {
            defs[name].push({ line: lineNum, col })
          }
        }
      }
    }
  }

  // Pass 2 — references: every identifier usage that matches a known def name
  const refs = Object.create(null)
  const defNames = Object.keys(defs)
  if (defNames.length > 0) {
    for (let i = 0; i < lines.length; i++) {
      const lineNum = i + 1
      const raw  = lines[i]
      const trim = raw.trim()
      if (!trim || trim.startsWith('//') || trim.startsWith('*')) continue
      for (const name of defNames) {
        const defArr = getArr(defs, name)
        if (!defArr) continue  // guard: skip if somehow not an array
        const re = new RegExp(`\\b${name}\\b`, 'g')
        let m
        while ((m = re.exec(raw)) !== null) {
          const col = m.index + 1
          if (defArr.some(d => d.line === lineNum && d.col === col)) continue // skip own def
          if (!getArr(refs, name)) refs[name] = []
          refs[name].push({ line: lineNum, col })
        }
      }
    }
  }

  return { defs, refs }
}

// ─── File type icons — react-icons (real IDE-quality icons) ──────────────────
// SiJava / SiSpring  → actual brand logos (Simple Icons)
// Vsc*               → VS Code Codicons — same icons VS Code uses in its tree
// No badges. No borders. Just icons.

function FileTypeIcon({ path = '', folder = '' }) {
  const name = path.split('/').pop().toLowerCase()

  // Interface — only explicit: in interfaces/ folder OR name ends with 'interface.java'
  // Never infer from leading 'I' — too many false positives (Ifpr321Domain.java etc.)
  if (folder === 'interfaces' || name.endsWith('interface.java'))
    return <VscSymbolInterface className="rcw-fi-interface" />

  // Domain class — ModernizeIT generated domain objects (*Domain.java, *DomainA.java)
  if (name.endsWith('domain.java') || name.match(/domain[a-z]\.java$/))
    return <VscSymbolClass className="rcw-fi-entity" />

  // Entity / Model — JPA entities and data models
  if (folder === 'entities' || name.endsWith('entity.java') || name.endsWith('model.java'))
    return <VscSymbolClass className="rcw-fi-entity" />

  // Repository — Spring Data repositories
  if (folder === 'repositories' || name.endsWith('repository.java') || name.endsWith('repo.java'))
    return <VscDatabase className="rcw-fi-repo" />

  // Service — Spring service layer
  if (name.endsWith('serviceimpl.java') || name.endsWith('service.java'))
    return <SiSpring className="rcw-fi-service" />

  // Controller — REST controllers
  if (name.endsWith('controller.java'))
    return <FaGlobe className="rcw-fi-controller" />

  // Config — Spring configuration classes
  if (name.endsWith('config.java') || name.endsWith('configuration.java'))
    return <VscSettingsGear className="rcw-fi-config" />

  // Shared — base/shared classes (in shared/ folder)
  if (folder === 'shared')
    return <VscSymbolClass className="rcw-fi-shared" />

  // XML / properties / yml
  if (name.endsWith('.xml') || name.endsWith('.properties') || name.endsWith('.yml'))
    return <VscFileCode className="rcw-fi-xml" />

  // Default Java
  return <FaJava className="rcw-fi-java" />
}

// Alias so existing JSX call sites using <JavaBadge> don't need to change
const JavaBadge = FileTypeIcon

// ─── Root component ───────────────────────────────────────────────────────────

export default function RefineCodeWorkspace({ project, onBack, onGoToJavaPkg, onModeChange, onBackToProjects }) {
  const apiBase     = getAPIBaseUrl()
  const accountId   = project?.account || ''
  const projectName = project?.name    || ''

  // Phase: 'select' | 'running' | 'workbench'
  const [phase,     setPhase]     = useState('select')
  const [jobId,     setJobId]     = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [workbench, setWorkbench] = useState(null)   // kept for WorkbenchIDE compat
  const [wbLoading, setWbLoading] = useState(false)
  const [pollError, setPollError] = useState(null)

  // Phase 2 — editor bundle state
  const [cobolFiles,   setCobolFiles]   = useState([])   // [{name, path, ...}]
  const [javaFiles,    setJavaFiles]    = useState([])   // [{name, path, ...}]
  const [sections,     setSections]     = useState([])   // [{cobol_name, ...}]
  const [bundleJobIds, setBundleJobIds] = useState(null) // {ingestJobId, caJobId, pfJobId}
  const [bundleError,  setBundleError]  = useState(null)

  const pollRef = useRef(null)

  // Phase 9 — fix loop re-run
  const [rerunning,   setRerunning]   = useState(false)
  const [rerunStatus, setRerunStatus] = useState(null)  // null | 'running' | 'passed' | 'failed'
  const rerunPollRef = useRef(null)

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  }, [])

  const stopRerunPoll = useCallback(() => {
    if (rerunPollRef.current) { clearInterval(rerunPollRef.current); rerunPollRef.current = null }
  }, [])

  // Phase 9 — Fix loop: re-run pre-flight compile with workspace edits layered on top.
  // overlay_job_id = current pfJobId → backend loads workspace/ edits from that job's S3 prefix.
  // Backend auto-discovers svcgen/CA upstream — no source job ID needed in the request.
  const handleRerun = useCallback(async () => {
    if (!bundleJobIds?.pfJobId || rerunning) return
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''
    setRerunning(true)
    setRerunStatus('running')
    try {
      const r = await fetch(`${apiBase}/pre-flight/start`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          scout_account_id: accountId,
          application_name: projectName,
          user_id: userId,
          overlay_job_id: bundleJobIds.pfJobId,
        }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      if (!d.job_id) throw new Error('No job_id returned')
      const newJobId = d.job_id
      console.log(`[Phase9] Re-run started: ${newJobId} (overlay: ${bundleJobIds.pfJobId})`)

      // Poll new job until terminal
      const poll = async () => {
        try {
          const sr = await fetch(
            `${apiBase}/pre-flight/status/${encodeURIComponent(newJobId)}`,
            { headers: getAuthHeaders() }
          )
          if (!sr.ok) return
          const sd = await sr.json()
          const terminal = sd.status === 'completed' || sd.status === 'succeeded'
            || sd.status === 'failed' || sd.status === 'compile_error'
          if (terminal) {
            stopRerunPoll()
            const passed = sd.compile_passed === true
            setRerunning(false)
            setRerunStatus(passed ? 'passed' : 'failed')
            // Update jobId + pfJobId — triggers existing Phase 7 compileErrors reload chain
            setJobId(newJobId)
            setBundleJobIds(prev => ({ ...prev, pfJobId: newJobId }))
            console.log(`[Phase9] Re-run complete: ${newJobId} — ${passed ? 'PASSED' : 'FAILED'}`)
          }
        } catch (e) {
          console.warn('[Phase9] poll error:', e.message)
        }
      }
      poll()
      rerunPollRef.current = setInterval(poll, 3000)
    } catch (e) {
      console.error('[Phase9] handleRerun failed:', e.message)
      setRerunning(false)
      setRerunStatus('failed')
    }
  }, [apiBase, accountId, projectName, bundleJobIds, rerunning, stopRerunPoll])

  // Load editor bundle via GET /editor/bundle after pre-flight job reaches terminal state.
  // ingestJobId + caJobId come from the pre-flight status response (resolved server-side
  // by walking pre-flight → SvcGen/CodeRefactor → Code Analysis → Ingest upstream chain).
  // svcgenJobId or crJobId is needed for the Full Project explorer scope (GET /editor/files).
  const loadEditorBundle = useCallback(async (pfJobId, ingestJobId, caJobId, svcgenJobId = null, crJobId = null) => {
    setWbLoading(true)
    setBundleError(null)

    if (!caJobId) {
      const msg = 'code_analysis_job_id not available in pre-flight status — bundle cannot load'
      console.warn('[RefineCodeWorkspace] ' + msg)
      setBundleError(msg)
      setWbLoading(false)
      setPhase('workbench')
      return
    }
    if (!ingestJobId) {
      const msg = 'ingest_job_id not available in pre-flight status — bundle cannot load'
      console.warn('[RefineCodeWorkspace] ' + msg)
      setBundleError(msg)
      setWbLoading(false)
      setPhase('workbench')
      return
    }

    try {
      const url = `${apiBase}/editor/bundle` +
        `?ingest_job_id=${encodeURIComponent(ingestJobId)}` +
        `&code_analysis_job_id=${encodeURIComponent(caJobId)}` +
        `&pre_flight_job_id=${encodeURIComponent(pfJobId)}`

      console.log('[RefineCodeWorkspace] loadEditorBundle →', url)
      const r = await fetch(url, { headers: getAuthHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const bundle = await r.json()

      const cobol = bundle.cobol?.files    || []
      const java  = bundle.java?.files     || []
      const sects = bundle.sections?.data?.sections || []

      console.log(`[RefineCodeWorkspace] bundle loaded — cobol=${cobol.length} java=${java.length} sections=${sects.length}`)
      setCobolFiles(cobol)
      setJavaFiles(java)
      setSections(sects)
      setBundleJobIds({ ingestJobId, caJobId, pfJobId, svcgenJobId, crJobId })
      setPhase('workbench')
    } catch (e) {
      const msg = `Editor bundle load failed: ${e.message}`
      console.error('[RefineCodeWorkspace] ' + msg)
      setBundleError(msg)
      setPhase('workbench') // still transition — WorkbenchIDE renders with empty lists
    } finally {
      setWbLoading(false)
    }
  }, [apiBase])

  // Poll status while job is running
  useEffect(() => {
    if (phase !== 'running' || !jobId) return
    const poll = async () => {
      try {
        const r = await fetch(`${apiBase}/pre-flight/status/${encodeURIComponent(jobId)}`, {
          headers: getAuthHeaders(),
        })
        if (!r.ok) return
        const d = await r.json()
        setJobStatus(d)
        const terminal = d.status === 'completed' || d.status === 'succeeded'
          || d.status === 'failed' || d.status === 'compile_error'
        if (terminal) {
          stopPoll()
          // Pass upstream chain IDs returned by the enhanced status endpoint
          loadEditorBundle(d.job_id, d.ingest_job_id, d.code_analysis_job_id, d.svcgen_job_id, d.code_refactor_job_id)
        }
      } catch (e) {
        setPollError(e.message)
      }
    }
    poll()
    pollRef.current = setInterval(poll, 3000)
    return stopPoll
  }, [phase, jobId, apiBase, stopPoll, loadEditorBundle])

  const handleStarted = useCallback((newJobId) => {
    stopRerunPoll()          // cancel any in-flight re-run poll
    setRerunning(false)
    setRerunStatus(null)
    setJobId(newJobId)
    setPhase('running')
    setPollError(null)
    setBundleError(null)
    setJobStatus(null)
    setWorkbench(null)
    setCobolFiles([])
    setJavaFiles([])
    setSections([])
    setBundleJobIds(null)
  }, [stopRerunPoll])

  const handleContinue = useCallback(() => {
    // Pass svcgenJobId (if known) and the Pre-Flight job ID so JPkg has full context
    const svcgenIds = bundleJobIds?.svcgenJobId ? [bundleJobIds.svcgenJobId] : []
    onGoToJavaPkg?.(svcgenIds, bundleJobIds?.pfJobId ?? null)
  }, [onGoToJavaPkg, bundleJobIds])

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (phase === 'select') {
    return (
      <div className="rcw-shell">
        <SelectPhase
          apiBase={apiBase}
          accountId={accountId}
          projectName={projectName}
          onBack={onBack}
          onStarted={handleStarted}
        />
      </div>
    )
  }

  if (phase === 'running') {
    return (
      <div className="rcw-shell">
        <RunningPhase
          jobStatus={jobStatus}
          error={pollError}
          wbLoading={wbLoading}
          projectName={projectName}
          onBack={() => { stopPoll(); setPhase('select') }}
        />
      </div>
    )
  }

  // workbench phase — full VS Code IDE
  return (
    <WorkbenchIDE
      jobId={jobId}
      apiBase={apiBase}
      workbench={workbench}
      projectName={projectName}
      accountId={accountId}
      cobolFiles={cobolFiles}
      javaFiles={javaFiles}
      sections={sections}
      bundleJobIds={bundleJobIds}
      bundleError={bundleError}
      onContinue={handleContinue}
      onBack={() => setPhase('select')}
      onReload={() => {
        if (bundleJobIds) {
          loadEditorBundle(bundleJobIds.pfJobId, bundleJobIds.ingestJobId, bundleJobIds.caJobId, bundleJobIds.svcgenJobId, bundleJobIds.crJobId)
        }
      }}
      onRerun={handleRerun}
      rerunning={rerunning}
      rerunStatus={rerunStatus}
      onClearRerunStatus={() => setRerunStatus(null)}
    />
  )
}

// ─── SelectPhase ─────────────────────────────────────────────────────────────
// Two-option source picker:
//   A) Build Services — start a new Refine Code session from the latest SvcGen run
//   B) Refactor Code  — start from a finalized Refactor Code plan (includes SvcGen upstream)
// User picks ONE source. The system auto-finds the latest for this project.

function SelectPhase({ apiBase, accountId, projectName, onBack, onStarted }) {
  // source: 'svcgen' | 'refactor'
  const [source, setSource] = useState('refactor')   // default to Refactor Code

  // Build Services
  const [svcgenJobs,    setSvcgenJobs]    = useState([])
  const [svcgenLoading, setSvcgenLoading] = useState(true)
  const [svcgenError,   setSvcgenError]   = useState(null)
  const [selectedSvcgen, setSelectedSvcgen] = useState(null)
  const [showSvcgenList,  setShowSvcgenList]  = useState(false)

  // Refactor Code
  const [rfctJobs,    setRfctJobs]    = useState([])
  const [rfctLoading, setRfctLoading] = useState(true)
  const [selectedRfct, setSelectedRfct] = useState(null)
  const [showRfctList,  setShowRfctList]  = useState(false)

  const [starting,   setStarting]  = useState(false)
  const [startError, setStartError]= useState(null)

  // Load both sources upfront so cards are ready when user clicks
  useEffect(() => {
    if (!accountId) return
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    // SvcGen jobs
    const svcParams = new URLSearchParams({ account_id: accountId, user_id: userId })
    if (projectName) svcParams.set('application_name', projectName)
    fetch(`${apiBase}/refactor-code/available-svcgen-jobs?${svcParams}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setSvcgenJobs(list)
        if (list.length > 0) { setSelectedSvcgen(list[0].job_id); setSource('svcgen') }
      })
      .catch(e => setSvcgenError(String(e)))
      .finally(() => setSvcgenLoading(false))

    // Refactor Code plans — prefer this project, fall back to all for account
    const rfctParams = new URLSearchParams({ account_id: accountId, user_id: userId })
    fetch(`${apiBase}/refine-code/available-refactor-jobs?${rfctParams}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        // Try to filter to this project first (exact, then partial match)
        const exact   = projectName ? list.filter(j => (j.application_name || '').toLowerCase() === projectName.toLowerCase()) : []
        const partial = projectName ? list.filter(j => (j.application_name || '').toLowerCase().includes(projectName.toLowerCase()) || projectName.toLowerCase().includes((j.application_name || '').toLowerCase())) : []
        // Use the best match set; if nothing matches fall back to the whole account list
        const best = exact.length > 0 ? exact : partial.length > 0 ? partial : list
        setRfctJobs(best)
        if (best.length > 0) setSelectedRfct(best[0].job_id)
      })
      .catch(() => setRfctJobs([]))
      .finally(() => setRfctLoading(false))
  }, [apiBase, accountId, projectName]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async () => {
    setStarting(true)
    setStartError(null)
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''
    const body = {
      scout_account_id: accountId,
      application_name: projectName,
      user_id: userId,
      overlay_job_id: null,   // null = fresh run; set to prior job_id for fix-loop re-runs (Phase 9)
    }
    if (source === 'refactor' && selectedRfct) {
      body.refactor_job_id = selectedRfct
    } else {
      body.svcgen_job_id = selectedSvcgen
    }
    try {
      const r = await fetch(`${apiBase}/pre-flight/start`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      if (!d.job_id) throw new Error('No job_id returned')
      onStarted(d.job_id)
    } catch (e) {
      setStartError(e.message)
      setStarting(false)
    }
  }

  const shortDate = (iso) => {
    if (!iso) return ''
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) }
    catch { return String(iso).slice(0, 10) }
  }

  const isLoading = svcgenLoading || rfctLoading
  const canStart  = !isLoading && !starting && (
    (source === 'svcgen' && !!selectedSvcgen) ||
    (source === 'refactor' && !!selectedRfct)
  )

  const selSvcgenJob = svcgenJobs.find(j => j.job_id === selectedSvcgen)
  const selRfctJob   = rfctJobs.find(j => j.job_id === selectedRfct)

  return (
    <div className="rcw-select-phase">
      <div className="rcw-select-card">

        {/* Header */}
        <div className="rcw-select-head">
          <Code2 size={18} className="rcw-select-head-icon" />
          <div>
            <div className="rcw-select-head-title">Refine Code Workbench</div>
            {projectName && <div className="rcw-select-head-project">{projectName}</div>}
          </div>
          {onBack && (
            <button className="rcw-select-back" onClick={onBack}>
              <ArrowLeft size={13} /> Back
            </button>
          )}
        </div>

        <div className="rcw-select-prompt">Where would you like to start?</div>

        {/* ── Two source options ── */}
        <div className="rcw-source-grid">

          {/* Option A — Build Services (disabled — Refactor Code is the required path) */}
          <div className="rcw-source-option rcw-source-option--disabled" aria-disabled="true">
            <div className="rcw-source-option-header">
              <div className="rcw-source-radio">
                <div className="rcw-pick-circle" />
              </div>
              <div className="rcw-source-option-title">Build Services</div>
              <span className="rcw-source-option-badge">Coming soon</span>
            </div>
            <div className="rcw-source-option-desc">
              Start from your latest generated Java services
            </div>
          </div>

          {/* Option B — Refactor Code */}
          <div
            className={`rcw-source-option ${source === 'refactor' ? 'selected' : ''} ${rfctJobs.length === 0 && !rfctLoading ? 'unavailable' : ''}`}
            onClick={() => rfctJobs.length > 0 && setSource('refactor')}
            role="button" tabIndex={0}
            onKeyDown={e => e.key === ' ' && rfctJobs.length > 0 && setSource('refactor')}
          >
            <div className="rcw-source-option-header">
              <div className={`rcw-source-radio ${source === 'refactor' ? 'on' : ''}`}>
                {source === 'refactor' ? <CheckCircle2 size={16} /> : <div className="rcw-pick-circle" />}
              </div>
              <div className="rcw-source-option-title">Refactor Code</div>
              {rfctLoading && <Loader2 size={12} className="rcv-spin rcw-source-spin" />}
            </div>
            <div className="rcw-source-option-desc">
              Apply a finalized Refactor Code analysis with confirmed changes
            </div>
            {!rfctLoading && rfctJobs.length === 0 && (
              <div className="rcw-source-option-none">No finalized plans for this project</div>
            )}
            {selRfctJob && !showRfctList && (
              <div className="rcw-source-job-row">
                <span className="rcw-source-job-label">
                  {selRfctJob.confirmed_count} confirmed · {shortDate(selRfctJob.completed_at)}
                </span>
                {rfctJobs.length > 1 && (
                  <button className="rcw-source-change-btn"
                    onClick={e => { e.stopPropagation(); setShowRfctList(true) }}>
                    Change ▾
                  </button>
                )}
              </div>
            )}
            {showRfctList && (
              <div className="rcw-job-picker" onClick={e => e.stopPropagation()}>
                {rfctJobs.map(j => (
                  <button key={j.job_id}
                    className={`rcw-job-pick-row ${selectedRfct === j.job_id ? 'active' : ''}`}
                    onClick={() => { setSelectedRfct(j.job_id); setShowRfctList(false); setSource('refactor') }}>
                    {selectedRfct === j.job_id ? <CheckCircle2 size={13} /> : <div className="rcw-pick-circle" />}
                    <span className="rcw-pick-label">{j.confirmed_count} confirmed · {shortDate(j.completed_at)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>

        {startError && (
          <div className="rcw-select-err">
            <AlertTriangle size={13} /> {startError}
          </div>
        )}

        <button
          className={`rcw-start-btn ${canStart ? '' : 'disabled'}`}
          onClick={handleStart}
          disabled={!canStart}
        >
          {starting
            ? <><Loader2 size={14} className="rcv-spin" /> Starting…</>
            : <><Play size={14} /> Open Refine Code Workbench</>}
        </button>

      </div>
    </div>
  )
}

// ─── RunningPhase ─────────────────────────────────────────────────────────────

function RunningPhase({ jobStatus, error, wbLoading, projectName, onBack }) {
  const status = jobStatus?.status || 'running'
  const phase  = jobStatus?.current_phase || null

  // Pre-flight status fields (available once job completes or fails)
  const compilePassed     = jobStatus?.compile_passed     ?? null
  const errorCount        = jobStatus?.errors             ?? null
  const javaFilesChecked  = jobStatus?.java_files_checked ?? null

  const phaseLabels = {
    phase_1: 'Setting up workspace…',
    phase_2: 'Loading refinement plan…',
    phase_3: 'Copying Java files to S3…',
    phase_4: 'Applying patches…',
    phase_5: 'Running Maven compile…',
    phase_6: 'Packaging refined code…',
    phase_7: 'Writing final artifacts…',
  }

  const phaseLabel = (phase && phaseLabels[phase]) || 'Running Maven compile…'
  const isTerminal = status === 'failed' || status === 'compile_error'

  return (
    <div className="rcv-running-phase">
      <div className="rcv-running-card">
        {wbLoading ? (
          <>
            <Loader2 size={32} className="rcv-running-spinner" />
            <div className="rcv-running-title">Loading workbench…</div>
          </>
        ) : (
          <>
            {!isTerminal
              ? <Loader2 size={32} className="rcv-running-spinner" />
              : <AlertTriangle size={32} className="rcv-running-error-icon" />}
            <div className="rcv-running-title">
              {isTerminal
                ? (compilePassed === false ? 'Compile finished with errors' : 'Pre-Flight finished with issues')
                : 'Pre-Flight compile is running'}
            </div>
            {projectName && (
              <div className="rcv-running-project">{projectName}</div>
            )}
            {!isTerminal && (
              <div className="rcv-running-phase-label">{phaseLabel}</div>
            )}
            {/* Pre-flight result summary — shown when terminal */}
            {isTerminal && errorCount !== null && (
              <div className="rcv-running-compile-summary">
                {compilePassed
                  ? <span className="rcv-compile-pass">✓ Compile passed</span>
                  : <span className="rcv-compile-fail">✗ {errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
                {javaFilesChecked !== null && (
                  <span className="rcv-compile-files">{javaFilesChecked} file{javaFilesChecked !== 1 ? 's' : ''} checked</span>
                )}
              </div>
            )}
            {error && (
              <div className="rcv-running-error">
                <AlertTriangle size={14} /> {error}
              </div>
            )}
            <button className="rcw-running-back-btn" onClick={onBack}>
              <ArrowLeft size={13} /> Back to source picker
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── WorkbenchIDE ─────────────────────────────────────────────────────────────
// Full VS Code-style 3-pane IDE: File Tree | Monaco Editor | Context Panel

function WorkbenchIDE({
  jobId, apiBase, workbench, projectName, accountId = '',
  // Phase 2 — editor bundle data (populated after pre-flight completes)
  cobolFiles = [], javaFiles = [], sections = [],
  bundleJobIds = null, bundleError = null,
  onContinue, onBack, onReload,
  // Phase 9 — fix loop
  onRerun = null, rerunning = false, rerunStatus = null,
  // Reset cleanup
  onClearRerunStatus = null,
}) {
  // File state
  const [fileCache,     setFileCache]     = useState({})        // {path: content}
  const [openTabs,      setOpenTabs]      = useState([])         // [{path, label}]
  const [activeTabPath, setActiveTabPath] = useState(null)
  const [fileLoading,   setFileLoading]   = useState(false)
  const [fileError,     setFileError]     = useState(null)

  // Flow nav menu
  const [showFlowMenu, setShowFlowMenu] = useState(false)

  // Tree state
  const [treeFilter,       setTreeFilter]       = useState('')
  const [treeExpanded,     setTreeExpanded]     = useState({})
  const [treeRootExpanded, setTreeRootExpanded] = useState(true)

  // Right panel
  const [rightTab,     setRightTab]     = useState('outline')   // 'outline' | 'compile' | 'patches'
  const [patchReport,  setPatchReport]  = useState(null)
  const [compileReport,setCompileReport]= useState(null)
  const [outlineItems, setOutlineItems] = useState([])

  // Sections Navigator (Phase 4)
  const [sectionFilter,    setSectionFilter]    = useState('')
  const [activeSectionIdx, setActiveSectionIdx] = useState(null) // index into sections[]
  const [sectionsPaneTab,  setSectionsPaneTab]  = useState('sections') // 'sections' | 'cobol'

  // COBOL Editor (Phase 5)
  const [cobolContent, setCobolContent] = useState(null)   // null = not loaded yet
  // Feature 3 — COBOL outline: paragraphs + sections parsed from raw cobolContent.
  // Re-derived whenever cobolContent changes (new file loaded).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cobolOutlineItems = useMemo(() => parseCobolOutline(cobolContent), [cobolContent])
  const [cobolLoading, setCobolLoading] = useState(false)
  const [cobolError,   setCobolError]   = useState(null)
  const [cobolFile,    setCobolFile]    = useState(null)   // active cobol file object
  const cobolEditorRef = useRef(null)

  // Java Editor (Phase 6)
  const [javaContent,   setJavaContent]   = useState(null)   // null = not loaded yet
  const [javaLoading,   setJavaLoading]   = useState(false)
  const [javaError,     setJavaError]     = useState(null)
  const [javaFile,      setJavaFile]      = useState(null)   // active java file object
  const [modifiedPaths, setModifiedPaths] = useState(new Set())
  // Save confirm modal: null | { path, content, filename }
  const [saveConfirmModal, setSaveConfirmModal] = useState(null)
  // Inline save result banner: null | { type: 'ok'|'err', msg }
  const [saveBanner, setSaveBanner] = useState(null)
  const javaEditorRef    = useRef(null)
  const saveTimerRef     = useRef(null)
  const baselineContentRef = useRef({})          // { [path]: string } — content at load time (pre-edits)
  const [diffMode,    setDiffMode]    = useState(false)
  const [diffSnapshot, setDiffSnapshot] = useState(null) // { original, modified, path } | null

  // Phase 7 — compile error highlights
  const [compileErrors,      setCompileErrors]      = useState([])   // data.errors_preview — rows/decorations (≤ 100)
  const [compileTotalErrors, setCompileTotalErrors] = useState(0)    // data.total_errors   — true Maven error count
  const [selectedError,      setSelectedError]      = useState(null) // selected error → full-line highlight + AI modal
  const decorationsRef      = useRef([])                      // Monaco decoration IDs for compile errors
  const qualityDecorationsRef = useRef([])                    // Monaco decoration IDs for quality warnings (separate so they don't clobber errors)
  const monacoRef           = useRef(null)                    // Monaco instance ref
  const compileErrorsRef    = useRef([])                      // live ref — avoids stale closure in onMouseDown
  const selectedErrorRef    = useRef(null)                    // live ref for selectedError in onMouseDown handler

  // Feature 6: Conversion Quality Indicators
  const [qualityWarnings, setQualityWarnings] = useState([])  // [{javaMethod, cobolName, severity, patterns:[{id,label,desc,severity}]}]

  // Java IDE navigation (definition + reference providers)
  // javaNavIndexRef    — { [path]: { defs: {name:[{line,col}]}, refs: {name:[{line,col}]} } }
  //                      Populated whenever javaContent changes (one entry per visited file).
  // javaNavModelsRef   — { [path]: ITextModel }
  //                      Monaco models keyed by path; enables cross-file Peek to show content.
  //                      Models are created/updated in sync with javaNavIndexRef.
  // javaNavProvidersRef — { def: IDisposable, ref: IDisposable }
  //                      Registered once on first Java editor mount; disposed on unmount.
  // javaFileRef        — mirrors javaFile state so provider closures (registered once) see
  //                      the current file without stale-closure issues.
  const javaNavIndexRef     = useRef({})
  const javaNavModelsRef    = useRef({})
  const javaNavProvidersRef = useRef(null)
  const javaFileRef         = useRef(null)

  // Bidirectional COBOL ↔ Java cursor sync
  // javaMethodBoundariesRef — [{name, startLine, endLine}] rebuilt on every javaContent change.
  //   Used to find which Java method contains the cursor without re-parsing on every keystroke.
  // bidirSyncSourceRef — 'java' | 'cobol' | 'section' | null
  //   Prevents feedback: when Java cursor triggers a COBOL scroll, the resulting COBOL cursor
  //   event must not re-trigger the Java side (and vice versa).
  // cursorSyncTimerRef — debounce handle (150 ms quiet period) so we don't fire on every
  //   character typed or arrow key held down.
  // sectionsRef — live mirror of sections[] prop so cursor handlers (registered once) always
  //   see the latest list without stale closures.
  const javaMethodBoundariesRef = useRef([])   // [{name, startLine, endLine}]
  const bidirSyncSourceRef      = useRef(null) // 'java' | 'cobol' | 'section' | null
  const cursorSyncTimerRef      = useRef(null)
  const sectionsRef             = useRef([])   // live mirror of sections[]
  const codeLensJumpCmdRef      = useRef(null) // Monaco command ID for code-lens jump-to-COBOL
  const codeLensFireRefreshRef  = useRef(null) // () => void — fires onDidChange to force lens re-render

  // Phase 8 — Explorer sub-tabs
  const [explorerTab,       setExplorerTab]       = useState('files') // 'issues' | 'changes' | 'files'
  const [explorerScope,     setExplorerScope]     = useState('focused') // 'focused' | 'full'
  const pendingScrollLineRef = useRef(null)                            // line to jump after async file load

  // Full Project explorer — lazy-loaded on first 'full' scope selection
  const [allJavaFiles,        setAllJavaFiles]        = useState([])    // normalized file list from /editor/files
  const [allJavaFilesLoading, setAllJavaFilesLoading] = useState(false)

  // Explorer right-click context menu
  const [explorerCtxMenu,      setExplorerCtxMenu]      = useState(null) // null | {x, y, file}
  const [explorerRenameState,  setExplorerRenameState]  = useState(null) // null | {path, value}
  const [explorerNewFileState, setExplorerNewFileState] = useState(null) // null | {folder, value}

  // Feature 8 — Project-wide search
  const [searchQuery,    setSearchQuery]    = useState('')
  const [searchResults,  setSearchResults]  = useState(null)  // null = not yet searched
  const [searchLoading,  setSearchLoading]  = useState(false)
  const searchContentRef = useRef({})  // { [path]: string } — content cache to avoid re-fetching

  // Pane collapse controls — visible={false} approach (space fully redistributed by Allotment)
  const [filesCollapsed,    setFilesCollapsed]    = useState(false)
  const [sectionsCollapsed, setSectionsCollapsed] = useState(false)
  const [cobolCollapsed,    setCobolCollapsed]    = useState(false)
  const [javaCollapsed,     setJavaCollapsed]     = useState(false)

  // Feature 5: Data Dictionary popup
  const [dataDictOpen,    setDataDictOpen]    = useState(false)
  const [dataDictData,    setDataDictData]    = useState(null)   // { fields, groups, conditions, stats, source_file }
  const [dataDictLoading, setDataDictLoading] = useState(false)
  const [dataDictError,   setDataDictError]   = useState(null)
  const [dataDictFilter,  setDataDictFilter]  = useState('')
  const [dataDictHideFiller, setDataDictHideFiller] = useState(true)
  const [dataDictFullscreen, setDataDictFullscreen] = useState(false)
  const [dataDictSort, setDataDictSort] = useState({ col: 'line', dir: 'asc' })

  // When COBOL pane is restored, give Allotment one frame to redistribute width
  // then force Monaco to recalculate its layout dimensions.
  useEffect(() => {
    if (!cobolCollapsed) {
      setTimeout(() => cobolEditorRef.current?.layout(), 50)
    }
  }, [cobolCollapsed])

  // Close explorer context menu on click-outside or Escape
  useEffect(() => {
    if (!explorerCtxMenu) return
    const close = () => setExplorerCtxMenu(null)
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [explorerCtxMenu])

  // Auto-switch scope: focused when there are errors, full when compile is clean
  useEffect(() => {
    setExplorerScope(compileErrors.length > 0 ? 'focused' : 'full')
  }, [compileErrors.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Java IDE navigation — keep javaFileRef in sync ─────────────────────────
  useEffect(() => { javaFileRef.current = javaFile }, [javaFile])

  // Keep sectionsRef in sync so cursor-sync handlers (registered once at editor mount)
  // always see the latest sections list without needing to re-register.
  useEffect(() => {
    sectionsRef.current = sections
    const mapped = sections.filter(s => s.java_method && s.java_method !== 'unknown')
    console.log('[BiDirSync] sections loaded:', sections.length, '| mapped java_method:', mapped.length,
      mapped.length ? '| sample: ' + mapped.slice(0,3).map(s => s.java_method).join(', ') : '')
    codeLensFireRefreshRef.current?.()
  }, [sections])

  // Rebuild Java method boundaries whenever the active Java file content changes.
  // Produces [{name, startLine, endLine}] sorted by startLine so we can binary-search
  // for the method containing a given cursor line in O(log n).
  // "endLine" is estimated as one line before the next method starts (or EOF).
  useEffect(() => {
    if (!javaContent) { javaMethodBoundariesRef.current = []; return }
    const items = parseOutlineItems(javaContent).filter(it => it.type === 'method')
    const boundaries = items.map((item, i) => ({
      name:      item.name.replace(/\(\)$/, ''),  // strip trailing "()" → plain name for matching
      startLine: item.line,
      endLine:   items[i + 1] ? items[i + 1].line - 1 : 999999,
    }))
    javaMethodBoundariesRef.current = boundaries
    console.log('[BiDirSync] boundaries built:', boundaries.length, 'methods from', javaFile?.name,
      boundaries.length ? '| sample: ' + boundaries.slice(0,3).map(b => b.name + '@' + b.startLine).join(', ') : '')
    codeLensFireRefreshRef.current?.()
  }, [javaContent]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Feature 6: Quality analysis — scan COBOL sections for risky patterns ──
  useEffect(() => {
    if (!cobolContent || !sections.length) { setQualityWarnings([]); return }

    const PATTERNS = [
      { id: 'alter',      regex: /\bALTER\b/i,                severity: 'high', label: 'ALTER',               desc: 'Modifies GO TO targets at runtime — extremely difficult to translate safely' },
      { id: 'goto',       regex: /\bGO\s+TO\b/i,              severity: 'warn', label: 'GO TO',               desc: 'Unstructured control flow — verify Java translation preserves execution order' },
      { id: 'occurs_dep', regex: /OCCURS\s+DEPENDING\s+ON/i,  severity: 'warn', label: 'OCCURS DEPENDING ON', desc: 'Dynamic array size — verify Java List/array sizing is correct' },
      { id: 'evaluate',   regex: /\bEVALUATE\s+TRUE\b/i,      severity: 'warn', label: 'EVALUATE TRUE',       desc: 'Complex conditional — review the generated if/else chain' },
      { id: 'call',       regex: /\bCALL\b/i,                  severity: 'info', label: 'EXTERNAL CALL',       desc: 'Calls external COBOL program — verify Java service wiring is correct' },
      { id: 'comp3',      regex: /\bCOMP-3\b|\bPACKED-DECIMAL\b/i, severity: 'info', label: 'COMP-3',        desc: 'Packed decimal — verify BigDecimal precision and scale' },
      { id: 'perform_var',regex: /\bPERFORM\s+VARYING\b/i,    severity: 'info', label: 'PERFORM VARYING',     desc: 'Loop with varying index — check Java loop bounds' },
    ]

    const cobolLines = cobolContent.split('\n')
    const warnings = []

    for (const sect of sections) {
      if (!sect.java_method || sect.java_method === 'unknown') continue
      const start = Math.max(0, (sect.cobol_start_line || 1) - 1)
      const end   = Math.min(cobolLines.length - 1, (sect.cobol_end_line || start) - 1)
      const sectionText = cobolLines.slice(start, end + 1)
        .filter(l => {
          if (l.length > 6 && (l[6] === '*' || l[6] === '/')) return false  // skip comment lines
          return true
        })
        .join('\n')

      const found = PATTERNS.filter(p => p.regex.test(sectionText))
      if (!found.length) continue

      const maxSeverity = found.some(p => p.severity === 'high') ? 'high'
        : found.some(p => p.severity === 'warn') ? 'warn' : 'info'

      warnings.push({ javaMethod: sect.java_method, cobolName: sect.cobol_name, severity: maxSeverity, patterns: found })
    }

    setQualityWarnings(warnings)
    console.log('[Quality]', warnings.length, 'method(s) flagged:', warnings.filter(w => w.severity === 'high').length, 'high,', warnings.filter(w => w.severity === 'warn').length, 'warn,', warnings.filter(w => w.severity === 'info').length, 'info')
  }, [cobolContent, sections])

  // ── Feature 6: Apply quality glyph decorations to Java editor ──
  useEffect(() => {
    const editor = javaEditorRef.current
    if (!editor) return
    if (!qualityWarnings.length) {
      qualityDecorationsRef.current = editor.deltaDecorations(qualityDecorationsRef.current, [])
      return
    }
    const monaco = monacoRef.current
    if (!monaco) return
    const boundaries = javaMethodBoundariesRef.current
    const newDecos = []

    for (const w of qualityWarnings) {
      const boundary = boundaries.find(b => b.name === w.javaMethod)
      if (!boundary) continue
      const hoverMd = [
        `**Review Required** — ${w.patterns.length} flag${w.patterns.length !== 1 ? 's' : ''}`,
        '',
        ...w.patterns.map(p => `**${p.label}**: ${p.desc}`),
      ].join('\n\n')
      newDecos.push({
        range: new monaco.Range(boundary.startLine, 1, boundary.startLine, 1),
        options: {
          glyphMarginClassName: `rcw-quality-glyph rcw-quality-glyph--${w.severity}`,
          glyphMarginHoverMessage: { value: hoverMd },
          overviewRuler: {
            color: w.severity === 'high' ? '#ef4444' : w.severity === 'warn' ? '#f59e0b' : '#eab308',
            position: monaco.editor.OverviewRulerLane.Left,
          },
          zIndex: 1,  // below compile errors (which use Right lane)
        },
      })
    }
    qualityDecorationsRef.current = editor.deltaDecorations(qualityDecorationsRef.current, newDecos)
    console.log('[Quality] applied', newDecos.length, 'glyph decoration(s)')
  }, [qualityWarnings, javaContent])

  // ── Java IDE navigation — rebuild index + Monaco model when content changes ──
  // Index is keyed by path; each entry contains defs + refs maps.
  // A Monaco ITextModel is created at mz-java:///path so cross-file Peek can
  // render the target file's source inline without opening it in the editor.
  useEffect(() => {
    if (!javaContent || !javaFile?.path) return
    const path = javaFile.path

    // Rebuild symbol index for this file
    javaNavIndexRef.current[path] = buildJavaNavIndex(javaContent)

    // Create or update Monaco model (monacoRef may not be set yet on first render)
    const monaco = monacoRef.current
    if (!monaco) return
    const uri = monaco.Uri.parse(`mz-java:///${path}`)
    const existing = javaNavModelsRef.current[path]
    if (existing && !existing.isDisposed()) {
      existing.setValue(javaContent)
    } else {
      // Dispose any stale disposed model first
      if (existing) delete javaNavModelsRef.current[path]
      const model = monaco.editor.createModel(javaContent, 'java', uri)
      javaNavModelsRef.current[path] = model
    }
    console.log(`[JavaNav] indexed ${path} — defs: ${Object.keys(javaNavIndexRef.current[path].defs).length}`)
  }, [javaContent, javaFile?.path]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Java IDE navigation — dispose providers + models on WorkbenchIDE unmount ─
  useEffect(() => {
    return () => {
      try { javaNavProvidersRef.current?.def?.dispose()   } catch { /* ignore */ }
      try { javaNavProvidersRef.current?.ref?.dispose()   } catch { /* ignore */ }
      try { javaNavProvidersRef.current?.lens?.dispose()  } catch { /* ignore */ }
      try { javaNavProvidersRef.current?.hover?.dispose() } catch { /* ignore */ }
      javaNavProvidersRef.current  = null
      codeLensFireRefreshRef.current = null
      for (const model of Object.values(javaNavModelsRef.current)) {
        try { if (!model.isDisposed()) model.dispose() } catch { /* ignore */ }
      }
      javaNavModelsRef.current = {}
      javaNavIndexRef.current  = {}
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load full project file list when scope switches to 'full'.
  // Fires on first switch and whenever pfJobId changes (re-run).
  // Response shape: [{ path, type, source }] — normalize to match javaFiles shape.
  useEffect(() => {
    if (explorerScope !== 'full') return
    if (!bundleJobIds?.pfJobId) return
    if (allJavaFiles.length > 0) return  // already loaded for this pfJobId

    const load = async () => {
      setAllJavaFilesLoading(true)
      try {
        const params = new URLSearchParams({
          gate_job_id: bundleJobIds.pfJobId,
          scope:       'full',
        })
        if (bundleJobIds.svcgenJobId) {
          params.set('upstream_svcgen_job_id', bundleJobIds.svcgenJobId)
        } else if (bundleJobIds.crJobId) {
          // Refactor Code path — no SvcGen, use code_refactor for full project listing
          params.set('upstream_code_refactor_job_id', bundleJobIds.crJobId)
        }
        console.log(`[FullProject] fetching /editor/files?${params}`)
        const r = await fetch(`${apiBase}/editor/files?${params}`, {
          headers: getAuthHeaders(),
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        // Normalize: add job_id + editor_scope so loadJavaFile works.
        // Use the per-file job_id from the response (set by sibling aggregation)
        // so /editor/java/file reads from the correct SvcGen job's artifact path.
        // Fall back to pfJobId only if the backend omits job_id (older API versions).
        const normalized = (Array.isArray(data) ? data : [])
          .filter(f => f.type === 'file' && f.path?.endsWith('.java'))
          .map(f => ({
            path:         f.path,
            name:         f.path.split('/').pop(),
            type:         'file',
            source:       f.source || 'base',
            job_id:       f.job_id || bundleJobIds.pfJobId,
            editor_scope: 'full',
          }))
        console.log(`[FullProject] loaded ${normalized.length} java files`)
        setAllJavaFiles(normalized)
      } catch (e) {
        console.error('[FullProject] /editor/files failed:', e.message)
      } finally {
        setAllJavaFilesLoading(false)
      }
    }
    load()
  }, [explorerScope, bundleJobIds?.pfJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear allJavaFiles when pfJobId changes (re-run produces a new workspace)
  useEffect(() => {
    setAllJavaFiles([])
  }, [bundleJobIds?.pfJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 13 — Compile Report view
  const [showReport, setShowReport] = useState(false)

  // Phase 10 — AI Fix modal
  const [aiFixOpen, setAiFixOpen] = useState(false)

  const analysisContext = useMemo(() => {
    const u = getStoredUser()
    return {
      scout_account_id: accountId,
      user_id:          u.user_id || u.id || '',
      application_name: projectName,
    }
  }, [accountId, projectName])

  const {
    analyzeState, startAnalyze, applyFix, dismiss,
    STATE_LOADING, STATE_READY, STATE_APPLYING, STATE_APPLIED, STATE_ERROR,
  } = useAIResolveAnalyze(analysisContext, apiBase, null)

  // Trigger startAnalyze whenever the modal opens (or selectedError changes while open)
  useEffect(() => {
    if (!aiFixOpen || !selectedError || !bundleJobIds?.pfJobId) return
    const targetFile = selectedError.file
    const fileErrors = compileErrors.filter(e => e.file === targetFile)
    startAnalyze(bundleJobIds.pfJobId, targetFile, fileErrors)
  }, [aiFixOpen, selectedError]) // eslint-disable-line react-hooks/exhaustive-deps

  // Apply AI fix → reload Monaco model from workspace/ so the patch is visible
  const handleApplyFix = useCallback(async (card) => {
    if (!bundleJobIds?.pfJobId) return
    await applyFix(card, bundleJobIds.pfJobId)
    // If the fixed file is the one currently open in the Java editor, reload it
    if (javaFile && card.file === javaFile.path) {
      try {
        const params = new URLSearchParams({
          job_id:       javaFile.job_id,
          path:         javaFile.path,
          editor_scope: 'focused',
          gate_job_id:  bundleJobIds.pfJobId,
        })
        const r = await fetch(`${apiBase}/editor/java/file?${params}`, { headers: getAuthHeaders() })
        if (r.ok) {
          const d = await r.json()
          const newContent = d.content ?? ''
          javaEditorRef.current?.getModel()?.setValue(newContent)
          setJavaContent(newContent)
          setModifiedPaths(prev => new Set([...prev, javaFile.path]))
          console.log('[Phase10] editor model updated after apply fix')
        }
      } catch (e) {
        console.warn('[Phase10] reload after apply failed (non-fatal):', e.message)
      }
    }
  }, [apiBase, bundleJobIds, javaFile, applyFix])

  const handleCloseAIModal = useCallback(() => {
    dismiss()
    setAiFixOpen(false)
    setSelectedError(null)
    // compileErrors intentionally preserved — decorations stay until re-run clears them
  }, [dismiss])

  // Phase 11 — Explain modal (COBOL + Java AI Explain)
  const [explainModal, setExplainModal] = useState({
    open: false, loading: false, text: null, error: null, selectionInfo: '', language: 'COBOL',
  })

  // Shared core: POST to /ai/flow ai-explain, update explainModal with result
  const _aiExplainRequest = useCallback(async (code, selectionInfo, language) => {
    setExplainModal({ open: true, loading: true, text: null, error: null, selectionInfo, language })
    try {
      const storedUser = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()
      const userId = storedUser.user_id || storedUser.id || ''
      const r = await fetch(`${apiBase}/ai/flow`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          scout_account_id: accountId,
          application_name: projectName,
          user_id:          userId,
          node_type:        'ai-explain',
          context_source:   'flow_input',
          code,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      const raw  = d.output?.documentation ?? d.output ?? 'No explanation returned.'
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)
      setExplainModal(prev => ({ ...prev, loading: false, text }))
    } catch (e) {
      console.error(`[Phase11] AI Explain (${language}) failed:`, e.message)
      setExplainModal(prev => ({ ...prev, loading: false, error: e.message }))
    }
  }, [apiBase, accountId, projectName])

  const handleExplainCobol = useCallback(async (code, selectionInfo) => {
    await _aiExplainRequest(code, selectionInfo, 'COBOL')
  }, [_aiExplainRequest])

  // ── AI: Generate Java Code (COBOL → Java) ─────────────────────────────────
  const [generateJavaModal, setGenerateJavaModal] = useState({
    isOpen: false, selectedCode: '', sectionName: '', loading: false, response: null,
  })

  const handleGenerateJavaFromCobol = useCallback(async (selectedCode, sectionName) => {
    setGenerateJavaModal({ isOpen: true, selectedCode, sectionName, loading: true, response: null })
    const storedUser = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()
    const userId = storedUser.user_id || storedUser.id || ''
    try {
      const response = await generateJavaFromCobol(selectedCode, 'editor-selection', sectionName, {
        scout_account_id: accountId,
        application_name: projectName,
        user_id: userId,
      })
      setGenerateJavaModal(prev => ({ ...prev, loading: false, response }))
    } catch (e) {
      setGenerateJavaModal(prev => ({ ...prev, loading: false, response: { error: e.message } }))
    }
  }, [accountId, projectName])

  const handleRegenerateJava = useCallback(() => {
    const { selectedCode, sectionName } = generateJavaModal
    if (!selectedCode) return
    handleGenerateJavaFromCobol(selectedCode, sectionName)
  }, [generateJavaModal, handleGenerateJavaFromCobol])

  const handleGenerateJavaRef = useRef(null)
  useEffect(() => { handleGenerateJavaRef.current = handleGenerateJavaFromCobol }, [handleGenerateJavaFromCobol])

  // ── Batch B1 — Java AI Explain (Explain Issue + Cmd+Shift+E)
  const handleExplainJava = useCallback(() => {
    const editor = javaEditorRef.current
    const monaco = monacoRef.current
    if (!editor || !monaco) return

    const model     = editor.getModel()
    const selection = editor.getSelection()
    const position  = editor.getPosition()
    const lineNum   = position?.lineNumber ?? 1

    let code, selectionInfo

    // Use selection if non-empty, otherwise ±5 lines around cursor
    if (selection && !selection.isEmpty()) {
      code          = model.getValueInRange(selection)
      selectionInfo = `lines ${selection.startLineNumber}–${selection.endLineNumber}`
    } else {
      const startLine = Math.max(1, lineNum - 5)
      const endLine   = Math.min(model.getLineCount(), lineNum + 5)
      code = model.getValueInRange({
        startLineNumber: startLine, startColumn: 1,
        endLineNumber:   endLine,   endColumn:   model.getLineMaxColumn(endLine),
      })
      selectionInfo = `line ${lineNum}`
    }

    if (!code?.trim()) return

    // Append compile error context if one exists on the cursor line
    const filePath = javaFile?.path || ''
    const err = compileErrorsRef.current.find(e => e.file === filePath && e.line === lineNum)
    const explainCode = err
      ? `// File: ${filePath}\n// Line ${lineNum} — Compile error: ${err.message || err.summary || ''}\n\n${code}`
      : `// File: ${filePath}\n// Line ${lineNum}\n\n${code}`

    const label = err
      ? `line ${lineNum} — ${err.message || err.summary || 'compile error'}`
      : selectionInfo

    console.log('[BatchB1] handleExplainJava:', { lineNum, hasError: !!err, selectionInfo })
    _aiExplainRequest(explainCode, label, 'Java')
  }, [javaEditorRef, monacoRef, javaFile, compileErrorsRef, _aiExplainRequest])

  // Batch B2 — AI: Suggest Refactoring
  const handleRefactorJava = useCallback(() => {
    const editor = javaEditorRef.current
    const monaco = monacoRef.current
    if (!editor || !monaco) return

    const model     = editor.getModel()
    const selection = editor.getSelection()
    const lineNum   = editor.getPosition()?.lineNumber ?? 1

    let code, selectionInfo

    if (selection && !selection.isEmpty()) {
      code          = model.getValueInRange(selection)
      selectionInfo = `lines ${selection.startLineNumber}–${selection.endLineNumber}`
    } else {
      const startLine = Math.max(1, lineNum - 10)
      const endLine   = Math.min(model.getLineCount(), lineNum + 10)
      code = model.getValueInRange({
        startLineNumber: startLine, startColumn: 1,
        endLineNumber:   endLine,   endColumn:   model.getLineMaxColumn(endLine),
      })
      selectionInfo = `line ${lineNum} ± context`
    }

    if (!code?.trim()) return

    const refactorCode =
      `// Request: Suggest refactoring improvements for this Java code.\n` +
      `// File: ${javaFile?.path || 'unknown'} — ${selectionInfo}\n\n` +
      code

    console.log('[BatchB2] handleRefactorJava:', { lineNum, selectionInfo })
    _aiExplainRequest(refactorCode, `Refactoring — ${selectionInfo}`, 'Java')
  }, [javaEditorRef, monacoRef, javaFile, _aiExplainRequest])

  // Batch B3 — AI: Clean Code
  const [cleanCodeModal, setCleanCodeModal] = useState(null)
  // Shape: null (closed) | { loading, cleaned, filename, error }

  const handleCleanCodeJava = useCallback(async () => {
    const editor = javaEditorRef.current
    if (!editor || !javaFile) return

    const content  = editor.getValue()
    const filename = javaFile.name || javaFile.path || 'file.java'
    if (!content?.trim()) return

    setCleanCodeModal({ loading: true, cleaned: null, filename, error: null })

    try {
      const storedUser = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()
      const userId = storedUser.user_id || storedUser.id || ''
      const r = await fetch(`${apiBase}/ai/flow`, {
        method:  'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          scout_account_id: accountId,
          application_name: projectName,
          user_id:          userId,
          node_type:        'ai-transform',
          context_source:   'flow_input',
          code:             content,
          config: {
            targetLanguage: 'java',
            prompt: 'Clean the formatting and indentation of this Java source file. Return only the reformatted code.',
          },
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()

      // ai-transform returns d.output.transformed_code (confirmed from ai_flow.py)
      let cleaned = d.output?.transformed_code ?? d.output ?? ''
      if (typeof cleaned !== 'string') cleaned = JSON.stringify(cleaned)

      // Strip markdown fences if AI wrapped the code
      cleaned = cleaned.replace(/^```(?:java)?\n?/i, '').replace(/\n?```\s*$/i, '').trim()

      if (!cleaned) throw new Error('AI returned empty content')

      if (cleaned === content.trim()) {
        setCleanCodeModal(null)
        console.log('[BatchB3] AI Clean Code: no changes detected')
        return
      }

      setCleanCodeModal({ loading: false, cleaned, filename, error: null })
      console.log('[BatchB3] AI Clean Code: preview ready for', filename)
    } catch (e) {
      console.error('[BatchB3] AI Clean Code failed:', e.message)
      setCleanCodeModal(prev => prev ? { ...prev, loading: false, error: e.message } : null)
    }
  }, [apiBase, accountId, projectName, javaEditorRef, javaFile])

  // Persist Java edits — declared here so all handlers below (Clean Code, Repair, Stub)
  // can reference it without hitting the TDZ (saveJavaFile was previously declared after them).
  //   PUT /editor/file  body: { ingest_job_id, code_analysis_job_id, pre_flight_job_id,
  //                              relative_path, content, editor_scope }
  // markClean=true → removes path from modifiedPaths on S3 success (explicit user save).
  // markClean=false (default) → silent background write; dirty indicator unchanged.
  // Returns true on success, false on failure.
  const saveJavaFile = useCallback(async (path, content, { markClean = false } = {}) => {
    if (!bundleJobIds) return false
    try {
      const body = {
        ingest_job_id:        bundleJobIds.ingestJobId,
        code_analysis_job_id: bundleJobIds.caJobId,
        pre_flight_job_id:    bundleJobIds.pfJobId,
        relative_path:        path,
        content,
        editor_scope:         'focused',
      }
      const r = await fetch(`${apiBase}/editor/file`, {
        method:  'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify(body),
      })
      if (!r.ok) {
        console.warn(`[Java] save failed HTTP ${r.status} for ${path}`)
        return false
      }
      if (markClean) {
        setModifiedPaths(prev => { const next = new Set(prev); next.delete(path); return next })
      }
      console.log(`[Java] saved ${path}${markClean ? ' (explicit — marked clean)' : ' (auto)'}`)
      return true
    } catch (e) {
      console.error('[Java] save error:', e.message)
      return false
    }
  }, [apiBase, bundleJobIds])

  // Accept & Insert generated Java → inserts at Java editor cursor, then saves
  const handleAcceptGeneratedJava = useCallback((code) => {
    const editor = javaEditorRef.current
    const monaco = monacoRef.current
    if (!editor || !monaco || !code) return
    const position  = editor.getPosition()
    const selection = editor.getSelection()
    const range = selection && !selection.isEmpty()
      ? selection
      : new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column)
    editor.executeEdits('rcw-generate-java', [{ range, text: '\n' + code + '\n', forceMoveMarkers: true }])
    editor.focus()
    if (javaFile) saveJavaFile(javaFile.path, editor.getValue())
    setGenerateJavaModal(prev => ({ ...prev, isOpen: false }))
  }, [javaEditorRef, monacoRef, javaFile, saveJavaFile])

  const handleApplyCleanCode = useCallback(() => {
    const editor  = javaEditorRef.current
    const cleaned = cleanCodeModal?.cleaned
    if (!editor || !cleaned || !javaFile) return

    const model     = editor.getModel()
    const fullRange = model.getFullModelRange()
    editor.executeEdits('ai-clean-code', [{ range: fullRange, text: cleaned, forceMoveMarkers: true }])
    editor.focus()

    // Auto-save — consistent with new workbench pattern
    saveJavaFile(javaFile.path, cleaned)
    setCleanCodeModal(null)
    console.log('[BatchB3] AI Clean Code applied + saved:', javaFile.path)
  }, [javaEditorRef, cleanCodeModal, javaFile, saveJavaFile])

  // ── Batch B4 — Repair Actions ──────────────────────────────────────────────
  // repairModal shape: null | { actionId, label, loading, patch, previewText, error, contextCode, filename }
  const [repairModal, setRepairModal] = useState(null)

  // Helper: get code lines around a 1-indexed line number (inclusive both ends)
  const _getCodeAroundLine = useCallback((content, line1, radius) => {
    const lines = content.split('\n')
    const start = Math.max(0, line1 - 1 - radius)
    const end   = Math.min(lines.length, line1 + radius)
    return { code: lines.slice(start, end).join('\n'), startLine: start + 1, endLine: end }
  }, [])

  // Helper: build the CUSTOM_EDIT JSON contract prompt
  const _buildRepairPrompt = useCallback((contextCode, filename, line, errorSummary, actionLabel) => {
    return `You are fixing a Java compile error in ${filename}.
${errorSummary ? `Compile error: ${errorSummary}` : ''}
Action: ${actionLabel}

Return ONLY this JSON object, no other text:
{
  "action": "CUSTOM_EDIT",
  "file": "${filename}",
  "start_line": <1-indexed start line to replace>,
  "end_line": <1-indexed end line to replace (inclusive)>,
  "replacement_text": "<corrected code for those lines>",
  "confidence": <0.0-1.0>,
  "explanation": "<one sentence: what was wrong and what was changed>"
}

Rules:
- start_line and end_line must be integers within the provided code snippet line range
- replacement_text replaces exactly lines start_line through end_line in the file
- Return ONLY the JSON object — no markdown, no prose before or after

Code (around line ${line}):`
  }, [])

  // Helper: POST to /ai/chat and extract patch
  const _callRepairAI = useCallback(async (contextCode, filename, line, errorSummary, actionLabel) => {
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''
    const prompt = _buildRepairPrompt(contextCode, filename, line, errorSummary, actionLabel)

    const r = await fetch(`${apiBase}/ai/chat`, {
      method:  'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        prompt,
        code:             contextCode,
        purpose:          'custom',
        context:          { file: filename, line, issue_summary: errorSummary || '' },
        scout_account_id: accountId,
        application_name: projectName,
        user_id:          userId,
      }),
    })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const result = await r.json()
    return extractAIPatch(result)
  }, [apiBase, accountId, projectName, _buildRepairPrompt])

  // Helper: apply a parsed CUSTOM_EDIT patch to the Java editor + auto-save
  const _applyRepairToEditor = useCallback((patch) => {
    const editor = javaEditorRef.current
    if (!editor || !patch || !javaFile) return
    const monaco = monacoRef.current
    if (!monaco) return

    const model = editor.getModel()
    if (!model) return

    const lineCount = model.getLineCount()
    const startLine = Math.max(1, patch.start_line)
    const endLine   = Math.min(lineCount, patch.end_line ?? patch.start_line)
    const endCol    = model.getLineMaxColumn(endLine)

    const range = new monaco.Range(startLine, 1, endLine, endCol)
    editor.executeEdits('mz-repair', [{
      range,
      text:              patch.replacement_text,
      forceMoveMarkers:  true,
    }])
    editor.focus()
    editor.revealLineInCenter(startLine)

    const updatedContent = editor.getValue()
    saveJavaFile(javaFile.path, updatedContent)
    console.log('[BatchB4] repair applied + saved:', javaFile.path, `L${startLine}–${endLine}`)
  }, [javaEditorRef, monacoRef, javaFile, saveJavaFile])

  // Helper: open modal and optionally auto-trigger Generate
  const _openRepairModal = useCallback((actionId, label, contextCode, filename) => {
    setRepairModal({ actionId, label, loading: false, patch: null, previewText: null, error: null, contextCode, filename })
  }, [])

  // Helper: run the AI call and populate the modal
  const _generateRepair = useCallback(async (actionId, label, contextCode, filename, line, errorSummary) => {
    setRepairModal(prev => prev ? { ...prev, loading: true, patch: null, previewText: null, error: null } : null)
    try {
      const patch = await _callRepairAI(contextCode, filename, line, errorSummary, label)
      if (!patch) throw new Error('AI response did not contain a valid CUSTOM_EDIT patch')
      setRepairModal(prev => prev
        ? { ...prev, loading: false, patch, previewText: patch.replacement_text }
        : null
      )
      console.log('[BatchB4] repair patch ready:', actionId, patch)
    } catch (e) {
      console.error('[BatchB4] repair failed:', actionId, e.message)
      setRepairModal(prev => prev ? { ...prev, loading: false, error: e.message } : null)
    }
  }, [_callRepairAI])

  // MZ: AI Fix (Safe Patch) — cursor line ±8
  const handleAISafePatch = useCallback(() => {
    const editor = javaEditorRef.current
    if (!editor || !javaFile) return
    const content  = editor.getValue()
    const line1    = editor.getPosition()?.lineNumber ?? 1
    const filename = javaFile.name || javaFile.path?.split('/').pop() || 'file.java'
    const { code, startLine, endLine } = _getCodeAroundLine(content, line1, 8)
    const err = compileErrorsRef.current?.find(e => (e.file === filename || e.file === javaFile.path) && e.line === line1)
    const errorSummary = err ? `${err.message} (line ${err.line})` : `(cursor at line ${line1})`
    const label = 'MZ: AI Fix (Safe Patch)'
    setRepairModal({ actionId: 'safe-patch', label, loading: true, patch: null, previewText: null, error: null, contextCode: code, filename })
    _generateRepair('safe-patch', label, code, filename, startLine, errorSummary)
  }, [javaEditorRef, javaFile, compileErrorsRef, _getCodeAroundLine, _generateRepair])

  // MZ: AI Repair Region — cursor line ±30
  const handleAIRepairRegion = useCallback(() => {
    const editor = javaEditorRef.current
    if (!editor || !javaFile) return
    const content  = editor.getValue()
    const line1    = editor.getPosition()?.lineNumber ?? 1
    const filename = javaFile.name || javaFile.path?.split('/').pop() || 'file.java'
    const { code, startLine } = _getCodeAroundLine(content, line1, 30)
    const err = compileErrorsRef.current?.find(e => (e.file === filename || e.file === javaFile.path) && e.line === line1)
    const errorSummary = err ? `${err.message} (line ${err.line})` : `(region around line ${line1})`
    const label = 'MZ: AI Repair Region'
    setRepairModal({ actionId: 'repair-region', label, loading: true, patch: null, previewText: null, error: null, contextCode: code, filename })
    _generateRepair('repair-region', label, code, filename, startLine, errorSummary)
  }, [javaEditorRef, javaFile, compileErrorsRef, _getCodeAroundLine, _generateRepair])

  // MZ: AI Repair Method — findEnclosingMethod or ±20 fallback
  const handleAIRepairMethod = useCallback(() => {
    const editor = javaEditorRef.current
    if (!editor || !javaFile) return
    const content  = editor.getValue()
    const line1    = editor.getPosition()?.lineNumber ?? 1
    const filename = javaFile.name || javaFile.path?.split('/').pop() || 'file.java'

    let code, startLine
    const method = findEnclosingMethod(content, line1)
    if (method) {
      const lines = content.split('\n')
      code      = lines.slice(method.startLine - 1, method.endLine).join('\n')
      startLine = method.startLine
      console.log('[BatchB4] AI Repair Method: found enclosing method', method.startLine, '–', method.endLine)
    } else {
      const fallback = _getCodeAroundLine(content, line1, 20)
      code      = fallback.code
      startLine = fallback.startLine
      console.log('[BatchB4] AI Repair Method: fallback ±20 lines around', line1)
    }

    const err = compileErrorsRef.current?.find(e => (e.file === filename || e.file === javaFile.path) && e.line === line1)
    const errorSummary = err ? `${err.message} (line ${err.line})` : `(method containing line ${line1})`
    const label = 'MZ: AI Repair Method'
    setRepairModal({ actionId: 'repair-method', label, loading: true, patch: null, previewText: null, error: null, contextCode: code, filename })
    _generateRepair('repair-method', label, code, filename, startLine, errorSummary)
  }, [javaEditorRef, javaFile, compileErrorsRef, _getCodeAroundLine, _generateRepair])

  // MZ: AI Repair File Structure — full file ≤8000 chars
  const handleAIRepairFileStructure = useCallback(() => {
    const editor = javaEditorRef.current
    if (!editor || !javaFile) return
    const content  = editor.getValue()
    const filename = javaFile.name || javaFile.path?.split('/').pop() || 'file.java'
    const code     = content.length > 8000 ? content.slice(0, 8000) : content
    const firstErr = compileErrorsRef.current?.find(e => e.file === filename || e.file === javaFile.path)
    const errorSummary = firstErr ? `${firstErr.message} (line ${firstErr.line})` : '(file-level structural issues)'
    const label = 'MZ: AI Repair File Structure'
    setRepairModal({ actionId: 'repair-file-structure', label, loading: true, patch: null, previewText: null, error: null, contextCode: code, filename })
    _generateRepair('repair-file-structure', label, code, filename, 1, errorSummary)
  }, [javaEditorRef, javaFile, compileErrorsRef, _generateRepair])

  // ── Batch B5 — AI: Custom Prompt... ───────────────────────────────────────
  // Two-step modal: input step (textarea) → loading/result step
  // Shape: null | { step: 'input'|'loading'|'result', userPrompt, selectedCode, selectionInfo, response, error }
  const [customPromptModal, setCustomPromptModal] = useState(null)

  const handleCustomPromptJava = useCallback(() => {
    const editor  = javaEditorRef.current
    const monaco  = monacoRef.current
    if (!editor || !monaco) return

    const model     = editor.getModel()
    const selection = editor.getSelection()
    let selectedCode, selectionInfo

    if (selection && !selection.isEmpty()) {
      selectedCode  = model.getValueInRange(selection)
      selectionInfo = `Lines ${selection.startLineNumber}–${selection.endLineNumber}`
    } else {
      // No selection — use current line ±5
      const line1 = editor.getPosition()?.lineNumber ?? 1
      const lines  = model.getValue().split('\n')
      const start  = Math.max(0, line1 - 1 - 5)
      const end    = Math.min(lines.length, line1 + 5)
      selectedCode  = lines.slice(start, end).join('\n')
      selectionInfo = `Around line ${line1}`
    }

    setCustomPromptModal({ step: 'input', userPrompt: '', selectedCode, selectionInfo, response: null, error: null })
    console.log('[BatchB5] custom prompt opened:', selectionInfo)
  }, [javaEditorRef, monacoRef])

  const handleCustomPromptSend = useCallback(async (userPrompt) => {
    if (!userPrompt?.trim() || !customPromptModal) return

    const { selectedCode, selectionInfo } = customPromptModal
    setCustomPromptModal(prev => prev ? { ...prev, step: 'loading', userPrompt, response: null, error: null } : null)

    try {
      const storedUser = getStoredUser()
      const userId     = storedUser.user_id || storedUser.id || ''
      // Use filename from modal state (supports both COBOL and Java callers)
      const filename   = customPromptModal.filename || javaFile?.path || javaFile?.name || 'file.java'

      const r = await fetch(`${apiBase}/ai/chat`, {
        method:  'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          prompt:           userPrompt,
          code:             selectedCode,
          purpose:          'custom',
          context:          { file: filename, section: selectionInfo },
          scout_account_id: accountId,
          application_name: projectName,
          user_id:          userId,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      const text = d.response || d.explanation || ''
      if (!text) throw new Error('AI returned an empty response')

      setCustomPromptModal(prev => prev ? { ...prev, step: 'result', response: text, error: null } : null)
      console.log('[BatchB5] custom prompt result received, length:', text.length)
    } catch (e) {
      console.error('[BatchB5] custom prompt failed:', e.message)
      setCustomPromptModal(prev => prev ? { ...prev, step: 'result', response: null, error: e.message } : null)
    }
  }, [customPromptModal, javaFile, apiBase, accountId, projectName])

  // Live ref — stale-closure safety inside addAction
  const handleCustomPromptJavaRef = useRef(handleCustomPromptJava)
  useEffect(() => { handleCustomPromptJavaRef.current = handleCustomPromptJava }, [handleCustomPromptJava])

  // ── Batch B6 — MZ: Apply as Stub ──────────────────────────────────────────
  // No modal — immediate editor action at cursor line.
  // Strategy 1: findEnclosingMethod → buildMethodStub → replace whole method body.
  // Strategy 2: fallback single-line TODO comment at cursor line.
  // Always followed by saveJavaFile (S3 overlay) + reveal affected line.
  const handleApplyStub = useCallback(() => {
    const editor = javaEditorRef.current
    const monaco = monacoRef.current
    if (!editor || !monaco || !javaFile) return

    const cursorLine = editor.getPosition()?.lineNumber ?? 1
    const content    = editor.getValue()
    const filename   = javaFile.name || javaFile.path?.split('/').pop() || 'file.java'

    // Use compile error message as summary when the cursor line has a known error
    const err = compileErrorsRef.current?.find(
      e => (e.file === filename || e.file === javaFile.path) && e.line === cursorLine
    )
    const summary = err?.message || 'Manual stub applied from Refine Code Workbench'

    const model     = editor.getModel()
    const lineCount = model.getLineCount()

    // Strategy 1: method-level stub
    const method = findEnclosingMethod(content, cursorLine)
    if (method) {
      const stub      = buildMethodStub(method, summary)
      const startLine = Math.max(1, method.startLine)
      const endLine   = Math.min(lineCount, method.endLine)
      const endCol    = model.getLineMaxColumn(endLine)
      const range     = new monaco.Range(startLine, 1, endLine, endCol)

      editor.executeEdits('mz-stub', [{ range, text: stub, forceMoveMarkers: true }])
      editor.revealLineInCenter(startLine)
      editor.focus()

      const updated = editor.getValue()
      saveJavaFile(javaFile.path, updated)
      console.log(`[BatchB6] method stub applied: ${filename} L${startLine}–${endLine}`)
      return
    }

    // Strategy 2: single-line TODO fallback
    const originalLine = model.getLineContent(cursorLine).trim()
    const todoLines    = [`        // TODO: Fix compile issue: ${summary}`]
    if (originalLine) todoLines.push(`        // ORIGINAL: ${originalLine}`)
    const todoText = todoLines.join('\n')

    const endCol   = model.getLineMaxColumn(cursorLine)
    const range    = new monaco.Range(cursorLine, 1, cursorLine, endCol)
    editor.executeEdits('mz-stub', [{ range, text: todoText, forceMoveMarkers: true }])
    editor.revealLineInCenter(cursorLine)
    editor.focus()

    const updated = editor.getValue()
    saveJavaFile(javaFile.path, updated)
    console.log(`[BatchB6] TODO stub applied: ${filename} L${cursorLine}`)
  }, [javaEditorRef, monacoRef, javaFile, compileErrorsRef, saveJavaFile])

  const handleApplyStubRef = useRef(handleApplyStub)
  useEffect(() => { handleApplyStubRef.current = handleApplyStub }, [handleApplyStub])

  // Phase 12 — Bottom action bar
  const [resetConfirming, setResetConfirming] = useState(false)
  const [resetting,       setResetting]       = useState(false)
  const loadCompileErrorsRef = useRef(null) // live ref — avoids TDZ (loadCompileErrors declared after handleReset)
  const [resetError,      setResetError]      = useState(null)
  const [downloadError,   setDownloadError]   = useState(null)

  // Download all Java files from project_bundle.zip with workspace edits applied.
  // Uses /editor/folder-download — the correct workbench endpoint (includes user edits).
  const handleWorkbenchDownload = useCallback(async () => {
    if (!bundleJobIds?.pfJobId) return
    setDownloadError(null)
    try {
      const url = `${apiBase}/editor/folder-download` +
        `?gate_job_id=${encodeURIComponent(bundleJobIds.pfJobId)}&folder_path=`
      const r = await fetch(url, { headers: getAuthHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      const href = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      const cd   = r.headers.get('Content-Disposition') || ''
      const m    = cd.match(/filename="([^"]+)"/)
      a.download = m ? m[1] : `project_${bundleJobIds.pfJobId.slice(-8)}.zip`
      a.href = href; a.click()
      URL.revokeObjectURL(href)
      console.log('[Phase12] workbench download complete')
    } catch (e) {
      console.error('[Phase12] download failed:', e.message)
      setDownloadError(`Download failed: ${e.message}`)
    }
  }, [apiBase, bundleJobIds])

  // Reset workspace/ back to baseline/ and reload the open Java file into Monaco.
  const handleReset = useCallback(async () => {
    if (!bundleJobIds?.pfJobId) return
    setResetting(true)
    setResetError(null)
    const storedUser = getStoredUser()
    try {
      const r = await fetch(`${apiBase}/editor/workspace/reset`, {
        method:  'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify({
          pre_flight_job_id: bundleJobIds.pfJobId,
          scout_account_id:  accountId,
          user_id:           storedUser.user_id || storedUser.id || '',
        }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      console.log(`[Phase12] workspace reset — ${d.files_restored} files restored`)

      // Clear all dirty-file indicators
      setModifiedPaths(new Set())
      setResetConfirming(false)
      setResetting(false)

      // Reload the currently open Java file into the live Monaco model.
      // Cannot rely on defaultValue re-render — must push content directly.
      if (javaFile && bundleJobIds) {
        try {
          const params = new URLSearchParams({
            job_id:       javaFile.job_id,
            path:         javaFile.path,
            editor_scope: 'focused',
            gate_job_id:  bundleJobIds.pfJobId,
          })
          const fr = await fetch(`${apiBase}/editor/java/file?${params}`, {
            headers: getAuthHeaders(),
          })
          if (fr.ok) {
            const fd = await fr.json()
            const newContent = fd.content ?? ''
            javaEditorRef.current?.getModel()?.setValue(newContent)
            setJavaContent(newContent)
            console.log('[Phase12] Java editor reloaded to baseline')
          }
        } catch (e) {
          console.warn('[Phase12] reload after reset failed (non-fatal):', e.message)
        }
      }

      // P0: reload compile errors so squiggles + ISSUES tab reflect baseline state
      loadCompileErrorsRef.current?.(bundleJobIds.pfJobId)
      // P1: clear stale "Build Passed/Failed" badge
      onClearRerunStatus?.()
      // P1: invalidate full-project cache so next open forces a fresh fetch
      setAllJavaFiles([])
      // Clear baseline cache so the next file load re-captures fresh baseline content
      baselineContentRef.current = {}
      setDiffMode(false)
      setDiffSnapshot(null)
      console.log('[Phase12] post-reset cleanup complete')
    } catch (e) {
      console.error('[Phase12] reset failed:', e.message)
      setResetError(`Reset failed: ${e.message}`)
      setResetting(false)
    }
  }, [apiBase, accountId, bundleJobIds, javaFile, onClearRerunStatus, setAllJavaFiles])

  // Toolbar
  const [copied,       setCopied]       = useState(false)

  const editorRef = useRef(null)

  // Explorer uses javaFiles from /editor/bundle (workbench is no longer populated).
  // Focused scope: show only files that have at least one compile error (basename match).
  // Full scope: show allJavaFiles (lazy-loaded from /editor/files?scope=full).
  const errorFileBasenames = new Set(compileErrors.map(e => e.file))
  const files = explorerScope === 'full'
    ? allJavaFiles
    : compileErrors.length > 0
      ? javaFiles.filter(f => {
          const basename = f.path.split('/').pop()
          return errorFileBasenames.has(basename)
        })
      : javaFiles
  const summary       = workbench?.summary  || {}
  const appName       = workbench?.app_name || projectName || ''
  const compileStatus = workbench?.compile_status || 'UNKNOWN'

  // ── Phase 5: COBOL fetch ────────────────────────────────────────────────────

  // Fetch COBOL source: GET /editor/cobol/file?job_id={file.job_id}&path={file.path}
  const loadCobolFile = useCallback(async (file) => {
    if (!file) return
    setCobolLoading(true)
    setCobolError(null)
    setCobolFile(file)
    try {
      const params = new URLSearchParams({ job_id: file.job_id, path: file.path })
      const r = await fetch(`${apiBase}/editor/cobol/file?${params}`, {
        headers: getAuthHeaders(),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setCobolContent(d.content ?? '')
      console.log(`[COBOL] loaded ${file.name} — ${(d.content ?? '').split('\n').length} lines`)
    } catch (e) {
      console.error('[COBOL] fetch failed:', e.message)
      setCobolError(`Failed to load ${file.name}: ${e.message}`)
    } finally {
      setCobolLoading(false)
    }
  }, [apiBase])

  // Auto-load largest COBOL file once bundle is ready
  useEffect(() => {
    if (!bundleJobIds || cobolFiles.length === 0) return
    const sorted = [...cobolFiles].sort((a, b) => (b.size || 0) - (a.size || 0))
    loadCobolFile(sorted[0])
  }, [bundleJobIds?.ingestJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll COBOL editor when active section changes
  useEffect(() => {
    if (activeSectionIdx === null || !cobolEditorRef.current) return
    const section = sections[activeSectionIdx]
    if (section?.cobol_start_line) {
      cobolEditorRef.current.revealLineInCenter(section.cobol_start_line)
    }
  }, [activeSectionIdx, sections])

  // ── Phase 6: Java fetch + save ─────────────────────────────────────────────

  // Fetch Java source:
  //   GET /editor/java/file?job_id={file.job_id}&path={file.path}
  //                        &editor_scope=focused&gate_job_id={bundleJobIds.pfJobId}
  const loadJavaFile = useCallback(async (file) => {
    if (!file || !bundleJobIds) return
    setJavaLoading(true)
    setJavaError(null)
    setJavaFile(file)
    try {
      const params = new URLSearchParams({
        job_id:       file.job_id,
        path:         file.path,
        editor_scope: file.editor_scope || 'focused',
        gate_job_id:  bundleJobIds.pfJobId,
      })
      const r = await fetch(`${apiBase}/editor/java/file?${params}`, {
        headers: getAuthHeaders(),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      const content = d.content ?? ''
      setJavaContent(content)
      // Capture baseline (pre-edit snapshot) for the diff view.
      // Only save if no baseline exists yet for this path — so the first load
      // always wins and user edits in the session don't overwrite it.
      if (!baselineContentRef.current[file.path]) {
        baselineContentRef.current[file.path] = content
      }
      setDiffMode(false)     // exit diff mode when a new file is opened
      setDiffSnapshot(null)
      console.log(`[Java] loaded ${file.name} — source=${d.source} — ${content.split('\n').length} lines`)
    } catch (e) {
      console.error('[Java] fetch failed:', e.message)
      setJavaError(`Failed to load ${file.name}: ${e.message}`)
    } finally {
      setJavaLoading(false)
    }
  }, [apiBase, bundleJobIds])

  // Keep saveJavaFile live inside addAction closures.
  // saveJavaFile changes when bundleJobIds changes (re-run), but addAction is registered
  // at editor mount. Using a ref avoids a stale closure without remounting the editor.
  const saveJavaFileRef = useRef(saveJavaFile)
  useEffect(() => { saveJavaFileRef.current = saveJavaFile }, [saveJavaFile])

  // Keep handleExplainJava live inside addAction closures (same stale-closure reason).
  const handleExplainJavaRef = useRef(handleExplainJava)
  useEffect(() => { handleExplainJavaRef.current = handleExplainJava }, [handleExplainJava])

  // Keep handleRefactorJava live inside addAction closures.
  const handleRefactorJavaRef = useRef(handleRefactorJava)
  useEffect(() => { handleRefactorJavaRef.current = handleRefactorJava }, [handleRefactorJava])

  // Keep handleCleanCodeJava live inside addAction closures.
  const handleCleanCodeJavaRef = useRef(handleCleanCodeJava)
  useEffect(() => { handleCleanCodeJavaRef.current = handleCleanCodeJava }, [handleCleanCodeJava])

  // Keep Batch B4 repair handlers live inside addAction closures.
  const handleAISafePatchRef        = useRef(handleAISafePatch)
  const handleAIRepairRegionRef     = useRef(handleAIRepairRegion)
  const handleAIRepairMethodRef     = useRef(handleAIRepairMethod)
  const handleAIRepairFileStructRef = useRef(handleAIRepairFileStructure)
  useEffect(() => { handleAISafePatchRef.current        = handleAISafePatch        }, [handleAISafePatch])
  useEffect(() => { handleAIRepairRegionRef.current     = handleAIRepairRegion     }, [handleAIRepairRegion])
  useEffect(() => { handleAIRepairMethodRef.current     = handleAIRepairMethod     }, [handleAIRepairMethod])
  useEffect(() => { handleAIRepairFileStructRef.current = handleAIRepairFileStructure }, [handleAIRepairFileStructure])

  // Debounced onChange — fires every keystroke, saves after 1500 ms of quiet
  const handleJavaChange = useCallback((content) => {
    if (!javaFile) return
    // Mark dirty immediately so the indicator and Save button respond at once.
    setModifiedPaths(prev => new Set([...prev, javaFile.path]))
    // Silent auto-save after 1500 ms (markClean:false — dirty indicator stays until
    // the user explicitly confirms save via the Save button or Cmd+S).
    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveJavaFile(javaFile.path, content)
    }, 1500)
  }, [javaFile, saveJavaFile])

  // Save button / Cmd+S — captures current editor content and opens the
  // confirmation modal.  The actual S3 write happens in handleConfirmSave.
  const handleSaveCurrentFile = useCallback(() => {
    if (!javaFile || !javaEditorRef.current) return
    const content  = javaEditorRef.current.getValue()
    const filename = javaFile.path.split('/').pop()
    setSaveConfirmModal({ path: javaFile.path, content, filename })
  }, [javaFile, javaEditorRef])

  // Ref so the Monaco Cmd+S addAction closure (registered once at mount) always
  // calls the latest version without a stale capture.
  const handleSaveCurrentFileRef = useRef(handleSaveCurrentFile)
  useEffect(() => { handleSaveCurrentFileRef.current = handleSaveCurrentFile }, [handleSaveCurrentFile])

  // Called when the user clicks "Save" inside the confirmation modal.
  // Uses markClean:true so dirty state clears only on proven S3 success.
  const handleConfirmSave = useCallback(async () => {
    if (!saveConfirmModal) return
    const { path, content } = saveConfirmModal
    setSaveConfirmModal(null)          // close the dialog immediately
    clearTimeout(saveTimerRef.current) // cancel any pending debounce
    const ok = await saveJavaFile(path, content, { markClean: true })
    if (ok) {
      setSaveBanner({ type: 'ok', msg: `Saved` })
      setTimeout(() => setSaveBanner(null), 3000)
    } else {
      // Keep dirty — S3 write failed; saveJavaFile already didn't clear it.
      setSaveBanner({ type: 'err', msg: 'Save failed — check console for details' })
      setTimeout(() => setSaveBanner(null), 5000)
    }
  }, [saveConfirmModal, saveJavaFile, saveTimerRef])

  // Force-save open Java editor content before kicking off a Re-Run Build.
  // The debounced handleJavaChange has a 1500 ms delay — if the user clicks
  // Re-Run Build immediately after typing, the latest content would not yet be
  // written to S3. This handler flushes synchronously first, then delegates.
  const handleRerunWithSave = useCallback(async () => {
    if (javaFile && javaEditorRef.current) {
      clearTimeout(saveTimerRef.current)                      // cancel pending debounce
      const currentContent = javaEditorRef.current.getValue()
      await saveJavaFile(javaFile.path, currentContent)      // flush to S3 now
    }
    await onRerun?.()
  }, [javaFile, javaEditorRef, saveTimerRef, saveJavaFile, onRerun])

  // Auto-open first Java file when bundle is ready
  useEffect(() => {
    if (!bundleJobIds || javaFiles.length === 0) return
    loadJavaFile(javaFiles[0])
  }, [bundleJobIds?.pfJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Phase 7: compile error decorations ────────────────────────────────────

  // Keep live refs in sync so onMouseDown handler always sees current values
  useEffect(() => { compileErrorsRef.current = compileErrors }, [compileErrors])
  useEffect(() => { selectedErrorRef.current = selectedError }, [selectedError])

  // Apply Monaco error decorations for the given file.
  //
  // ALL errors  → deltaDecorations with inlineClassName 'rcw-error-squiggly'
  //               CSS text-decoration: underline wavy covers the full line (col 1 → maxCol)
  //               isWholeLine: true ensures leading whitespace is also covered
  // SELECTED error additionally → className 'rcw-error-line' (full-line red background)
  //
  // err.file from compile_result.json is always a basename (e.g. "Custupd.java").
  // javaFile.path may be a full relative path (e.g. "TimH/Custupd.java").
  const applyErrorDecorations = useCallback((editor, monacoInstance, filePath, errors, selError) => {
    if (!editor || !monacoInstance || !filePath) return
    const model = editor.getModel()
    if (!model) return

    // Clear any native Monaco markers left from previous approach
    monacoInstance.editor.setModelMarkers(model, 'rcw-compile', [])

    const fileBasename = filePath.split('/').pop()

    // Resolve canonical editor basename for an error entry.
    // New records carry err.basename (bare filename from backend contract).
    // Old compile_result.json files without basename fall back to splitting err.file.
    const errEditorBasename = (e) => e.basename || (e.file || '').split('/').pop()

    // Primary match: exact filePath match (covers future full-path records).
    // Secondary match: canonical basename match (the normal focused-scope case).
    const fileErrors = errors.filter(e =>
      e.file === filePath || errEditorBasename(e) === fileBasename
    )

    // Same contract for the selected-error line highlight
    const selLine = (selError && (
      selError.file === filePath || errEditorBasename(selError) === fileBasename
    )) ? selError.line : null

    // One deltaDecorations call — squiggly on every error, background only on selected
    const newDecos = fileErrors.map(err => {
      const isSelected = selLine !== null && err.line === selLine
      const options = {
        isWholeLine:          true,
        inlineClassName:      'rcw-error-squiggly',   // CSS wavy underline, full line
        glyphMarginClassName: 'rcw-error-glyph',
        hoverMessage:         { value: `**${err.file}:${err.line}** — ${err.message}` },
        overviewRuler:        { color: '#ef444488', position: monacoInstance.editor.OverviewRulerLane.Right },
      }
      if (isSelected) {
        options.className     = 'rcw-error-line'      // full-line red background
        options.overviewRuler = { color: '#e24a4a', position: monacoInstance.editor.OverviewRulerLane.Full }
      }
      return {
        range: new monacoInstance.Range(err.line, 1, err.line, model.getLineMaxColumn(err.line) || 1000),
        options,
      }
    })
    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecos)
    console.log(`[Phase7] ${newDecos.length} decoration(s), selected line=${selLine ?? 'none'} on ${filePath}`)
  }, [])

  // Re-apply when errors load, file changes, or selected error changes
  useEffect(() => {
    const editor = javaEditorRef.current
    const monacoInstance = monacoRef.current
    if (!editor || !monacoInstance || !javaFile) return
    applyErrorDecorations(editor, monacoInstance, javaFile.path, compileErrors, selectedError)
  }, [compileErrors, javaFile?.path, selectedError]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch compile_result.json — uses errors_preview for decorations/rows,
  // total_errors (new field) for honest badge counts.
  const loadCompileErrors = useCallback(async (pfJobId) => {
    try {
      const r = await fetch(
        `${apiBase}/pre-flight/results/json/${encodeURIComponent(pfJobId)}/compile_result.json`,
        { headers: getAuthHeaders() }
      )
      if (!r.ok) {
        console.warn(`[Phase7] compile_result fetch HTTP ${r.status} — no decorations`)
        return
      }
      const data = await r.json()
      const errors = data.errors_preview || []
      // total_errors = real Maven count (new field); fall back to error_count then preview length
      const total  = data.total_errors ?? data.error_count ?? errors.length
      setCompileErrors(errors)
      setCompileTotalErrors(total)
      console.log(`[Phase7] compile_result loaded — ${errors.length} preview / ${total} total error(s)`)
    } catch (e) {
      console.warn('[Phase7] compile_result load failed (non-fatal):', e.message)
    }
  }, [apiBase])

  // Keep loadCompileErrorsRef live so handleReset can call it without a TDZ dep
  useEffect(() => { loadCompileErrorsRef.current = loadCompileErrors }, [loadCompileErrors])

  // Trigger once pfJobId is known — same timing pattern as COBOL auto-load
  useEffect(() => {
    if (!bundleJobIds?.pfJobId) return
    loadCompileErrors(bundleJobIds.pfJobId)
  }, [bundleJobIds?.pfJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run UI refresh: when pfJobId changes AND a Java file is already open,
  // reload that file into Monaco so the editor reflects the new workspace state.
  // Guard: javaFile null on initial load → skips. Non-null on re-run → reloads.
  // Also clears stale selectedError highlight.
  useEffect(() => {
    if (!bundleJobIds?.pfJobId || !javaFile) return
    setSelectedError(null)
    const reload = async () => {
      try {
        const params = new URLSearchParams({
          job_id:       javaFile.job_id,
          path:         javaFile.path,
          editor_scope: javaFile.editor_scope || 'focused',
          gate_job_id:  bundleJobIds.pfJobId,
        })
        const r = await fetch(`${apiBase}/editor/java/file?${params}`, { headers: getAuthHeaders() })
        if (r.ok) {
          const d = await r.json()
          const newContent = d.content ?? ''
          javaEditorRef.current?.getModel()?.setValue(newContent)
          setJavaContent(newContent)
          console.log('[Phase9] Java editor reloaded after re-run complete')
        }
      } catch (e) {
        console.warn('[Phase9] Java reload after re-run failed (non-fatal):', e.message)
      }
    }
    reload()
  }, [bundleJobIds?.pfJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-expand all folders on load ────────────────────────────────────────
  useEffect(() => {
    const expanded = {}
    for (const f of files) {
      const parts = f.path.split('/')
      const key = parts.length > 1 ? parts[0] : '__root__'
      expanded[key] = true
    }
    setTreeExpanded(expanded)
  }, [files.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Parse outline when active file content changes
  const activeContent = activeTabPath ? (fileCache[activeTabPath] ?? null) : null
  useEffect(() => {
    if (!activeContent) { setOutlineItems([]); return }
    setOutlineItems(parseOutlineItems(activeContent))
  }, [activeContent, activeTabPath])

  // Fetch a file from S3 on demand (cache hits skip the request)
  // Explorer file click → load in Java editor pane via GET /editor/java/file
  const openFile = useCallback(async (path) => {
    // Search focused list first (has full metadata), fall back to full project list
    const file = javaFiles.find(f => f.path === path)
      || javaFiles.find(f => f.path.endsWith('/' + path) || f.name === path)
      || allJavaFiles.find(f => f.path === path)
      || allJavaFiles.find(f => f.name === path)
    if (!file) return
    loadJavaFile(file)
  }, [javaFiles, allJavaFiles, loadJavaFile])

  // ── Explorer context menu handlers ─────────────────────────────────────────

  const handleExplorerCtxDownload = useCallback(async (file) => {
    setExplorerCtxMenu(null)
    if (!bundleJobIds?.pfJobId) return
    try {
      const params = new URLSearchParams({ gate_job_id: bundleJobIds.pfJobId, file_path: file.path })
      const r = await fetch(`${apiBase}/editor/file-download?${params}`, { headers: getAuthHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href; a.download = file.filename || file.path.split('/').pop()
      document.body.appendChild(a); a.click()
      document.body.removeChild(a); URL.revokeObjectURL(href)
    } catch (e) { console.error('[Explorer] single file download failed:', e.message) }
  }, [apiBase, bundleJobIds])

  const handleExplorerRenameConfirm = useCallback(async (oldPath, newValue) => {
    const trimmed = newValue.trim()
    const oldName = oldPath.split('/').pop()
    if (!trimmed || trimmed === oldName) { setExplorerRenameState(null); return }
    const dir = oldPath.includes('/') ? oldPath.substring(0, oldPath.lastIndexOf('/') + 1) : ''
    const newPath = dir + trimmed
    // Get content from cache, or fetch from API
    let content = fileCache[oldPath] ?? null
    if (content === null) {
      try {
        const file = javaFiles.find(f => f.path === oldPath) || allJavaFiles.find(f => f.path === oldPath)
        if (file) {
          const params = new URLSearchParams({ job_id: file.job_id || bundleJobIds?.pfJobId, path: oldPath })
          const r = await fetch(`${apiBase}/editor/java/file?${params}`, { headers: getAuthHeaders() })
          if (r.ok) content = await r.text()
        }
      } catch (e) { console.error('[Explorer] rename fetch failed:', e.message) }
    }
    await saveJavaFile(newPath, content || '')
    const newName = trimmed
    setAllJavaFiles(prev => prev.map(f =>
      f.path === oldPath ? { ...f, path: newPath, name: newName, filename: newName } : f
    ))
    setExplorerRenameState(null)
  }, [apiBase, bundleJobIds, fileCache, javaFiles, allJavaFiles, saveJavaFile])

  const handleExplorerNewFileConfirm = useCallback(async (folder, newValue) => {
    const trimmed = newValue.trim()
    if (!trimmed) { setExplorerNewFileState(null); return }
    const name = trimmed.endsWith('.java') ? trimmed : trimmed + '.java'
    const dir = folder === '__root__' ? '' : folder + '/'
    const newPath = dir + name
    const className = name.replace('.java', '')
    await saveJavaFile(newPath, `public class ${className} {\n}\n`)
    const newFile = {
      path: newPath, name, filename: name, type: 'file',
      source: 'overlay', job_id: bundleJobIds?.pfJobId, editor_scope: explorerScope,
    }
    setAllJavaFiles(prev => [...prev, newFile])
    setExplorerNewFileState(null)
    openFile(newPath)
  }, [bundleJobIds, explorerScope, saveJavaFile, openFile])

  // ── Phase 8 — ISSUES tab: open file and jump Java editor to error line
  // Contract: err.basename is the canonical editor filename (new records).
  // Fallback: split err.file for old compile_result.json without basename field.
  const handleIssueClick = useCallback((err) => {
    setSelectedError(err)   // sets selected → triggers full-line highlight via applyErrorDecorations
    // Derive canonical editor filename from the error entry
    const errBasename     = err.basename || (err.file || '').split('/').pop()
    const currentBasename = (javaFile?.path || '').split('/').pop()
    if (currentBasename && currentBasename === errBasename) {
      // File already open — scroll directly, no remount needed
      javaEditorRef.current?.revealLineInCenter(err.line)
      javaEditorRef.current?.setPosition({ lineNumber: err.line, column: err.column ?? 1 })
      javaEditorRef.current?.focus()
    } else {
      // Different file — store pending line; onMount applies it after the editor remounts.
      // openFile's f.name === path fallback resolves basename to the correct file object.
      pendingScrollLineRef.current = err.line
      openFile(errBasename)
    }
  }, [javaFile?.path, openFile])

  // Feature 8 — Project-wide search
  // Searches all Java files for a query string (case-insensitive).
  // Content is fetched from Monaco models (already loaded) or from the API on demand.
  // Results are cached in searchContentRef so re-searches skip re-fetching.
  const handleProjectSearch = useCallback(async (query) => {
    const q = query.trim()
    if (!q || !bundleJobIds) return
    setSearchLoading(true)
    setSearchResults(null)
    const lowerQ = q.toLowerCase()
    const results = []

    // Build file list: allJavaFiles (full project) or javaFiles (focused scope).
    // Always ensure the currently open file is included — it may have been loaded via
    // the Sections panel and may not appear in either list.
    const baseFiles = allJavaFiles.length > 0 ? allJavaFiles : javaFiles
    const filesToSearch = [...baseFiles]
    if (javaFile && !filesToSearch.find(f => f.path === javaFile.path || f.name === javaFile.name)) {
      filesToSearch.unshift(javaFile)
    }

    console.log('[Search] searching', filesToSearch.length, 'file(s) for:', q)

    await Promise.all(filesToSearch.map(async (file) => {
      try {
        // 1. Use search content cache (avoids re-fetching on repeat searches)
        let content = searchContentRef.current[file.path]

        // 2. Use currently open Java content (most reliable — always up to date)
        if (!content && javaFile && (file.path === javaFile.path || file.name === javaFile.name)) {
          content = javaContent
        }

        // 3. Use already-built Monaco model (keyed by file.path)
        if (!content) {
          const model = javaNavModelsRef.current[file.path]
          if (model && !model.isDisposed()) content = model.getValue()
        }

        // 4. Fetch from API as last resort
        if (!content) {
          const params = new URLSearchParams({
            job_id:       file.job_id || bundleJobIds.pfJobId,
            path:         file.path,
            editor_scope: file.editor_scope || 'focused',
            gate_job_id:  bundleJobIds.pfJobId,
          })
          const r = await fetch(`${apiBase}/editor/java/file?${params}`, { headers: getAuthHeaders() })
          if (r.ok) {
            const d = await r.json()
            content = d.content ?? ''
          }
        }

        if (!content) { console.warn('[Search] no content for', file.path); return }
        searchContentRef.current[file.path] = content  // cache for re-use

        const lines = content.split('\n')
        const fileMatches = []
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].toLowerCase().includes(lowerQ)) {
            fileMatches.push({ line: i + 1, preview: lines[i].trim() })
          }
        }
        if (fileMatches.length > 0) {
          results.push({ file, matches: fileMatches })
        }
      } catch (e) {
        console.warn('[Search] failed to load', file.path, e.message)
      }
    }))

    results.sort((a, b) => b.matches.length - a.matches.length)
    setSearchResults(results)
    setSearchLoading(false)
    console.log('[Search] done —', results.reduce((n, r) => n + r.matches.length, 0), 'match(es) in', results.length, 'file(s)')
  }, [apiBase, bundleJobIds, allJavaFiles, javaFiles, javaFile, javaContent]) // eslint-disable-line react-hooks/exhaustive-deps

  // Feature 5: open Data Dictionary (fetch once, cache for session)
  const handleOpenDataDict = useCallback(async () => {
    setDataDictOpen(true)
    if (dataDictData || dataDictLoading) return   // already loaded
    if (!bundleJobIds?.caJobId) {
      setDataDictError('No Code Analysis job available.')
      return
    }
    setDataDictLoading(true)
    setDataDictError(null)
    try {
      const res = await fetch(
        `${apiBase}/editor/data-dictionary?code_analysis_job_id=${encodeURIComponent(bundleJobIds.caJobId)}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('modernizeit_token')}` } }
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      const data = await res.json()
      setDataDictData(data)
      const sample = data.fields?.slice(0, 3) ?? []
      console.log('[DataDict] loaded:', data.fields?.length, 'fields,', data.groups?.length, 'groups')
      console.log('[DataDict] sample field keys:', sample.length ? Object.keys(sample[0]) : 'none')
      console.log('[DataDict] sample fields:', JSON.stringify(sample, null, 2))
      const withJava = data.fields?.filter(f => f.java_name && f.java_name !== 'unknown') ?? []
      console.log('[DataDict] fields with java_name:', withJava.length)
    } catch (e) {
      setDataDictError(e.message || 'Failed to load data dictionary.')
      console.error('[DataDict] error:', e)
    } finally {
      setDataDictLoading(false)
    }
  }, [apiBase, bundleJobIds, dataDictData, dataDictLoading])

  const closeTab = useCallback((path, e) => {
    e?.stopPropagation()
    setOpenTabs(prev => {
      const next = prev.filter(t => t.path !== path)
      if (activeTabPath === path) {
        setActiveTabPath(next.length > 0 ? next[next.length - 1].path : null)
      }
      return next
    })
  }, [activeTabPath])

  // Build grouped tree (folder → files), applying filter
  const filter = treeFilter.toLowerCase()
  const grouped = {}
  for (const f of files) {
    const parts = f.path.split('/')
    // Use the first path segment as the folder label.
    // Root-level files (no subdirectory) go into a 'root' bucket displayed as '/'
    const folder   = parts.length > 1 ? parts[0] : '__root__'
    const filename = parts[parts.length - 1]
    if (filter && !filename.toLowerCase().includes(filter) && !f.path.toLowerCase().includes(filter)) continue
    if (!grouped[folder]) grouped[folder] = []
    grouped[folder].push({ ...f, filename })
  }

  // Display label for folder key
  const folderLabel = (key) => key === '__root__' ? 'src' : key + '/'

  // Phase 14 — error count per file keyed by basename (matches err.file from compile_result.json)
  const errorCountByFile = compileErrors.reduce((acc, err) => {
    acc[err.file] = (acc[err.file] || 0) + 1
    return acc
  }, {})

  // Color-code Java file icons by type — mirrors IntelliJ/PyCharm icon colors
  const javaFileColor = (path = '', folder = '') => {
    const name = path.split('/').pop().toLowerCase()
    if (folder === 'entities' || name.endsWith('entity.java') || name.endsWith('model.java')) return '#f0c04a'  // gold  — JPA entity
    if (folder === 'repositories' || name.endsWith('repository.java') || name.endsWith('repo.java')) return '#c792ea' // purple — Spring repo
    if (folder === 'interfaces' || (name.startsWith('i') && name.endsWith('.java'))) return '#82aaff'  // blue  — interface
    if (name.endsWith('service.java') || name.endsWith('serviceimpl.java')) return '#ff9800' // amber — service
    if (name.endsWith('controller.java')) return '#4caf50' // green  — controller
    if (name.endsWith('config.java') || name.endsWith('configuration.java')) return '#9e9e9e' // gray  — config
    return '#75beff' // default blue
  }

  const activeFileInfo = activeTabPath ? files.find(f => f.path === activeTabPath) : null

  // Outline icon helper (matches WorkspaceView style)
  const outlineIcon = (type) => {
    switch (type) {
      case 'class':     return <Code2   size={12} />
      case 'interface': return <Layers  size={12} />
      case 'method':    return <Sparkles size={12} />
      default:          return <FileCode size={12} />
    }
  }

  const handleCopy = useCallback(() => {
    if (!activeContent) return
    navigator.clipboard.writeText(activeContent).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [activeContent])

  const handleOpenRaw = useCallback(() => {
    if (!activeContent || !activeTabPath) return
    const blob = new Blob([activeContent], { type: 'text/plain' })
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href; a.target = '_blank'; a.click()
    URL.revokeObjectURL(href)
  }, [activeContent, activeTabPath])

  // ── Right panel content ─────────────────────────────────────────────────────

  const renderRightPanel = () => {
    // ── Outline ──
    if (rightTab === 'outline') {
      if (!activeContent) {
        return <div className="ws-ctx-empty">Open a file to see its outline</div>
      }
      if (outlineItems.length === 0) {
        return <div className="ws-ctx-empty">No symbols found</div>
      }
      return (
        <div className="ws-target-outline-list">
          {outlineItems.map((item, idx) => (
            <div
              key={idx}
              className={`ws-target-outline-item ${item.type}`}
              onClick={() => {
                if (editorRef.current && item.line) {
                  editorRef.current.revealLineInCenter(item.line)
                  editorRef.current.setPosition({ lineNumber: item.line, column: 1 })
                  editorRef.current.focus()
                }
              }}
            >
              {outlineIcon(item.type)}
              <span className="ws-target-outline-name">{item.name}</span>
              <span className="ws-target-outline-line">:{item.line}</span>
            </div>
          ))}
        </div>
      )
    }

    // ── Compile ──
    if (rightTab === 'compile') {
      if (!compileReport) {
        return (
          <div className="ws-ctx-empty">
            <Info size={16} />
            <span>No compile report available yet</span>
          </div>
        )
      }
      const passed = compileReport.compile_passed
      const errors = compileReport.errors_preview || []
      return (
        <div className="rcw-ctx-compile">
          <div className={`rcw-compile-result ${passed ? 'passed' : 'failed'}`}>
            {passed
              ? <CheckCircle2 size={16} />
              : <XCircle size={16} />}
            <span>Maven Compile {passed ? 'Passed' : 'Failed'}</span>
          </div>
          {errors.length > 0 && (
            <div className="rcw-compile-errors">
              {errors.map((e, i) => (
                <div key={i} className="rcw-compile-error-row">
                  <span className="rcw-compile-error-loc">{e.file || 'unknown'}:{e.line || 0}</span>
                  <span className="rcw-compile-error-msg">{e.message}</span>
                </div>
              ))}
            </div>
          )}
          {compileReport.maven_output_tail && (
            <div className="rcw-compile-tail">
              <div className="rcw-compile-tail-label">Maven Output</div>
              <pre>{compileReport.maven_output_tail}</pre>
            </div>
          )}
        </div>
      )
    }

    // ── Patches ──
    if (rightTab === 'patches') {
      if (!patchReport) {
        return (
          <div className="ws-ctx-empty">
            <GitCompare size={16} />
            <span>No patch report available</span>
          </div>
        )
      }
      const allResults = patchReport.results || []
      const fileResults = activeFileInfo
        ? allResults.filter(r =>
            r.target_file === activeFileInfo.path ||
            r.target_file === activeFileInfo.filename ||
            (r.target_file && activeFileInfo.path.endsWith(r.target_file)) ||
            r.target_file === '<all>'
          )
        : allResults

      const summary2 = patchReport.summary || {}
      return (
        <div className="rcw-ctx-patches">
          <div className="rcw-patch-summary">
            <span className="rcw-patch-count applied">{summary2.applied ?? 0} applied</span>
            <span className="rcw-patch-count annotated">{summary2.annotated ?? 0} annotated</span>
            <span className="rcw-patch-count review">{summary2.skipped_manual_only ?? 0} needs review</span>
          </div>
          {activeFileInfo && (
            <div className="rcw-patch-filter-label">
              Showing patches for {activeFileInfo.filename}
            </div>
          )}
          {fileResults.length === 0 && (
            <div className="ws-ctx-empty">
              {activeFileInfo ? 'No patches for this file' : 'No patches recorded'}
            </div>
          )}
          {fileResults.map((r, i) => (
            <div key={i} className={`rcw-patch-item ${r.status || 'unknown'}`}>
              <div className="rcw-patch-item-header">
                {r.status === 'applied'
                  ? <Check size={12} />
                  : r.status === 'annotated'
                  ? <Info size={12} />
                  : <AlertTriangle size={12} />}
                <span className="rcw-patch-smell">{r.smell_type || r.status}</span>
              </div>
              {(r.reason || r.recommendation_id) && (
                <div className="rcw-patch-reason">{r.reason || r.recommendation_id}</div>
              )}
              {r.target_file && r.target_file !== '<all>' && (
                <div className="rcw-patch-file">{r.target_file.split('/').pop()}</div>
              )}
            </div>
          ))}
        </div>
      )
    }

    return null
  }

  // ── Compile status badge — derived from live compileErrors (workbench.compile_status is always UNKNOWN) ──
  // compileTotalErrors = real Maven count; compileErrors.length = preview rows (≤ 100)
  const compileBadgeClass = !bundleJobIds ? ''
    : compileTotalErrors === 0 ? 'pass' : 'fail'
  const compileBadgeLabel = !bundleJobIds ? ''
    : compileTotalErrors === 0 ? 'Build Clean'
    : `${compileTotalErrors} Error${compileTotalErrors !== 1 ? 's' : ''}`

  // ── Monaco options ──────────────────────────────────────────────────────────
  const monacoOptions = {
    minimap: { enabled: true },
    fontSize: 13,
    fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
    automaticLayout: true,
    folding: true,
    scrollBeyondLastLine: false,
    renderLineHighlight: 'all',
    lineNumbers: 'on',
    codeLens: true,   // Feature 2: inline "Generated from COBOL paragraph X" annotations
    readOnly: true,
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="ws-target-tab rcw-workbench">

      {/* ── Top bar ── */}
      <div className="ws-target-header">
        <div className="ws-target-title">
          <button className="rcw-back-btn" onClick={onBack} title="Back to source picker">
            <ArrowLeft size={14} />
          </button>
          <Code2 size={15} />
          <span>Refine Code Workbench</span>
          {appName && <span className="rcw-app-name">{appName}</span>}
          <span className="ws-target-file-count">{summary.total_files || files.length} files</span>
          {compileBadgeClass && (
            <span className={`ws-target-badge ${compileBadgeClass}`}>
              {compileBadgeClass === 'pass' && <Check size={12} />}
              {compileBadgeClass === 'fail' && <XCircle size={12} />}
              {compileBadgeLabel}
            </span>
          )}
        </div>

        <div className="ws-target-stats">
          {compileTotalErrors > 0 && (
            <span className="ws-target-stat rcw-stat-review">
              <AlertTriangle size={12} /> {compileTotalErrors} compile error{compileTotalErrors !== 1 ? 's' : ''}
            </span>
          )}
          {modifiedPaths.size > 0 && (
            <span className="ws-target-stat"><FileCode size={12} /> {modifiedPaths.size} file{modifiedPaths.size !== 1 ? 's' : ''} modified</span>
          )}
        </div>

        <div className="ws-target-actions">
          {compileTotalErrors > 0 && (
            <button
              className={`ws-target-download-btn rcw-report-toggle-btn${showReport ? ' active' : ''}`}
              onClick={() => setShowReport(v => !v)}
              title={showReport ? 'Back to IDE' : 'View compile report'}
            >
              <XCircle size={14} />
              {compileTotalErrors} Error{compileTotalErrors !== 1 ? 's' : ''}
            </button>
          )}
          {/* Save — icon only. Enabled + amber only when current file is dirty. */}
          <button
            className={`ws-target-download-btn rcw-save-btn${javaFile && modifiedPaths.has(javaFile.path) ? ' rcw-save-btn--dirty' : ''}`}
            onClick={handleSaveCurrentFile}
            disabled={!javaFile || !bundleJobIds || !modifiedPaths.has(javaFile?.path)}
            title={javaFile && modifiedPaths.has(javaFile.path) ? 'Save Java file' : 'No unsaved changes'}
          >
            <Save size={14} />
          </button>
          {/* Inline save result banner — fades after 3-5 s */}
          {saveBanner && (
            <span className={`rcw-save-banner rcw-save-banner--${saveBanner.type}`}>
              {saveBanner.type === 'ok'
                ? <><CheckCircle2 size={12} /> {saveBanner.msg}</>
                : <><XCircle size={12} /> {saveBanner.msg}</>
              }
            </span>
          )}
          <button className="ws-target-download-btn" onClick={onReload} title="Reload workbench">
            <RefreshCw size={14} />
          </button>
          <button
            className={`ws-target-download-btn rcw-rerun-btn${rerunning ? ' rcw-rerun-running' : rerunStatus === 'passed' ? ' rcw-rerun-passed' : ''}`}
            onClick={handleRerunWithSave}
            disabled={rerunning || !bundleJobIds}
            title="Re-run compile with your workspace edits applied (overlay fix loop)"
          >
            {rerunning
              ? <><Loader2 size={14} className="rcv-spin" /> Building…</>
              : rerunStatus === 'passed'
              ? <><CheckCircle2 size={14} /> Build Passed</>
              : rerunStatus === 'failed'
              ? <><XCircle size={14} /> Re-Run Build</>
              : <><Play size={14} /> Re-Run Build</>
            }
          </button>
          <button
            className="ws-target-download-btn rcw-continue-btn"
            onClick={onContinue}
            title="Build and package the current Refine Code workspace."
          >
            <Package size={14} /> Build / Package
          </button>

          {/* ── Modernization Flow nav menu ── */}
          <div className="rcw-flow-menu">
            <button
              className={`ws-target-download-btn rcw-flow-trigger${showFlowMenu ? ' active' : ''}`}
              onClick={() => setShowFlowMenu(v => !v)}
              title="Modernization Flow"
            >
              <LayoutGrid size={14} />
              <ChevronDown size={11} className={`rcw-flow-chevron${showFlowMenu ? ' open' : ''}`} />
            </button>

            {showFlowMenu && (
              <>
                <div className="more-backdrop" onClick={() => setShowFlowMenu(false)} />
                <div className="more-dropdown rcw-flow-dropdown">
                  {/* Projects */}
                  <button
                    className="dropdown-item"
                    onClick={() => { setShowFlowMenu(false); onBackToProjects?.() }}
                  >
                    <span>Projects</span>
                  </button>
                  <div className="dropdown-divider" />

                  {/* Modernization Flow */}
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowFlowMenu(false); onModeChange?.('guided-analyze') }}>
                    <span>Analyze</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowFlowMenu(false); onModeChange?.('generate-docs') }}>
                    <span>Generate Documentation</span>
                  </button>
                  {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                  <button className="dropdown-item" onClick={() => { setShowFlowMenu(false); onModeChange?.('extract-biz') }}>
                    <span>Extract Business Logic</span>
                  </button>
                  */}
                  <button className="dropdown-item" onClick={() => { setShowFlowMenu(false); onModeChange?.('decompose-code') }}>
                    <span>Define Services</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowFlowMenu(false); onModeChange?.('service-generation') }}>
                    <span>Build Services</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowFlowMenu(false); onModeChange?.('refactor-code') }}>
                    <span>Refactor Code</span>
                  </button>
                  <button className="dropdown-item active-item" onClick={() => setShowFlowMenu(false)}>
                    <span>Refine Code</span>
                  </button>
                  <button className="dropdown-item" onClick={() => setShowFlowMenu(false)}>
                    <span>Go to Refine Code to Build / Package</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => {
                    setShowFlowMenu(false)
                    window.open(window.location.origin + window.location.pathname + '?standalone=reports', '_blank')
                  }}>
                    <span>Reports</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Body: Compile Report OR 4-pane IDE ── */}
      {showReport ? (
        <CompileReportView
          compileErrors={compileErrors}
          totalErrors={compileTotalErrors}
          onBack={() => setShowReport(false)}
          onFixError={(err) => { setSelectedError(err); setAiFixOpen(true) }}
          onRerun={handleRerunWithSave}
          rerunning={rerunning}
          rerunStatus={rerunStatus}
        />
      ) : (<>

      <div className="ws-target-body">
        <div className="rcw-workbench-row">

          {/* ── Activity Bar (PyCharm-style) — always visible, icons toggle panels ── */}
          <div className="rcw-activity-bar">
            {/* Files panel */}
            <button
              className={`rcw-activity-btn${!filesCollapsed ? ' active' : ''}`}
              onClick={() => setFilesCollapsed(v => !v)}
              title={filesCollapsed ? 'Show Files' : 'Hide Files'}
            >
              {filesCollapsed ? <Folder size={17} /> : <FolderOpen size={17} />}
              {compileTotalErrors > 0 && (
                <span className="rcw-activity-badge rcw-activity-badge--error">
                  {compileTotalErrors > 9 ? '9+' : compileTotalErrors}
                </span>
              )}
            </button>

            {/* Sections + COBOL outline pane */}
            <button
              className={`rcw-activity-btn${!sectionsCollapsed ? ' active' : ''}`}
              onClick={() => setSectionsCollapsed(v => !v)}
              title={sectionsCollapsed ? 'Show Sections' : 'Hide Sections'}
            >
              <Layers size={17} />
              {sections.length > 0 && !sectionsCollapsed && (
                <span className="rcw-activity-badge">{sections.length > 99 ? '99+' : sections.length}</span>
              )}
            </button>

            {/* COBOL editor pane */}
            <button
              className={`rcw-activity-btn${!cobolCollapsed ? ' active' : ''}`}
              onClick={() => setCobolCollapsed(v => !v)}
              title={cobolCollapsed ? 'Show COBOL Source' : 'Hide COBOL Source'}
            >
              <Code2 size={17} />
            </button>

            {/* Java editor pane */}
            <button
              className={`rcw-activity-btn rcw-activity-btn--java${!javaCollapsed ? ' active' : ''}`}
              onClick={() => setJavaCollapsed(v => !v)}
              title={javaCollapsed ? 'Show Java Editor' : 'Hide Java Editor'}
            >
              <FaJava size={18} />
            </button>

            {/* Data Dictionary popup — bottom of bar, separated */}
            <button
              className={`rcw-activity-btn rcw-activity-btn--dict${dataDictOpen ? ' active' : ''}`}
              onClick={handleOpenDataDict}
              title="Data Dictionary — COBOL field → Java mapping"
            >
              <Database size={16} />
            </button>
          </div>

        <Allotment defaultSizes={[200, 180, 370, 370]} className="rcw-allotment-grow">

          {/* Pane 1: File Explorer — ISSUES | CHANGES | FILES tabs */}
          <Allotment.Pane minSize={160} maxSize={400} visible={!filesCollapsed}>
            <div className="ws-target-tree rcw-explorer-pane">

              {/* ── Tab bar ── */}
              <div className="rcw-explorer-tabs">
                <button
                  className={`rcw-explorer-tab ${explorerTab === 'issues' ? 'active' : ''}`}
                  onClick={() => setExplorerTab('issues')}
                >
                  ISSUES
                  {compileTotalErrors > 0 && (
                    <span className="rcw-explorer-tab-badge rcw-explorer-tab-badge--error">
                      {compileTotalErrors}
                    </span>
                  )}
                  {qualityWarnings.length > 0 && compileTotalErrors === 0 && (
                    <span className="rcw-explorer-tab-badge rcw-explorer-tab-badge--warn">
                      {qualityWarnings.length}
                    </span>
                  )}
                </button>
                <button
                  className={`rcw-explorer-tab ${explorerTab === 'changes' ? 'active' : ''}`}
                  onClick={() => setExplorerTab('changes')}
                >
                  CHANGES
                  {modifiedPaths.size > 0 && (
                    <span className="rcw-explorer-tab-badge rcw-explorer-tab-badge--modified">
                      {modifiedPaths.size}
                    </span>
                  )}
                </button>
                <button
                  className={`rcw-explorer-tab ${explorerTab === 'files' ? 'active' : ''}`}
                  onClick={() => setExplorerTab('files')}
                >
                  FILES
                </button>
                <button
                  className={`rcw-explorer-tab ${explorerTab === 'search' ? 'active' : ''}`}
                  onClick={() => setExplorerTab('search')}
                  title="Search across all Java files"
                >
                  <Search size={10} />
                </button>
                <button
                  className="rcw-pane-close-btn rcw-explorer-close"
                  onClick={() => setFilesCollapsed(true)}
                  title="Close Files panel"
                >
                  <X size={11} />
                </button>
              </div>

              {/* ── ISSUES tab ── */}
              {explorerTab === 'issues' && (
                <div className="rcw-explorer-content">
                  {compileErrors.length === 0 ? (
                    <div className="rcw-explorer-empty rcw-explorer-empty--pass">
                      <CheckCircle2 size={20} />
                      <span>No compile errors</span>
                    </div>
                  ) : (() => {
                    // Phase 1 — sort root candidates first; preserve original order within each group.
                    // Falls back gracefully when is_root_candidate field is absent (old compile_result.json).
                    const hasTriageData = compileErrors.some(e => e.is_root_candidate !== undefined)
                    const sorted = hasTriageData
                      ? [
                          ...compileErrors.filter(e => e.is_root_candidate !== false),
                          ...compileErrors.filter(e => e.is_root_candidate === false),
                        ]
                      : compileErrors
                    return (
                      <div className="rcw-issues-list">
                        {hasTriageData && compileErrors.some(e => e.is_root_candidate === false) && (
                          <div className="rcw-issues-triage-note">
                            Root candidates shown first
                          </div>
                        )}
                        {sorted.map((err, i) => {
                          const isCascade = err.is_root_candidate === false
                          const cascadeTip = isCascade
                            ? (err.cascade_reason
                                ? `Likely cascade — ${err.cascade_reason}`
                                : 'Likely cascade error')
                            : null
                          return (
                            <div
                              key={i}
                              className={`rcw-issue-row${isCascade ? ' rcw-issue-row--cascade' : ''}`}
                              onClick={() => handleIssueClick(err)}
                              title={`${err.file}:${err.line} — ${err.message}`}
                            >
                              <XCircle size={12} className="rcw-issue-icon" />
                              <div className="rcw-issue-body">
                                <div className="rcw-issue-loc">
                                  <span className="rcw-issue-file">{err.file}</span>
                                  <span className="rcw-issue-line">:{err.line}</span>
                                  {hasTriageData && (
                                    isCascade
                                      ? <span className="rcw-triage-badge rcw-triage-badge--cascade" title={cascadeTip}>Likely cascade</span>
                                      : <span className="rcw-triage-badge rcw-triage-badge--root">Root candidate</span>
                                  )}
                                </div>
                                <div className="rcw-issue-msg">{err.message}</div>
                              </div>
                              <button
                                className="rcw-issue-ai-btn"
                                title="AI Fix"
                                onClick={e => {
                                  e.stopPropagation()
                                  setSelectedError(err)
                                  setAiFixOpen(true)
                                }}
                              >
                                <Sparkles size={11} />
                              </button>
                            </div>
                          )
                        })}
                        {compileTotalErrors > compileErrors.length && (
                          <div className="rcw-issues-truncation-note">
                            Showing first {compileErrors.length} of {compileTotalErrors} errors
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* ── Quality Warnings section (below compile errors) ── */}
                  {qualityWarnings.length > 0 && (
                    <div className="rcw-quality-section">
                      <div className="rcw-quality-section-header">
                        <AlertTriangle size={11} className="rcw-quality-section-icon" />
                        <span>Review Required</span>
                        <span className="rcw-quality-section-count">{qualityWarnings.length}</span>
                      </div>
                      {qualityWarnings.map((w, i) => (
                        <div
                          key={i}
                          className={`rcw-quality-item rcw-quality-item--${w.severity}`}
                          onClick={() => {
                            const b = javaMethodBoundariesRef.current.find(x => x.name === w.javaMethod)
                            if (b && javaEditorRef.current) {
                              javaEditorRef.current.revealLineInCenter(b.startLine)
                              javaEditorRef.current.setPosition({ lineNumber: b.startLine, column: 1 })
                            }
                          }}
                          title={`Click to jump to ${w.javaMethod} in Java editor`}
                        >
                          <div className="rcw-quality-item-method">{w.javaMethod}</div>
                          <div className="rcw-quality-item-cobol">{w.cobolName}</div>
                          <div className="rcw-quality-item-flags">
                            {w.patterns.map(p => (
                              <span key={p.id} className={`rcw-quality-pill rcw-quality-pill--${p.severity}`}>{p.label}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── CHANGES tab ── */}
              {explorerTab === 'changes' && (
                <div className="rcw-explorer-content">
                  {modifiedPaths.size === 0 ? (
                    <div className="rcw-explorer-empty">
                      <FileCode size={20} />
                      <span>No modified files</span>
                    </div>
                  ) : (
                    <div className="rcw-changes-list">
                      {[...modifiedPaths].map((path) => (
                        <div
                          key={path}
                          className="rcw-change-row"
                          onClick={() => openFile(path)}
                          title={path}
                        >
                          <span className="rcw-change-dot" />
                          <FaJava size={13} className="rcw-fi-java" />
                          <span className="rcw-change-name">{path.split('/').pop()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── FILES tab — existing tree, behavior unchanged ── */}
              {explorerTab === 'files' && (
                <>
                  {/* Focused / Full Project scope toggle */}
                  <div className="rcw-scope-toggle">
                    <button
                      className={`rcw-scope-btn${explorerScope === 'focused' ? ' active' : ''}`}
                      onClick={() => setExplorerScope('focused')}
                      disabled={compileErrors.length === 0}
                      title={compileErrors.length === 0 ? 'No compile errors — showing full project' : 'Show only files with compile errors'}
                    >
                      Focused
                    </button>
                    <button
                      className={`rcw-scope-btn${explorerScope === 'full' ? ' active' : ''}`}
                      onClick={() => setExplorerScope('full')}
                      title="Show all project files"
                    >
                      Full Project
                    </button>
                  </div>

                  {allJavaFilesLoading && (
                    <div className="rcw-full-loading">Loading full project…</div>
                  )}

                  <div className="ws-target-tree-search">
                    <Search size={13} />
                    <input
                      type="text"
                      placeholder="Filter files…"
                      value={treeFilter}
                      onChange={e => setTreeFilter(e.target.value)}
                    />
                    {treeFilter && (
                      <button
                        className="ws-target-tree-search-clear"
                        onClick={() => setTreeFilter('')}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>

                  {/* ── Project root node (PyCharm-style) ── */}
                  <div
                    className="rcw-tree-root-node"
                    onClick={() => setTreeRootExpanded(p => !p)}
                    onContextMenu={(e) => {
                      e.preventDefault(); e.stopPropagation()
                      setExplorerCtxMenu({ x: e.clientX, y: e.clientY, type: 'root', label: appName || projectName || 'Project' })
                    }}
                  >
                    <ChevronDown
                      size={13}
                      className="rcw-tree-chevron"
                      style={{ transform: treeRootExpanded ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }}
                    />
                    {treeRootExpanded
                      ? <FolderOpen size={15} className="rcw-root-folder-icon" />
                      : <Folder     size={15} className="rcw-root-folder-icon" />}
                    <span className="rcw-tree-root-label">{appName || projectName || 'Project'}</span>
                  </div>

                  {/* ── Nested folder groups ── */}
                  {treeRootExpanded && Object.entries(grouped).map(([folder, items]) => (
                    <div className="ws-target-tree-section rcw-tree-nested" key={folder}>
                      <div
                        className="ws-target-tree-section-header"
                        onClick={() => setTreeExpanded(p => ({ ...p, [folder]: !p[folder] }))}
                        onContextMenu={(e) => {
                          e.preventDefault(); e.stopPropagation()
                          const fp = folder === '__root__' ? '' : folder
                          setExplorerCtxMenu({ x: e.clientX, y: e.clientY, type: 'folder', folder, folderPath: fp, label: folderLabel(folder) })
                        }}
                      >
                        {treeExpanded[folder]
                          ? <ChevronDown size={13} />
                          : <ChevronRight size={13} />}
                        {treeExpanded[folder]
                          ? <FolderOpen size={14} />
                          : <Folder     size={14} />}
                        <span className="ws-target-tree-section-label">{folderLabel(folder)}</span>
                        <span className="ws-target-tree-section-count">{items.length}</span>
                      </div>
                      {treeExpanded[folder] && items.map(f => (
                        <div
                          key={f.path}
                          className={`ws-target-tree-item ${activeTabPath === f.path ? 'selected' : ''}`}
                          onClick={() => { if (explorerRenameState?.path !== f.path) openFile(f.path) }}
                          onContextMenu={(e) => {
                            e.preventDefault(); e.stopPropagation()
                            setExplorerCtxMenu({ x: e.clientX, y: e.clientY, file: f })
                          }}
                        >
                          <JavaBadge path={f.path} folder={folder} />
                          {explorerRenameState?.path === f.path
                            ? <input
                                className="rcw-explorer-rename-input"
                                autoFocus
                                value={explorerRenameState.value}
                                onChange={e => setExplorerRenameState(s => ({ ...s, value: e.target.value }))}
                                onKeyDown={e => {
                                  e.stopPropagation()
                                  if (e.key === 'Enter') handleExplorerRenameConfirm(f.path, explorerRenameState.value)
                                  if (e.key === 'Escape') setExplorerRenameState(null)
                                }}
                                onBlur={() => setExplorerRenameState(null)}
                                onClick={e => e.stopPropagation()}
                              />
                            : <span className="ws-target-tree-item-label">{f.filename}</span>
                          }
                          {errorCountByFile[f.filename] > 0 && (
                            <span
                              className="rcw-tree-error-badge"
                              title={`${errorCountByFile[f.filename]} compile error(s)`}
                            >
                              {errorCountByFile[f.filename]}
                            </span>
                          )}
                          {modifiedPaths.has(f.path) && <span className="rcw-tree-dot" />}
                        </div>
                      ))}
                      {/* New file inline input — shown at bottom of folder when triggered */}
                      {explorerNewFileState?.folder === folder && (
                        <div className="ws-target-tree-item rcw-explorer-newfile-row">
                          <FileCode size={12} style={{ flexShrink: 0, opacity: 0.5 }} />
                          <input
                            className="rcw-explorer-rename-input"
                            autoFocus
                            placeholder="NewFile.java"
                            value={explorerNewFileState.value}
                            onChange={e => setExplorerNewFileState(s => ({ ...s, value: e.target.value }))}
                            onKeyDown={e => {
                              e.stopPropagation()
                              if (e.key === 'Enter') handleExplorerNewFileConfirm(folder, explorerNewFileState.value)
                              if (e.key === 'Escape') setExplorerNewFileState(null)
                            }}
                            onBlur={() => setExplorerNewFileState(null)}
                          />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Root collapsed or no files */}
                  {Object.keys(grouped).length === 0 && (
                    <div style={{ padding: '32px 16px', textAlign: 'center', color: '#6f6f6f', fontSize: 13 }}>
                      <FileCode size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <div>{files.length === 0 ? 'No files in workspace' : 'No files match filter'}</div>
                    </div>
                  )}
                </>
              )}

              {/* ── SEARCH tab (Feature 8) ── */}
              {explorerTab === 'search' && (
                <div className="rcw-explorer-content rcw-search-pane">
                  <form
                    className="rcw-search-form"
                    onSubmit={e => { e.preventDefault(); handleProjectSearch(searchQuery) }}
                  >
                    <div className="rcw-search-input-row">
                      <Search size={12} className="rcw-search-icon" />
                      <input
                        className="rcw-search-input"
                        type="text"
                        placeholder="Search all Java files…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="rcw-sections-search-clear"
                          onClick={() => { setSearchQuery(''); setSearchResults(null) }}
                        ><X size={11} /></button>
                      )}
                    </div>
                    <button type="submit" className="rcw-search-submit" disabled={!searchQuery.trim() || searchLoading}>
                      {searchLoading ? <Loader2 size={12} className="rcv-spin" /> : 'Search'}
                    </button>
                  </form>

                  {searchLoading && (
                    <div className="rcw-search-status">
                      <Loader2 size={14} className="rcv-spin" /> Searching…
                    </div>
                  )}

                  {!searchLoading && searchResults !== null && searchResults.length === 0 && (
                    <div className="rcw-search-status">No results for "{searchQuery}"</div>
                  )}

                  {!searchLoading && searchResults !== null && searchResults.length > 0 && (
                    <div className="rcw-search-results">
                      <div className="rcw-search-summary">
                        {searchResults.reduce((n, r) => n + r.matches.length, 0)} match(es) in {searchResults.length} file(s)
                      </div>
                      {searchResults.map((result, ri) => (
                        <div key={ri} className="rcw-search-file-group">
                          <div className="rcw-search-file-header">
                            <FileCode size={11} />
                            <span>{result.file.name || result.file.path.split('/').pop()}</span>
                            <span className="rcw-search-file-count">{result.matches.length}</span>
                          </div>
                          {result.matches.slice(0, 20).map((m, mi) => (
                            <div
                              key={mi}
                              className="rcw-search-match"
                              onClick={() => {
                                pendingScrollLineRef.current = m.line
                                openFile(result.file.path)
                              }}
                              title={`${result.file.name}:${m.line}`}
                            >
                              <span className="rcw-search-match-line">{m.line}</span>
                              <span className="rcw-search-match-preview">{m.preview.slice(0, 60)}</span>
                            </div>
                          ))}
                          {result.matches.length > 20 && (
                            <div className="rcw-search-more">+{result.matches.length - 20} more</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </Allotment.Pane>

          {/* Pane 2: Sections Navigator */}
          <Allotment.Pane
            minSize={140}
            maxSize={320}
            visible={!sectionsCollapsed}
          >
            <div className="rcw-sections-pane">

              {/* Header: tab switcher (Sections | COBOL) + collapse */}
              <div className="rcw-pane-header rcw-pane-header--tabs">
                <button
                  className={`rcw-sections-tab${sectionsPaneTab === 'sections' ? ' active' : ''}`}
                  onClick={() => setSectionsPaneTab('sections')}
                  title="Mapped COBOL → Java sections"
                >
                  <Layers size={11} />
                  Sections
                  {sections.length > 0 && (
                    <span className="rcw-pane-badge">{sections.length}</span>
                  )}
                </button>
                <button
                  className={`rcw-sections-tab${sectionsPaneTab === 'cobol' ? ' active' : ''}`}
                  onClick={() => setSectionsPaneTab('cobol')}
                  title="All COBOL paragraphs parsed from source"
                >
                  <FileCode size={11} />
                  COBOL
                  {cobolOutlineItems.length > 0 && (
                    <span className="rcw-pane-badge">{cobolOutlineItems.length}</span>
                  )}
                </button>
                <div className="rcw-pane-header-spacer" />
                <button
                  className="rcw-pane-close-btn"
                  onClick={() => setSectionsCollapsed(true)}
                  title="Close panel"
                >
                  <X size={11} />
                </button>
              </div>

              {/* Search / filter — works for both tabs */}
              <div className="rcw-sections-search">
                <Search size={12} />
                <input
                  type="text"
                  placeholder={sectionsPaneTab === 'cobol' ? 'Filter paragraphs…' : 'Filter sections…'}
                  value={sectionFilter}
                  onChange={e => setSectionFilter(e.target.value)}
                />
                {sectionFilter && (
                  <button
                    className="rcw-sections-search-clear"
                    onClick={() => setSectionFilter('')}
                  >
                    <X size={11} />
                  </button>
                )}
              </div>

              {/* ── Sections tab ── */}
              {sectionsPaneTab === 'sections' && (
                <div className="rcw-sections-list">
                  {sections.length === 0 && (
                    <div className="rcw-sections-empty">No sections loaded</div>
                  )}
                  {sections
                    .map((s, idx) => ({ ...s, _idx: idx }))
                    .filter(s => {
                      if (!sectionFilter) return true
                      const q = sectionFilter.toLowerCase()
                      return (
                        s.cobol_name?.toLowerCase().includes(q) ||
                        s.java_method?.toLowerCase().includes(q)
                      )
                    })
                    .map(s => (
                      <div
                        key={s._idx}
                        className={`rcw-section-item${activeSectionIdx === s._idx ? ' active' : ''}`}
                        onClick={() => {
                          bidirSyncSourceRef.current = 'section'
                          setActiveSectionIdx(s._idx)
                          setTimeout(() => { bidirSyncSourceRef.current = null }, 400)
                        }}
                        title={`COBOL lines ${s.cobol_start_line}–${s.cobol_end_line}`}
                      >
                        <span className="rcw-section-name">{s.cobol_name}</span>
                        {s.java_method && s.java_method !== 'unknown' && (
                          <span className="rcw-section-method">{s.java_method}</span>
                        )}
                      </div>
                    ))
                  }
                </div>
              )}

              {/* ── COBOL outline tab ── */}
              {sectionsPaneTab === 'cobol' && (
                <div className="rcw-sections-list">
                  {cobolOutlineItems.length === 0 && (
                    <div className="rcw-sections-empty">
                      {cobolContent ? 'No paragraphs found' : 'No COBOL file loaded'}
                    </div>
                  )}
                  {cobolOutlineItems
                    .filter(item => {
                      if (!sectionFilter) return true
                      return item.name.toLowerCase().includes(sectionFilter.toLowerCase())
                    })
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className={`rcw-section-item rcw-cobol-outline-item${item.isSection ? ' is-section' : ''}`}
                        onClick={() => {
                          cobolEditorRef.current?.revealLineInCenter(item.line)
                          cobolEditorRef.current?.setPosition({ lineNumber: item.line, column: 1 })
                          console.log('[CobolOutline] jumped to', item.name, 'line', item.line)
                        }}
                        title={`Line ${item.line}`}
                      >
                        <span className="rcw-section-name">{item.name}</span>
                        <span className="rcw-section-method rcw-cobol-outline-line">:{item.line}</span>
                      </div>
                    ))
                  }
                </div>
              )}

            </div>
          </Allotment.Pane>

          {/* Pane 3: COBOL Editor — read-only */}
          <Allotment.Pane
            minSize={200}
            visible={!cobolCollapsed}
          >
            <div className="rcw-cobol-pane">
              <div className="rcw-pane-header">
                <FileCode size={13} />
                <span>COBOL</span>
                {cobolFiles.length > 1
                  ? (
                    <select
                      className="rcw-cobol-file-select"
                      value={cobolFile?.path || ''}
                      onChange={e => {
                        const f = cobolFiles.find(x => x.path === e.target.value)
                        if (f) loadCobolFile(f)
                      }}
                    >
                      {[...cobolFiles]
                        .sort((a, b) => (b.size || 0) - (a.size || 0))
                        .map(f => (
                          <option key={f.path} value={f.path}>{f.name}</option>
                        ))
                      }
                    </select>
                  ) : cobolFile && (
                    <span className="rcw-pane-file-label">{cobolFile.name}</span>
                  )
                }
                <div className="rcw-pane-header-spacer" />
                <button
                  className="rcw-pane-close-btn"
                  onClick={() => setCobolCollapsed(true)}
                  title="Close COBOL panel"
                >
                  <X size={11} />
                </button>
              </div>
              <div className="rcw-pane-editor-area">
                {cobolLoading && (
                  <div className="ws-target-editor-placeholder">
                    <Loader2 size={24} className="ws-spin" />
                    <span>Loading COBOL…</span>
                  </div>
                )}
                {cobolError && !cobolLoading && (
                  <div className="ws-target-editor-placeholder">
                    <AlertTriangle size={24} />
                    <span>{cobolError}</span>
                  </div>
                )}
                {!cobolLoading && !cobolError && (
                  <Editor
                    height="100%"
                    language="plaintext"
                    value={cobolContent ?? ''}
                    theme="vs-dark"
                    options={{ ...monacoOptions, readOnly: true }}
                    onMount={(editor, monacoInstance) => {
                      cobolEditorRef.current = editor

                      // Helper: read selection or current line from COBOL editor
                      const getCobolSelection = (ed) => {
                        const selection = ed.getSelection()
                        const model     = ed.getModel()
                        if (!model) return null
                        let code, selectionInfo
                        if (selection && !selection.isEmpty()) {
                          code          = model.getValueInRange(selection)
                          selectionInfo = `lines ${selection.startLineNumber}–${selection.endLineNumber}`
                        } else {
                          const lineNum = ed.getPosition()?.lineNumber ?? 1
                          code          = model.getLineContent(lineNum)
                          selectionInfo = `line ${lineNum}`
                        }
                        return code.trim() ? { code, selectionInfo } : null
                      }

                      // COBOL: AI Generate Java Code (right-click + palette, Cmd+Shift+G)
                      editor.addAction({
                        id: 'rcw-cobol-generate-java',
                        label: 'AI: Generate Java Code',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 0,
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyG],
                        run: (ed) => {
                          const ctx = getCobolSelection(ed)
                          if (!ctx) return
                          const sectionName = `Lines ${ed.getSelection()?.startLineNumber ?? ed.getPosition()?.lineNumber ?? 1}–${ed.getSelection()?.endLineNumber ?? ed.getPosition()?.lineNumber ?? 1}`
                          handleGenerateJavaRef.current?.(ctx.code, ctx.selectionInfo || sectionName)
                        },
                      })

                      // COBOL: AI Explain (right-click + palette)
                      editor.addAction({
                        id: 'rcw-cobol-explain',
                        label: 'AI: Explain This Code (COBOL)',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 1,
                        run: (ed) => {
                          const ctx = getCobolSelection(ed)
                          if (!ctx) return
                          handleExplainCobol(ctx.code, ctx.selectionInfo)
                        },
                      })

                      // COBOL: AI Suggest Refactoring (right-click + palette)
                      editor.addAction({
                        id: 'rcw-cobol-refactor',
                        label: 'AI: Suggest Refactoring (COBOL)',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 2,
                        run: (ed) => {
                          const ctx = getCobolSelection(ed)
                          if (!ctx) return
                          const refactorCode = `// Request: Suggest refactoring improvements for this COBOL code.\n\n${ctx.code}`
                          handleExplainCobol(refactorCode, `Refactoring — ${ctx.selectionInfo}`)
                        },
                      })

                      // COBOL: AI Custom Prompt... (right-click + palette, Cmd+Shift+A)
                      editor.addAction({
                        id: 'rcw-cobol-custom-prompt',
                        label: 'AI: Custom Prompt... (COBOL)',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 3,
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyA],
                        run: (ed) => {
                          const ctx = getCobolSelection(ed)
                          if (!ctx) return
                          setCustomPromptModal({
                            step: 'input',
                            userPrompt: '',
                            selectedCode: ctx.code,
                            selectionInfo: ctx.selectionInfo,
                            response: null,
                            error: null,
                            filename: cobolFile?.name || 'COBOL',
                          })
                          console.log('[COBOL] custom prompt opened:', ctx.selectionInfo)
                        },
                      })

                      // ── Bidirectional sync: COBOL cursor → Java ──────────────────────────
                      // When the user moves the cursor in the COBOL pane, find which section
                      // contains that line (by cobol_start_line / cobol_end_line), then:
                      //   1. setActiveSectionIdx  → highlights the section in the Sections panel
                      //   2. Reveal the matching Java method in the Java editor
                      // Debounced 150 ms to avoid firing on every held-arrow keystroke.
                      editor.onDidChangeCursorPosition(e => {
                        // Skip if this scroll was triggered by the Java-side sync
                        if (bidirSyncSourceRef.current === 'java' || bidirSyncSourceRef.current === 'section') return
                        clearTimeout(cursorSyncTimerRef.current)
                        const capturedLine = e.position.lineNumber
                        cursorSyncTimerRef.current = setTimeout(() => {
                          const lineNum = capturedLine
                          const sects   = sectionsRef.current
                          if (!sects.length) {
                            console.log('[BiDirSync] COBOL line', lineNum, '— no sections loaded')
                            return
                          }

                          // Find the MOST SPECIFIC section containing this line.
                          // sections[] contains both COBOL section containers (large ranges,
                          // java_method='unknown') and individual paragraphs (smaller ranges,
                          // java_method='findCustomer' etc.). findIndex would return the container
                          // first, causing wrong highlight + no Java scroll.
                          // Using smallest-range match picks the innermost paragraph instead.
                          let idx = -1
                          let bestRange = Infinity
                          sects.forEach((s, i) => {
                            if (s.cobol_start_line != null && s.cobol_end_line != null &&
                                lineNum >= s.cobol_start_line && lineNum <= s.cobol_end_line) {
                              const range = s.cobol_end_line - s.cobol_start_line
                              if (range < bestRange) { bestRange = range; idx = i }
                            }
                          })
                          if (idx === -1) {
                            console.log('[BiDirSync] COBOL line', lineNum, '— no section range match')
                            return
                          }
                          const sect = sects[idx]
                          console.log('[BiDirSync] COBOL line', lineNum, '→ section', sect.cobol_name, '→ java_method:', sect.java_method)

                          bidirSyncSourceRef.current = 'cobol'

                          // Update sections panel highlight
                          setActiveSectionIdx(idx)

                          // Reveal matching Java method
                          const javaEditor = javaEditorRef.current
                          if (javaEditor && sect.java_method && sect.java_method !== 'unknown') {
                            const boundaries = javaMethodBoundariesRef.current
                            const match = boundaries.find(b => b.name === sect.java_method)
                            if (match) {
                              console.log('[BiDirSync] COBOL → Java: revealing', sect.java_method, 'at line', match.startLine)
                              javaEditor.revealLineInCenter(match.startLine)
                            } else {
                              console.log('[BiDirSync] COBOL → Java: java_method', sect.java_method, 'not found in boundaries (method not in current Java file?)')
                            }
                          }

                          // Release lock after a short window so normal Java movement works again
                          setTimeout(() => { bidirSyncSourceRef.current = null }, 300)
                        }, 150)
                      })
                    }}
                  />
                )}
              </div>
            </div>
          </Allotment.Pane>

          {/* Pane 4: Java Editor — editable, auto-save */}
          <Allotment.Pane minSize={200} visible={!javaCollapsed}>
            <div className="rcw-java-pane">
              <div className="rcw-pane-header">
                <FaJava size={13} className="rcw-fi-java" />
                <span>Java</span>
                {javaFile && (
                  <span className="rcw-pane-file-label">
                    {javaFile.name}
                    {modifiedPaths.has(javaFile.path) && (
                      <span className="rcw-pane-modified-dot" title="Unsaved changes" />
                    )}
                  </span>
                )}
                {javaFiles.length > 0 && (
                  <span className="rcw-pane-badge">{javaFiles.length} files</span>
                )}
                <div className="rcw-pane-header-spacer" />
                {javaFile && (
                  <button
                    className={`rcw-diff-toggle${diffMode ? ' active' : ''}`}
                    title={diffMode ? 'Exit diff view' : 'Compare with baseline (before your edits)'}
                    onClick={() => {
                      if (diffMode) {
                        setDiffMode(false)
                        setDiffSnapshot(null)
                      } else {
                        const path     = javaFile.path
                        const original = baselineContentRef.current[path] ?? ''
                        const modified = javaEditorRef.current?.getValue() ?? javaContent ?? ''
                        setDiffSnapshot({ original, modified, path })
                        setDiffMode(true)
                        console.log('[Diff] baseline vs current for', path,
                          '— baseline:', original.split('\n').length, 'lines',
                          '— current:', modified.split('\n').length, 'lines')
                      }
                    }}
                  >
                    <GitCompare size={13} />
                    {diffMode ? 'Exit Diff' : 'Diff'}
                  </button>
                )}
              </div>
              <div className="rcw-pane-editor-area">
                {javaLoading && (
                  <div className="ws-target-editor-placeholder">
                    <Loader2 size={24} className="ws-spin" />
                    <span>Loading Java…</span>
                  </div>
                )}
                {javaError && !javaLoading && (
                  <div className="ws-target-editor-placeholder">
                    <AlertTriangle size={24} />
                    <span>{javaError}</span>
                  </div>
                )}
                {/* ── Diff view (Feature 7) ── */}
                {/* Labels above each pane */}
                {!javaLoading && !javaError && diffMode && diffSnapshot && (
                  <>
                    <div className="rcw-diff-labels">
                      <span className="rcw-diff-label">Baseline (AI generated)</span>
                      <span className="rcw-diff-label">Your Edits</span>
                    </div>
                    <DiffEditor
                      key={`diff-${diffSnapshot.path}`}
                      height="100%"
                      language="java"
                      original={diffSnapshot.original}
                      modified={diffSnapshot.modified}
                      theme="vs-dark"
                      options={{
                        fontSize: 13,
                        fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
                        automaticLayout: true,
                        readOnly: true,
                        renderSideBySide: true,
                        scrollBeyondLastLine: false,
                        minimap: { enabled: false },
                        renderOverviewRuler: false,
                      }}
                    />
                  </>
                )}

                {/* Regular editor — always mounted, hidden during diff so edits are preserved */}
                {!javaLoading && !javaError && (
                  <div style={{ display: diffMode ? 'none' : 'contents' }}>
                  <Editor
                    key={javaFile?.path}
                    height="100%"
                    language="java"
                    defaultValue={javaContent ?? ''}
                    theme="vs-dark"
                    options={{ ...monacoOptions, readOnly: false, glyphMargin: true }}
                    onMount={(editor, monacoInstance) => {
                      javaEditorRef.current = editor
                      monacoRef.current = monacoInstance
                      decorationsRef.current = []         // fresh editor instance — no prior decoration IDs
                      qualityDecorationsRef.current = []  // fresh quality decorations
                      // Apply any errors already loaded; if not yet loaded the useEffect handles it
                      applyErrorDecorations(editor, monacoInstance, javaFile?.path || '', compileErrors)

                      // ── Java IDE Navigation providers (registered ONCE per Monaco instance) ──
                      // registerDefinitionProvider → Monaco auto-adds "Go to Definition" (F12),
                      //   "Peek Definition" (Alt+F12) to the context menu for java language models.
                      // registerReferenceProvider  → Monaco auto-adds "Go to References" (Shift+F12),
                      //   "Peek References" to the context menu.
                      // Providers read from javaNavIndexRef (updated per javaContent change) and
                      // javaNavModelsRef (ITextModel per file, created for cross-file peek support).
                      // javaFileRef mirrors javaFile state so the closures below are never stale.
                      if (!javaNavProvidersRef.current) {
                        const defProvider = monacoInstance.languages.registerDefinitionProvider('java', {
                          provideDefinition(model, position) {
                            const word = model.getWordAtPosition(position)
                            if (!word?.word) return null
                            const sym = word.word
                            const results = []
                            for (const [path, fileIdx] of Object.entries(javaNavIndexRef.current)) {
                              const defList = fileIdx?.defs?.[sym]
                              if (!Array.isArray(defList) || !defList.length) continue
                              let targetUri
                              if (path === javaFileRef.current?.path) {
                                targetUri = model.uri  // same model — always available
                              } else {
                                const m = javaNavModelsRef.current[path]
                                if (!m || m.isDisposed()) continue  // model not yet loaded — skip
                                targetUri = m.uri
                              }
                              for (const d of defList) {
                                results.push({
                                  uri: targetUri,
                                  range: {
                                    startLineNumber: d.line, startColumn: d.col,
                                    endLineNumber:   d.line, endColumn:   d.col + sym.length,
                                  },
                                })
                              }
                            }
                            return results.length ? results : null
                          },
                        })

                        const refProvider = monacoInstance.languages.registerReferenceProvider('java', {
                          provideReferences(model, position, context) {
                            const word = model.getWordAtPosition(position)
                            if (!word?.word) return null
                            const sym = word.word
                            const results = []
                            for (const [path, fileIdx] of Object.entries(javaNavIndexRef.current)) {
                              const refArr  = fileIdx?.refs?.[sym]
                              const defArr  = fileIdx?.defs?.[sym]
                              const usages  = Array.isArray(refArr) ? [...refArr] : []
                              if (context.includeDeclaration && Array.isArray(defArr)) {
                                usages.push(...defArr)
                              }
                              if (!usages.length) continue
                              let targetUri
                              if (path === javaFileRef.current?.path) {
                                targetUri = model.uri
                              } else {
                                const m = javaNavModelsRef.current[path]
                                if (!m || m.isDisposed()) continue
                                targetUri = m.uri
                              }
                              for (const r of usages) {
                                results.push({
                                  uri: targetUri,
                                  range: {
                                    startLineNumber: r.line, startColumn: r.col,
                                    endLineNumber:   r.line, endColumn:   r.col + sym.length,
                                  },
                                })
                              }
                            }
                            return results.length ? results : null
                          },
                        })

                        // ── Code Lens: "Generated from COBOL paragraph X" ─────────────
                        // Register a per-editor jump command first — addCommand returns a
                        // dynamic id that the lens provider uses for its click action.
                        // The command scrolls the COBOL pane to the matching paragraph
                        // and highlights the section, using the same lock as Feature 1.
                        const jumpCmdId = editor.addCommand(0, (_, sectIdx) => {
                          if (sectIdx == null || sectIdx < 0) return
                          const sects = sectionsRef.current
                          if (!sects[sectIdx]) return
                          bidirSyncSourceRef.current = 'section'
                          setActiveSectionIdx(sectIdx)
                          const sect = sects[sectIdx]
                          if (sect.cobol_start_line && cobolEditorRef.current) {
                            cobolEditorRef.current.revealLineInCenter(sect.cobol_start_line)
                          }
                          setTimeout(() => { bidirSyncSourceRef.current = null }, 400)
                          console.log('[CodeLens] jump to COBOL:', sect.cobol_name, 'line', sect.cobol_start_line)
                        })
                        codeLensJumpCmdRef.current = jumpCmdId

                        // onDidChange emitter — lets useEffects trigger a lens refresh
                        // when javaMethodBoundariesRef or sectionsRef update after the
                        // initial provideCodeLenses call (which may return 0 if data isn't
                        // ready yet). Monaco re-calls provideCodeLenses when this fires.
                        let _lensListeners = []
                        const fireLensRefresh = () =>
                          _lensListeners.forEach(l => { try { l() } catch { /* ignore */ } })
                        codeLensFireRefreshRef.current = fireLensRefresh

                        const lensProvider = monacoInstance.languages.registerCodeLensProvider('java', {
                          onDidChange: (listener) => {
                            _lensListeners.push(listener)
                            return {
                              dispose: () => {
                                _lensListeners = _lensListeners.filter(l => l !== listener)
                              },
                            }
                          },
                          provideCodeLenses(model) {
                            const boundaries = javaMethodBoundariesRef.current
                            const sects      = sectionsRef.current
                            const cmdId      = codeLensJumpCmdRef.current
                            if (!boundaries.length || !sects.length || !cmdId) {
                              return { lenses: [], dispose: () => {} }
                            }
                            const lenses = []
                            for (const b of boundaries) {
                              const sectIdx = sects.findIndex(s =>
                                s.java_method && s.java_method !== 'unknown' &&
                                s.java_method === b.name
                              )
                              if (sectIdx === -1) continue
                              const sect = sects[sectIdx]
                              const start = sect.cobol_start_line ?? ''
                              const end   = sect.cobol_end_line   ?? ''
                              const range = start && end ? ` (lines ${start}–${end})` : ''
                              lenses.push({
                                range: {
                                  startLineNumber: b.startLine,
                                  startColumn:     1,
                                  endLineNumber:   b.startLine,
                                  endColumn:       1,
                                },
                                command: {
                                  id:        cmdId,
                                  title:     `Generated from: ${sect.cobol_name}${range}`,
                                  arguments: [sectIdx],
                                },
                              })
                            }
                            console.log('[CodeLens] provided', lenses.length, 'lens(es)')
                            return { lenses, dispose: () => {} }
                          },
                          resolveCodeLens(model, codeLens) {
                            return codeLens
                          },
                        })

                        // ── Feature 4: COBOL Origin Hover Tooltip ─────────────────────
                        // On hover over a Java identifier that matches a sections[].java_method,
                        // show the originating COBOL paragraph name and line range.
                        const hoverProvider = monacoInstance.languages.registerHoverProvider('java', {
                          provideHover(model, position) {
                            const word = model.getWordAtPosition(position)
                            if (!word?.word) return null
                            const methodName = word.word
                            const sects = sectionsRef.current
                            const sect = sects.find(s =>
                              s.java_method && s.java_method !== 'unknown' &&
                              s.java_method === methodName
                            )
                            if (!sect) return null
                            const start = sect.cobol_start_line ?? '?'
                            const end   = sect.cobol_end_line   ?? '?'
                            return {
                              contents: [
                                { value: `**COBOL Origin**` },
                                { value: `Paragraph: \`${sect.cobol_name}\`` },
                                { value: `Lines: ${start}–${end}` },
                              ],
                            }
                          },
                        })

                        javaNavProvidersRef.current = { def: defProvider, ref: refProvider, lens: lensProvider, hover: hoverProvider }
                        console.log('[JavaNav] definition + reference + code lens + hover providers registered')
                      }

                      // Also trigger model creation for the current file if javaContent is already
                      // loaded (onMount fires after React state is set, so javaContent may be set).
                      // The useEffect above handles future changes; this covers the initial mount.
                      if (javaContent && javaFile?.path) {
                        const path = javaFile.path
                        if (!javaNavIndexRef.current[path]) {
                          javaNavIndexRef.current[path] = buildJavaNavIndex(javaContent)
                        }
                        const uri = monacoInstance.Uri.parse(`mz-java:///${path}`)
                        if (!javaNavModelsRef.current[path] || javaNavModelsRef.current[path].isDisposed()) {
                          javaNavModelsRef.current[path] = monacoInstance.editor.createModel(javaContent, 'java', uri)
                        }
                      }
                      // Phase 8: if an ISSUES-tab click caused this file to load, jump to that line
                      if (pendingScrollLineRef.current != null) {
                        const jumpLine = pendingScrollLineRef.current
                        pendingScrollLineRef.current = null
                        setTimeout(() => {
                          editor.revealLineInCenter(jumpLine)
                          editor.setPosition({ lineNumber: jumpLine, column: 1 })
                        }, 50)
                      }
                      // Glyph margin click → store selected error (Phase 10 will open AI modal)
                      editor.onMouseDown(e => {
                        if (e.target.type === monacoInstance.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
                          const line = e.target.position?.lineNumber
                          if (line == null) return
                          const path = javaFile?.path || ''
                          const err = compileErrorsRef.current.find(
                            er => er.file === path && er.line === line
                          )
                          if (err) {
                            setSelectedError(err)
                            setAiFixOpen(true)
                            console.log('[Phase7] error glyph clicked → AI modal open:', err)
                          }
                        }
                      })

                      // Phase 11 — palette only (no context menu): AI Fix (line-aware)
                      editor.addAction({
                        id: 'rcw-java-ai-fix',
                        label: 'MZ: AI Fix (Java)',
                        run: (ed) => {
                          const lineNum  = ed.getPosition()?.lineNumber
                          if (!lineNum) return
                          const filePath = javaFile?.path || ''
                          const err = compileErrorsRef.current.find(
                            e => e.file === filePath && e.line === lineNum
                          )
                          if (!err) return  // no compile error on this line — no-op
                          setSelectedError(err)
                          setAiFixOpen(true)
                          console.log('[Phase11] AI Fix via palette:', err)
                        },
                      })

                      // Phase 11 — palette only (no context menu): Save File
                      // Delegates to handleSaveCurrentFileRef so Cmd+S goes through
                      // the same confirmation modal as the toolbar Save button.
                      editor.addAction({
                        id: 'rcw-java-save',
                        label: 'MZ: Save File',
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS],
                        run: () => {
                          handleSaveCurrentFileRef.current()
                        },
                      })

                      // Phase A — palette only (no context menu): Monaco built-ins
                      editor.addAction({
                        id: 'rcw-open-command-palette',
                        label: 'MZ: Command Palette',
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyP],
                        run: (ed) => { ed.trigger('keyboard', 'editor.action.quickCommand', null) },
                      })

                      editor.addAction({
                        id: 'rcw-comment-out-line',
                        label: 'MZ: Comment Out Line',
                        run: (ed) => { ed.trigger('keyboard', 'editor.action.commentLine', null) },
                      })

                      editor.addAction({
                        id: 'rcw-block-comment',
                        label: 'MZ: Block Comment Selection',
                        run: (ed) => { ed.trigger('keyboard', 'editor.action.blockComment', null) },
                      })

                      editor.addAction({
                        id: 'rcw-jump-to-end',
                        label: 'MZ: Jump to End of File',
                        run: (ed) => { ed.trigger('keyboard', 'cursorBottom', null) },
                      })

                      // Batch B1 — palette only (no context menu): Explain Issue
                      editor.addAction({
                        id: 'rcw-explain-issue',
                        label: 'MZ: Explain Issue',
                        run: () => handleExplainJavaRef.current(),
                      })

                      // Batch B1 — context menu + palette: AI Explain
                      editor.addAction({
                        id: 'rcw-ai-explain-java',
                        label: 'AI: Explain This Code',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 1,
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyE],
                        run: () => handleExplainJavaRef.current(),
                      })

                      // Batch B2 — context menu + palette: AI Suggest Refactoring
                      editor.addAction({
                        id: 'rcw-ai-refactor-java',
                        label: 'AI: Suggest Refactoring',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 2,
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyR],
                        run: () => handleRefactorJavaRef.current(),
                      })

                      // Batch B3 — context menu + palette: AI Clean Code
                      editor.addAction({
                        id: 'rcw-ai-clean-code',
                        label: 'AI: Clean Code',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 3,
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyK],
                        run: () => handleCleanCodeJavaRef.current(),
                      })

                      // Batch B4 — palette only (no context menu): Repair Actions
                      editor.addAction({
                        id: 'rcw-ai-safe-patch',
                        label: 'MZ: AI Fix (Safe Patch)',
                        run: () => handleAISafePatchRef.current(),
                      })

                      editor.addAction({
                        id: 'rcw-ai-repair-region',
                        label: 'MZ: AI Repair Region',
                        run: () => handleAIRepairRegionRef.current(),
                      })

                      editor.addAction({
                        id: 'rcw-ai-repair-method',
                        label: 'MZ: AI Repair Method',
                        run: () => handleAIRepairMethodRef.current(),
                      })

                      editor.addAction({
                        id: 'rcw-ai-repair-file-structure',
                        label: 'MZ: AI Repair File Structure',
                        run: () => handleAIRepairFileStructRef.current(),
                      })

                      // Batch B5 — context menu + palette: AI Custom Prompt
                      editor.addAction({
                        id: 'rcw-ai-custom-java',
                        label: 'AI: Custom Prompt...',
                        contextMenuGroupId: 'rcw',
                        contextMenuOrder: 4,
                        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyA],
                        run: () => handleCustomPromptJavaRef.current(),
                      })

                      // Batch B6 — palette only (no context menu): Apply as Stub
                      editor.addAction({
                        id: 'rcw-apply-stub',
                        label: 'MZ: Apply as Stub',
                        run: () => handleApplyStubRef.current(),
                      })

                      // ── Bidirectional sync: Java cursor → COBOL ──────────────────────────
                      // When the cursor moves inside the Java editor, find the enclosing
                      // method using javaMethodBoundariesRef, then match it against
                      // sectionsRef[].java_method, and:
                      //   1. setActiveSectionIdx  → highlights section in Sections panel
                      //   2. revealLineInCenter   → scrolls COBOL pane to the source paragraph
                      // Debounced 150 ms; guarded by bidirSyncSourceRef to prevent loops.
                      editor.onDidChangeCursorPosition(e => {
                        if (bidirSyncSourceRef.current === 'cobol' || bidirSyncSourceRef.current === 'section') return
                        clearTimeout(cursorSyncTimerRef.current)
                        // Capture lineNum immediately — do not read e inside the timeout
                        const capturedLine = e.position.lineNumber
                        cursorSyncTimerRef.current = setTimeout(() => {
                          const lineNum    = capturedLine
                          const boundaries = javaMethodBoundariesRef.current
                          const sects      = sectionsRef.current

                          if (!boundaries.length) {
                            console.log('[BiDirSync] Java line', lineNum, '— no boundaries (javaContent not parsed yet?)')
                            return
                          }
                          if (!sects.length) {
                            console.log('[BiDirSync] Java line', lineNum, '— no sections loaded')
                            return
                          }

                          // Find the method whose line range contains the cursor
                          const method = boundaries.find(b =>
                            lineNum >= b.startLine && lineNum <= b.endLine
                          )
                          if (!method) {
                            console.log('[BiDirSync] Java line', lineNum, '— outside all method boundaries')
                            return
                          }

                          // Match against sections[]
                          const idx = sects.findIndex(s =>
                            s.java_method && s.java_method !== 'unknown' &&
                            s.java_method === method.name
                          )
                          if (idx === -1) {
                            console.log('[BiDirSync] Java line', lineNum, '→', method.name, '— no section match (unmapped method/inner class)')
                            return
                          }

                          const sect = sects[idx]
                          console.log('[BiDirSync] Java line', lineNum, '→', method.name, '→ section', sect.cobol_name, '→ COBOL line', sect.cobol_start_line)

                          bidirSyncSourceRef.current = 'java'
                          setActiveSectionIdx(idx)
                          setTimeout(() => { bidirSyncSourceRef.current = null }, 300)
                        }, 150)
                      })
                    }}
                    onChange={handleJavaChange}
                  />
                  </div>
                )}
              </div>{/* rcw-pane-editor-area */}
            </div>
          </Allotment.Pane>

        </Allotment>
        </div>{/* rcw-workbench-row */}
      </div>

      {/* ── Feature 5: Data Dictionary Modal ── */}
      {dataDictOpen && (() => {
        // ── sort helper ──
        const cycleSort = (col) => setDataDictSort(prev =>
          prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }
        )
        const SortIcon = ({ col }) => {
          if (dataDictSort.col !== col) return <span className="rcw-dict-sort-icon rcw-dict-sort-icon--off">↕</span>
          return <span className="rcw-dict-sort-icon rcw-dict-sort-icon--on">{dataDictSort.dir === 'asc' ? '↑' : '↓'}</span>
        }

        // ── build + filter + sort rows ──
        const q = dataDictFilter.toLowerCase()
        const allItems = [
          ...( dataDictData?.groups     || []).map(g => ({ ...g, _kind: 'group' })),
          ...( dataDictData?.fields     || []).map(f => ({ ...f, _kind: 'field' })),
          ...( dataDictData?.conditions || []).map(c => ({ ...c, _kind: 'condition' })),
        ]
        const filtered = allItems.filter(item => {
          if (dataDictHideFiller && item.is_filler) return false
          if (!q) return true
          return (
            item.cobol_name?.trim().toLowerCase().includes(q) ||
            item.java_name?.toLowerCase().includes(q)  ||
            item.java_type?.toLowerCase().includes(q)  ||
            item.pic?.toLowerCase().includes(q)
          )
        })
        const sortedRows = [...filtered].sort((a, b) => {
          const dir = dataDictSort.dir === 'asc' ? 1 : -1
          switch (dataDictSort.col) {
            case 'lvl':   return dir * ((a.level   || 0) - (b.level   || 0))
            case 'cobol': return dir * (a.cobol_name?.trim() || '').localeCompare(b.cobol_name?.trim() || '')
            case 'java':  return dir * (a.java_name  || '').localeCompare(b.java_name  || '')
            case 'type':  return dir * (a.java_type  || '').localeCompare(b.java_type  || '')
            case 'line':  return dir * ((a.line_num || 0) - (b.line_num || 0))
            default:      return dir * ((a.line_num || 0) - (b.line_num || 0))
          }
        })

        // ── CSV export ──
        const handleExport = () => {
          const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`
          const headers = ['Level','COBOL Name','Java Name','PIC','Java Type','Array','Redefines','Occurs','Depending On','Line']
          const rows = sortedRows.map(r => [
            r.level, r.cobol_name?.trim() ?? '', r.java_name ?? '', r.pic ?? '', r.java_type ?? '',
            r.is_array ? 'Y' : '', r.redefines ?? '', r.occurs ?? '', r.depending_on ?? '', r.line_num ?? '',
          ].map(esc).join(','))
          const csv = [headers.join(','), ...rows].join('\n')
          const a = document.createElement('a')
          a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
          const fname = (dataDictData?.source_file || 'data').split('/').pop().replace(/\..+$/, '')
          a.download = `${fname}_data_dictionary.csv`
          a.click()
          URL.revokeObjectURL(a.href)
        }

        return (
          <div
            className={`rcw-dict-overlay${dataDictFullscreen ? ' rcw-dict-overlay--fs' : ''}`}
            onClick={() => !dataDictFullscreen && setDataDictOpen(false)}
          >
            <div
              className={`rcw-dict-modal${dataDictFullscreen ? ' rcw-dict-modal--fs' : ''}`}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="rcw-dict-header">
                <Database size={14} className="rcw-dict-header-icon" />
                <span className="rcw-dict-title">Data Dictionary</span>
                {dataDictData?.source_file && (
                  <span className="rcw-dict-source">
                    {dataDictData.source_file.split('/').pop().replace('_line_inventory.json','').toUpperCase()}
                  </span>
                )}
                <div className="rcw-dict-header-spacer" />
                {/* Export CSV */}
                {dataDictData && !dataDictLoading && (
                  <button className="rcw-dict-action-btn" onClick={handleExport} title="Export visible rows as CSV">
                    <Download size={13} />
                    <span>CSV</span>
                  </button>
                )}
                {/* Fullscreen toggle */}
                <button
                  className="rcw-dict-action-btn"
                  onClick={() => setDataDictFullscreen(v => !v)}
                  title={dataDictFullscreen ? 'Exit full screen' : 'Full screen'}
                >
                  {dataDictFullscreen
                    ? <><ArrowLeft size={13} /><span>Exit</span></>
                    : <><ExternalLink size={13} /><span>Full screen</span></>
                  }
                </button>
                <button className="rcw-dict-close" onClick={() => setDataDictOpen(false)} title="Close (Esc)">
                  <X size={14} />
                </button>
              </div>

              {/* Toolbar */}
              {!dataDictLoading && !dataDictError && dataDictData && (
                <div className="rcw-dict-toolbar">
                  <div className="rcw-dict-search">
                    <Search size={12} />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Filter by COBOL name, Java name, or type…"
                      value={dataDictFilter}
                      onChange={e => setDataDictFilter(e.target.value)}
                      onKeyDown={e => e.key === 'Escape' && (dataDictFilter ? setDataDictFilter('') : setDataDictOpen(false))}
                    />
                    {dataDictFilter && (
                      <button className="rcw-dict-search-clear" onClick={() => setDataDictFilter('')}>
                        <X size={10} />
                      </button>
                    )}
                  </div>
                  <label className="rcw-dict-toggle">
                    <input type="checkbox" checked={dataDictHideFiller} onChange={e => setDataDictHideFiller(e.target.checked)} />
                    Hide FILLER
                  </label>
                  {dataDictFilter && (
                    <span className="rcw-dict-match-count">{sortedRows.length} match{sortedRows.length !== 1 ? 'es' : ''}</span>
                  )}
                </div>
              )}

              {/* Body */}
              <div className="rcw-dict-body">
                {dataDictLoading && (
                  <div className="rcw-dict-state">
                    <Loader2 size={22} className="ws-spin" />
                    <span>Loading field mapping…</span>
                  </div>
                )}
                {dataDictError && !dataDictLoading && (
                  <div className="rcw-dict-state rcw-dict-state--error">
                    <AlertTriangle size={22} />
                    <span>{dataDictError}</span>
                  </div>
                )}
                {!dataDictLoading && !dataDictError && dataDictData && (
                  sortedRows.length === 0 ? (
                    <div className="rcw-dict-state">
                      <span>No fields match "{dataDictFilter}"</span>
                    </div>
                  ) : (
                    <table className="rcw-dict-table">
                      <thead>
                        <tr>
                          <th className="rcw-dict-th rcw-dict-th--lvl rcw-dict-th--sort" onClick={() => cycleSort('lvl')}>
                            Lvl <SortIcon col="lvl" />
                          </th>
                          <th className="rcw-dict-th rcw-dict-th--sort" onClick={() => cycleSort('cobol')}>
                            COBOL Name <SortIcon col="cobol" />
                          </th>
                          <th className="rcw-dict-th rcw-dict-th--sort" onClick={() => cycleSort('java')}>
                            Java Name <SortIcon col="java" />
                          </th>
                          <th className="rcw-dict-th rcw-dict-th--sort" onClick={() => cycleSort('type')}>
                            PIC / Type <SortIcon col="type" />
                          </th>
                          <th className="rcw-dict-th rcw-dict-th--flags">Flags</th>
                          <th className="rcw-dict-th rcw-dict-th--line rcw-dict-th--sort" onClick={() => cycleSort('line')}>
                            Line <SortIcon col="line" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((item, i) => {
                          const indent = 0
                          const isGroup = item._kind === 'group'
                          const isCond  = item._kind === 'condition'
                          return (
                            <tr key={i} className={`rcw-dict-row${isGroup ? ' rcw-dict-row--group' : ''}${isCond ? ' rcw-dict-row--cond' : ''}`}>
                              <td className="rcw-dict-td rcw-dict-td--lvl">{item.level}</td>
                              <td className="rcw-dict-td rcw-dict-td--cobol" style={{ paddingLeft: `${8 + indent}px` }}>
                                {item.cobol_name?.trim()}
                              </td>
                              <td className="rcw-dict-td rcw-dict-td--java">
                                {item.java_name || <span className="rcw-dict-muted">—</span>}
                              </td>
                              <td className="rcw-dict-td rcw-dict-td--type">
                                {item.pic
                                  ? <><span className="rcw-dict-pic">{item.pic}</span><span className="rcw-dict-arrow">→</span><span className="rcw-dict-jtype">{item.java_type}</span></>
                                  : <span className="rcw-dict-jtype">{item.java_type || <span className="rcw-dict-muted">group</span>}</span>
                                }
                              </td>
                              <td className="rcw-dict-td rcw-dict-td--flags">
                                {item.is_array     && <span className="rcw-dict-pill rcw-dict-pill--array">[ ]</span>}
                                {item.redefines    && <span className="rcw-dict-pill rcw-dict-pill--redef">↔</span>}
                                {item.occurs && !item.is_array && <span className="rcw-dict-pill rcw-dict-pill--occurs">×{item.occurs}</span>}
                                {item.depending_on && <span className="rcw-dict-pill rcw-dict-pill--dep">DEP</span>}
                              </td>
                              <td className="rcw-dict-td rcw-dict-td--line rcw-dict-muted">{item.line_num}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )
                )}
              </div>

              {/* Footer */}
              {dataDictData && !dataDictLoading && !dataDictError && (
                <div className="rcw-dict-footer">
                  <span>{(dataDictData.fields?.length ?? 0) + (dataDictData.groups?.length ?? 0)} total items</span>
                  <span className="rcw-dict-footer-sep">·</span>
                  <span>{dataDictData.fields?.length ?? 0} fields</span>
                  <span className="rcw-dict-footer-sep">·</span>
                  <span>{dataDictData.groups?.length ?? 0} groups</span>
                  {dataDictData.stats?.with_occurs > 0 && <>
                    <span className="rcw-dict-footer-sep">·</span>
                    <span>{dataDictData.stats.with_occurs} arrays</span>
                  </>}
                  {dataDictData.stats?.with_redefines > 0 && <>
                    <span className="rcw-dict-footer-sep">·</span>
                    <span>{dataDictData.stats.with_redefines} redefines</span>
                  </>}
                  <div className="rcw-dict-footer-spacer" />
                  {dataDictFilter && <span className="rcw-dict-match-count">{sortedRows.length} shown</span>}
                </div>
              )}

            </div>
          </div>
        )
      })()}

      {/* ── Phase 12: Bottom Action Bar ── */}
      <div className="rcw-bottom-bar">

        {/* Left: Reset to Baseline (destructive — two-step confirm) */}
        <div className="rcw-bottom-bar-left">
          {resetError && (
            <span className="rcw-bottom-bar-err">
              <AlertTriangle size={11} /> {resetError}
            </span>
          )}
          {downloadError && (
            <span className="rcw-bottom-bar-err">
              <AlertTriangle size={11} /> {downloadError}
            </span>
          )}
          {!resetConfirming ? (
            <button
              className="rcw-reset-btn"
              onClick={() => { setResetConfirming(true); setResetError(null) }}
              disabled={resetting || !bundleJobIds}
              title="Roll back all editor saves to the baseline compile state"
            >
              {resetting
                ? <><Loader2 size={13} className="rcv-spin" /> Resetting…</>
                : <><RefreshCw size={13} /> Reset to Baseline</>}
            </button>
          ) : (
            <div className="rcw-reset-confirm-row">
              <span className="rcw-reset-confirm-label">Reset all edits?</span>
              <button
                className="rcw-reset-btn rcw-reset-btn--confirm"
                onClick={handleReset}
                disabled={resetting}
              >
                {resetting
                  ? <><Loader2 size={13} className="rcv-spin" /> Resetting…</>
                  : 'Confirm Reset'}
              </button>
              <button
                className="rcw-reset-cancel-btn"
                onClick={() => setResetConfirming(false)}
                disabled={resetting}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Centre: Status metrics (Feature 9) */}
        {bundleJobIds && (() => {
          const cobolLines  = cobolContent ? cobolContent.split('\n').length : 0
          const javaFileCount = (allJavaFiles.length || javaFiles.length)
          const javaLines   = javaContent ? javaContent.split('\n').length : 0
          const score       = compileTotalErrors === 0
            ? 100
            : Math.max(0, Math.round((1 - compileTotalErrors / Math.max(sections.length, 1)) * 100))
          return (
            <div className="rcw-status-metrics">
              {cobolLines > 0 && (
                <span className="rcw-status-metric" title="COBOL source lines loaded">
                  COBOL: {cobolLines.toLocaleString()} lines
                </span>
              )}
              {javaFileCount > 0 && (
                <span className="rcw-status-metric" title="Java files in project">
                  Java: {javaFileCount} {javaFileCount === 1 ? 'file' : 'files'}
                  {javaLines > 0 && <> · {javaLines.toLocaleString()} lines</>}
                </span>
              )}
              <span
                className={`rcw-status-metric rcw-status-metric--${compileTotalErrors === 0 ? 'pass' : 'fail'}`}
                title="Compile error count"
              >
                {compileTotalErrors === 0 ? 'Build Clean' : `${compileTotalErrors} error${compileTotalErrors !== 1 ? 's' : ''}`}
              </span>
              {qualityWarnings.length > 0 && (
                <span
                  className="rcw-status-metric rcw-status-metric--review"
                  title={`${qualityWarnings.length} method${qualityWarnings.length !== 1 ? 's' : ''} flagged for manual review — check Issues panel`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExplorerTab('issues')}
                >
                  ⚠ {qualityWarnings.length} review{qualityWarnings.length !== 1 ? 's' : ''}
                </span>
              )}
              <span
                className={`rcw-status-metric rcw-status-metric--score${score >= 90 ? ' high' : score >= 70 ? ' mid' : ' low'}`}
                title="Conversion quality score"
              >
                Score: {score}%
              </span>
            </div>
          )
        })()}

        {/* Right: Download + Re-Run Build */}
        <div className="rcw-bottom-bar-right">
          <button
            className="rcw-bar-btn rcw-bar-btn--download"
            onClick={handleWorkbenchDownload}
            disabled={!bundleJobIds}
            title="Download all Java files with your current workspace edits applied"
          >
            <Download size={13} /> Download
          </button>
          <button
            className={`rcw-bar-btn rcw-bar-btn--rerun${rerunning ? ' running' : rerunStatus === 'passed' ? ' passed' : ''}`}
            onClick={handleRerunWithSave}
            disabled={rerunning || !bundleJobIds}
            title="Re-run pre-flight compile with current workspace edits applied"
          >
            {rerunning
              ? <><Loader2 size={13} className="rcv-spin" /> Building…</>
              : rerunStatus === 'passed'
              ? <><CheckCircle2 size={13} /> Build Passed</>
              : rerunStatus === 'failed'
              ? <><XCircle size={13} /> Re-Run Build</>
              : <><Play size={13} /> Re-Run Build</>}
          </button>
        </div>

      </div>

      </>)}

      {/* ── Phase 11: AI Explain Modal (COBOL + Java) ── */}
      {explainModal.open && (
        <div
          className="rcw-ai-modal-overlay"
          onClick={() => setExplainModal(prev => ({ ...prev, open: false }))}
        >
          <div className="rcw-ai-modal rcw-explain-modal" onClick={e => e.stopPropagation()}>

            <div className="rcw-ai-modal-header">
              <Sparkles size={14} className="rcw-ai-modal-icon" />
              <span className="rcw-ai-modal-title">AI Explain</span>
              {explainModal.selectionInfo && (
                <span className="rcw-ai-modal-file-label">
                  {explainModal.language || 'COBOL'} — {explainModal.selectionInfo}
                </span>
              )}
              <button
                className="rcw-ai-modal-close-btn"
                onClick={() => setExplainModal(prev => ({ ...prev, open: false }))}
                title="Close"
              >
                <X size={14} />
              </button>
            </div>

            <div className="rcw-ai-modal-body">
              {explainModal.loading && (
                <div className="rcw-ai-modal-loading">
                  <Loader2 size={22} className="rcv-spin" />
                  <span>Analyzing {explainModal.language || 'COBOL'}…</span>
                </div>
              )}
              {explainModal.error && !explainModal.loading && (
                <div className="rcw-ai-modal-err-state">
                  <AlertTriangle size={14} />
                  <span>{explainModal.error}</span>
                </div>
              )}
              {explainModal.text && !explainModal.loading && (
                <div className="rcw-explain-text">{explainModal.text}</div>
              )}
            </div>

            <div className="rcw-ai-modal-footer">
              <button
                className="rcw-ai-close-btn"
                onClick={() => setExplainModal(prev => ({ ...prev, open: false }))}
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Batch B3: AI Clean Code Preview Modal ── */}
      {cleanCodeModal && (
        <div
          className="rcw-ai-modal-overlay"
          onClick={() => !cleanCodeModal.loading && setCleanCodeModal(null)}
        >
          <div className="rcw-ai-modal rcw-clean-modal" onClick={e => e.stopPropagation()}>

            <div className="rcw-ai-modal-header">
              <Sparkles size={14} className="rcw-ai-modal-icon" />
              <span className="rcw-ai-modal-title">AI: Clean Code</span>
              {cleanCodeModal.filename && (
                <span className="rcw-ai-modal-file-label">{cleanCodeModal.filename}</span>
              )}
              <button
                className="rcw-ai-modal-close-btn"
                onClick={() => setCleanCodeModal(null)}
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>

            <div className="rcw-ai-modal-body rcw-clean-modal-body">
              {cleanCodeModal.loading && (
                <div className="rcw-ai-modal-loading">
                  <Loader2 size={22} className="rcv-spin" />
                  <span>Cleaning code…</span>
                </div>
              )}
              {cleanCodeModal.error && !cleanCodeModal.loading && (
                <div className="rcw-ai-modal-err-state">
                  <AlertTriangle size={14} />
                  <span>{cleanCodeModal.error}</span>
                </div>
              )}
              {cleanCodeModal.cleaned && !cleanCodeModal.loading && (
                <>
                  <div className="rcw-clean-modal-hint">
                    Review the cleaned code below, then click Apply to replace the file content.
                  </div>
                  <div className="rcw-clean-modal-code">
                    <Editor
                      height="100%"
                      language="java"
                      value={cleanCodeModal.cleaned}
                      theme="vs-dark"
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                        lineNumbers: 'on',
                        automaticLayout: true,
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="rcw-ai-modal-footer">
              <button
                className="rcw-ai-close-btn"
                onClick={() => setCleanCodeModal(null)}
              >
                Cancel
              </button>
              {cleanCodeModal.cleaned && !cleanCodeModal.loading && (
                <button
                  className="rcw-ai-apply-btn"
                  onClick={handleApplyCleanCode}
                >
                  Apply
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── Batch B4: MZ Repair Modal ── */}
      {repairModal && (
        <div
          className="rcw-ai-modal-overlay"
          onClick={() => !repairModal.loading && setRepairModal(null)}
        >
          <div className="rcw-ai-modal rcw-repair-modal" onClick={e => e.stopPropagation()}>

            <div className="rcw-ai-modal-header">
              <Sparkles size={14} className="rcw-ai-modal-icon" />
              <span className="rcw-ai-modal-title">{repairModal.label}</span>
              {repairModal.filename && (
                <span className="rcw-ai-modal-file-label">{repairModal.filename}</span>
              )}
              <button
                className="rcw-ai-modal-close-btn"
                onClick={() => setRepairModal(null)}
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>

            <div className="rcw-ai-modal-body rcw-repair-modal-body">
              {repairModal.loading && (
                <div className="rcw-ai-modal-loading">
                  <Loader2 size={22} className="rcv-spin" />
                  <span>Generating repair patch…</span>
                </div>
              )}
              {repairModal.error && !repairModal.loading && (
                <div className="rcw-ai-modal-err-state">
                  <AlertTriangle size={14} />
                  <span>{repairModal.error}</span>
                </div>
              )}
              {repairModal.patch && !repairModal.loading && (
                <>
                  <div className="rcw-repair-modal-meta">
                    {repairModal.patch.explanation && (
                      <div className="rcw-repair-explanation">{repairModal.patch.explanation}</div>
                    )}
                    <div className="rcw-repair-range-hint">
                      Replacing lines {repairModal.patch.start_line}–{repairModal.patch.end_line ?? repairModal.patch.start_line}
                      {repairModal.patch.confidence != null && (
                        <span className="rcw-repair-confidence">
                          {' '}· Confidence: {Math.round(repairModal.patch.confidence * 100)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rcw-repair-modal-code">
                    <Editor
                      height="100%"
                      language="java"
                      value={repairModal.previewText}
                      theme="vs-dark"
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                        lineNumbers: 'on',
                        automaticLayout: true,
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="rcw-ai-modal-footer">
              <button
                className="rcw-ai-close-btn"
                onClick={() => setRepairModal(null)}
              >
                Cancel
              </button>
              {(repairModal.error || repairModal.patch) && !repairModal.loading && (
                <button
                  className="rcw-ai-regenerate-btn"
                  onClick={() => {
                    const { actionId, label, contextCode, filename } = repairModal
                    const line = repairModal.patch?.start_line ?? 1
                    _generateRepair(actionId, label, contextCode, filename, line, '')
                  }}
                >
                  Regenerate
                </button>
              )}
              {repairModal.patch && !repairModal.loading && (
                <button
                  className="rcw-ai-apply-btn"
                  onClick={() => {
                    _applyRepairToEditor(repairModal.patch)
                    setRepairModal(null)
                  }}
                >
                  <Check size={13} /> Apply
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── Batch B5: AI Custom Prompt Modal ── */}
      {customPromptModal && (() => {
        const { step, userPrompt, selectionInfo, response, error } = customPromptModal
        // Local submit handler — reads textarea value from state
        const canSend = step === 'input' && userPrompt.trim().length > 0
        return (
          <div
            className="rcw-ai-modal-overlay"
            onClick={() => step !== 'loading' && setCustomPromptModal(null)}
          >
            <div className="rcw-ai-modal rcw-custom-prompt-modal" onClick={e => e.stopPropagation()}>

              <div className="rcw-ai-modal-header">
                <Sparkles size={14} className="rcw-ai-modal-icon" />
                <span className="rcw-ai-modal-title">AI: Custom Prompt</span>
                {selectionInfo && (
                  <span className="rcw-ai-modal-file-label">Java — {selectionInfo}</span>
                )}
                <button
                  className="rcw-ai-modal-close-btn"
                  onClick={() => setCustomPromptModal(null)}
                  title="Close"
                  disabled={step === 'loading'}
                >
                  <X size={14} />
                </button>
              </div>

              <div className="rcw-ai-modal-body rcw-custom-prompt-body">

                {/* Step 1 — Input */}
                {(step === 'input' || step === 'loading') && (
                  <div className="rcw-custom-prompt-input-section">
                    <label className="rcw-custom-prompt-label">
                      Ask anything about this code:
                    </label>
                    <textarea
                      className="rcw-custom-prompt-textarea"
                      placeholder="E.g. What does this method do? How can I fix the null pointer? Suggest a better approach."
                      value={userPrompt}
                      disabled={step === 'loading'}
                      onChange={e => setCustomPromptModal(prev => prev ? { ...prev, userPrompt: e.target.value } : null)}
                      onKeyDown={e => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSend) {
                          e.preventDefault()
                          handleCustomPromptSend(userPrompt)
                        }
                      }}
                      rows={4}
                      autoFocus
                    />
                  </div>
                )}

                {/* Loading */}
                {step === 'loading' && (
                  <div className="rcw-ai-modal-loading">
                    <Loader2 size={22} className="rcv-spin" />
                    <span>Sending to AI…</span>
                  </div>
                )}

                {/* Step 2 — Result */}
                {step === 'result' && error && (
                  <div className="rcw-ai-modal-err-state">
                    <AlertTriangle size={14} />
                    <span>{error}</span>
                  </div>
                )}
                {step === 'result' && response && (
                  <div className="rcw-explain-text rcw-custom-prompt-result">{response}</div>
                )}

              </div>

              <div className="rcw-ai-modal-footer">
                <button
                  className="rcw-ai-close-btn"
                  onClick={() => setCustomPromptModal(null)}
                  disabled={step === 'loading'}
                >
                  Close
                </button>
                {step === 'result' && (
                  <button
                    className="rcw-ai-regenerate-btn"
                    onClick={() => handleCustomPromptSend(userPrompt)}
                  >
                    Regenerate
                  </button>
                )}
                {(step === 'input' || step === 'loading') && (
                  <button
                    className="rcw-ai-apply-btn"
                    onClick={() => handleCustomPromptSend(userPrompt)}
                    disabled={!canSend}
                    title={canSend ? 'Send (Cmd/Ctrl+Enter)' : 'Enter a prompt first'}
                  >
                    Send
                  </button>
                )}
              </div>

            </div>
          </div>
        )
      })()}

      {/* ── Phase 10: AI Fix Modal ── */}
      {aiFixOpen && selectedError && (
        <div className="rcw-ai-modal-overlay" onClick={handleCloseAIModal}>
          <div className="rcw-ai-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="rcw-ai-modal-header">
              <Sparkles size={14} className="rcw-ai-modal-icon" />
              <span className="rcw-ai-modal-title">AI Fix</span>
              <span className="rcw-ai-modal-file-label">
                {selectedError.file}:{selectedError.line}
              </span>
              <button className="rcw-ai-modal-close-btn" onClick={handleCloseAIModal} title="Close">
                <X size={14} />
              </button>
            </div>

            {/* Error message */}
            <div className="rcw-ai-modal-error-bar">
              <AlertTriangle size={12} />
              <span>{selectedError.message}</span>
            </div>

            {/* Phase 1 — cascade warning */}
            {selectedError.is_root_candidate === false && (
              <div className="rcw-ai-modal-cascade-warning">
                <AlertTriangle size={11} />
                <span>
                  This may be a cascade error
                  {selectedError.cascade_reason ? ` (${selectedError.cascade_reason})` : ''}.
                  Consider fixing the root candidate first.
                </span>
              </div>
            )}

            {/* Body */}
            <div className="rcw-ai-modal-body">

              {/* Loading */}
              {analyzeState.phase === STATE_LOADING && (
                <div className="rcw-ai-modal-loading">
                  <Loader2 size={22} className="rcv-spin" />
                  <span>Analyzing error…</span>
                </div>
              )}

              {/* Error state */}
              {analyzeState.phase === STATE_ERROR && (
                <div className="rcw-ai-modal-err-state">
                  <AlertTriangle size={14} />
                  <span>{analyzeState.error || 'Analysis failed'}</span>
                </div>
              )}

              {/* Ready / Applying / Applied — show cards */}
              {(analyzeState.phase === STATE_READY ||
                analyzeState.phase === STATE_APPLYING ||
                analyzeState.phase === STATE_APPLIED) && (
                <div className="rcw-ai-cards">
                  {analyzeState.cards.length === 0 && (
                    <div className="rcw-ai-no-cards">
                      No fix suggestions available for this error.
                    </div>
                  )}
                  {analyzeState.cards.map((card, i) => {
                    const cardKey = `${card.file}:${card.line}:${card.error_message || ''}`
                    const isApplied  = analyzeState.applied?.includes(cardKey)
                    const isApplying = analyzeState.applying === cardKey
                    return (
                      <div key={i} className={`rcw-ai-card${isApplied ? ' rcw-ai-card--applied' : ''}`}>

                        {card.explanation && (
                          <div className="rcw-ai-card-explanation">{card.explanation}</div>
                        )}
                        {card.root_cause && (
                          <div className="rcw-ai-card-root-cause">
                            <strong>Root cause:</strong> {card.root_cause}
                          </div>
                        )}

                        {card.fix && (
                          <div className="rcw-ai-card-diff">
                            {card.fix.old_text && (
                              <div className="rcw-ai-diff-block rcw-ai-diff-block--old">
                                <span className="rcw-ai-diff-label">Before</span>
                                <pre className="rcw-ai-diff-pre">{card.fix.old_text}</pre>
                              </div>
                            )}
                            {card.fix.new_text && (
                              <div className="rcw-ai-diff-block rcw-ai-diff-block--new">
                                <span className="rcw-ai-diff-label">After</span>
                                <pre className="rcw-ai-diff-pre">{card.fix.new_text}</pre>
                              </div>
                            )}
                          </div>
                        )}

                        {card.confidence != null && (
                          <div className="rcw-ai-card-confidence">
                            Confidence: {Math.round(card.confidence * 100)}%
                          </div>
                        )}

                        <div className="rcw-ai-card-actions">
                          {isApplied ? (
                            <span className="rcw-ai-applied-badge">
                              <CheckCircle2 size={12} /> Applied
                            </span>
                          ) : card.can_auto_fix ? (
                            <button
                              className="rcw-ai-apply-btn"
                              onClick={() => handleApplyFix(card)}
                              disabled={isApplying || analyzeState.phase === STATE_APPLYING}
                            >
                              {isApplying
                                ? <><Loader2 size={12} className="rcv-spin" /> Applying…</>
                                : <><Check size={12} /> Apply Fix</>}
                            </button>
                          ) : (
                            <span className="rcw-ai-no-fix-note">Manual fix required</span>
                          )}
                        </div>

                      </div>
                    )
                  })}
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="rcw-ai-modal-footer">
              <button
                className="rcw-ai-regenerate-btn"
                onClick={() => {
                  const targetFile = selectedError.file
                  const fileErrors = compileErrors.filter(e => e.file === targetFile)
                  startAnalyze(bundleJobIds?.pfJobId, targetFile, fileErrors)
                }}
                disabled={
                  analyzeState.phase === STATE_LOADING ||
                  analyzeState.phase === STATE_APPLYING
                }
              >
                <RefreshCw size={12} /> Regenerate
              </button>
              <button className="rcw-ai-close-btn" onClick={handleCloseAIModal}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── AI: Generate Java Code modal (COBOL → Java) ──────────────────── */}
      <AIResponseModal
        isOpen={generateJavaModal.isOpen}
        onClose={() => setGenerateJavaModal(prev => ({ ...prev, isOpen: false }))}
        title="Generate Java Code"
        mode="generate"
        selectedCode={generateJavaModal.selectedCode}
        sectionName={generateJavaModal.sectionName}
        aiResponse={generateJavaModal.response}
        aiLoading={generateJavaModal.loading}
        onRegenerate={handleRegenerateJava}
        onAccept={handleAcceptGeneratedJava}
      />

      {/* ── Explorer right-click context menu ─────────────────────────────── */}
      {explorerCtxMenu && (() => {
        const ctx = explorerCtxMenu

        // Shared folder-download helper
        const downloadFolder = async (folderPath, zipName) => {
          setExplorerCtxMenu(null)
          if (!bundleJobIds?.pfJobId) return
          try {
            const params = new URLSearchParams({ gate_job_id: bundleJobIds.pfJobId, folder_path: folderPath })
            const r = await fetch(`${apiBase}/editor/folder-download?${params}`, { headers: getAuthHeaders() })
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            const blob = await r.blob()
            const href = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = href; a.download = zipName
            document.body.appendChild(a); a.click()
            document.body.removeChild(a); URL.revokeObjectURL(href)
          } catch (e) { console.error('[Explorer] folder download failed:', e.message) }
        }

        // ── FILE context menu ──────────────────────────────────────────────
        if (ctx.type === 'file' || ctx.file) {
          const file = ctx.file
          const fileFolderPath = file.path.includes('/')
            ? file.path.substring(0, file.path.lastIndexOf('/'))
            : ''
          const fileFolderZip = (fileFolderPath ? fileFolderPath.split('/').pop() : 'root') + '.zip'
          return (
            <div className="rcw-explorer-ctx-menu" style={{ top: ctx.y, left: ctx.x }} onMouseDown={e => e.stopPropagation()}>
              <div className="rcw-explorer-ctx-header">📄 {file.filename}</div>
              <div className="rcw-explorer-ctx-sep" />
              <div className="rcw-explorer-ctx-section">Project</div>
              <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); openFile(file.path) }}>Open in Editor</button>
              <button className="rcw-explorer-ctx-item" disabled={rerunning || !bundleJobIds} onClick={() => { setExplorerCtxMenu(null); handleRerunWithSave() }}>Re-run Compile Check</button>
              <div className="rcw-explorer-ctx-sep" />
              <div className="rcw-explorer-ctx-section">Download</div>
              <button className="rcw-explorer-ctx-item" onClick={() => handleExplorerCtxDownload(file)}>Download File</button>
              <button className="rcw-explorer-ctx-item" disabled={!bundleJobIds?.pfJobId} onClick={() => downloadFolder(fileFolderPath, fileFolderZip)}>Download Folder</button>
              <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); handleWorkbenchDownload() }}>Download Project</button>
              <div className="rcw-explorer-ctx-sep" />
              <div className="rcw-explorer-ctx-section">Manage</div>
              <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); setExplorerRenameState({ path: file.path, value: file.filename }) }}>Rename</button>
              <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); const f = file.folder || '__root__'; setExplorerNewFileState({ folder: f, value: '' }); setTreeExpanded(p => ({ ...p, [f]: true })) }}>New File…</button>
            </div>
          )
        }

        // ── FOLDER context menu ────────────────────────────────────────────
        if (ctx.type === 'folder') {
          const zipName = (ctx.folderPath ? ctx.folderPath.split('/').pop() : 'project') + '.zip'
          return (
            <div className="rcw-explorer-ctx-menu" style={{ top: ctx.y, left: ctx.x }} onMouseDown={e => e.stopPropagation()}>
              <div className="rcw-explorer-ctx-header">📁 {ctx.label}</div>
              <div className="rcw-explorer-ctx-sep" />
              <div className="rcw-explorer-ctx-section">Project</div>
              <button className="rcw-explorer-ctx-item" disabled={rerunning || !bundleJobIds} onClick={() => { setExplorerCtxMenu(null); handleRerunWithSave() }}>Re-run Compile Check</button>
              <div className="rcw-explorer-ctx-sep" />
              <div className="rcw-explorer-ctx-section">Download</div>
              <button className="rcw-explorer-ctx-item" disabled={!bundleJobIds?.pfJobId} onClick={() => downloadFolder(ctx.folderPath, zipName)}>Download Folder</button>
              <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); handleWorkbenchDownload() }}>Download Project</button>
              <div className="rcw-explorer-ctx-sep" />
              <div className="rcw-explorer-ctx-section">Manage</div>
              <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); setExplorerNewFileState({ folder: ctx.folder, value: '' }); setTreeExpanded(p => ({ ...p, [ctx.folder]: true })) }}>New File…</button>
            </div>
          )
        }

        // ── ROOT context menu ──────────────────────────────────────────────
        return (
          <div className="rcw-explorer-ctx-menu" style={{ top: ctx.y, left: ctx.x }} onMouseDown={e => e.stopPropagation()}>
            <div className="rcw-explorer-ctx-header">📁 {ctx.label}</div>
            <div className="rcw-explorer-ctx-sep" />
            <div className="rcw-explorer-ctx-section">Project</div>
            <button className="rcw-explorer-ctx-item" disabled={rerunning || !bundleJobIds} onClick={() => { setExplorerCtxMenu(null); handleRerunWithSave() }}>Re-run Compile Check</button>
            <div className="rcw-explorer-ctx-sep" />
            <div className="rcw-explorer-ctx-section">Download</div>
            <button className="rcw-explorer-ctx-item" onClick={() => { setExplorerCtxMenu(null); handleWorkbenchDownload() }}>Download Project</button>
          </div>
        )
      })()}

      {/* ── Save confirmation modal ───────────────────────────────────────── */}
      {saveConfirmModal && (
        <div className="rcw-save-overlay" onClick={() => setSaveConfirmModal(null)}>
          <div className="rcw-save-dialog" onClick={e => e.stopPropagation()}>
            <p className="rcw-save-dialog-title">Save changes?</p>
            <p className="rcw-save-dialog-body">
              Save changes to{' '}
              <span className="rcw-save-dialog-filename">{saveConfirmModal.filename}</span>?
            </p>
            <div className="rcw-save-dialog-actions">
              <button
                className="rcw-save-dialog-btn rcw-save-dialog-btn--cancel"
                onClick={() => setSaveConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                className="rcw-save-dialog-btn rcw-save-dialog-btn--save"
                onClick={handleConfirmSave}
              >
                <Save size={13} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// ─── CompileReportView ────────────────────────────────────────────────────────
// Full-height report page rendered inside WorkbenchIDE when showReport is true.
// Reads compileErrors from parent state (no new fetches).
// Fix button → onFixError(err) → sets selectedError + aiFixOpen in WorkbenchIDE.
// Re-run Compile → onRerun() → same handler as header Re-Run Build.

function CompileReportView({ compileErrors, totalErrors = null, onBack, onFixError, onRerun, rerunning, rerunStatus }) {
  // totalErrors = real Maven count; compileErrors.length = preview rows (≤ 100)
  const displayTotal = totalErrors ?? compileErrors.length
  // Group errors by filename, sorted by error count descending (worst files first)
  const errorsByFile = compileErrors.reduce((acc, err) => {
    if (!acc[err.file]) acc[err.file] = []
    acc[err.file].push(err)
    return acc
  }, {})
  const sortedFiles = Object.entries(errorsByFile)
    .sort(([, a], [, b]) => b.length - a.length)

  return (
    <div className="rcw-report-page">

      {/* ── Report header ── */}
      <div className="rcw-report-header">
        <button className="rcw-report-back-btn" onClick={onBack}>
          <ArrowLeft size={13} /> Back to IDE
        </button>
        <div className="rcw-report-title">
          <XCircle size={15} className="rcw-report-title-icon" />
          <span>Compile Report</span>
        </div>
        <div className="rcw-report-header-right">
          <button
            className={`rcw-bar-btn rcw-bar-btn--rerun${rerunning ? ' running' : rerunStatus === 'passed' ? ' passed' : ''}`}
            onClick={onRerun}
            disabled={rerunning}
          >
            {rerunning
              ? <><Loader2 size={13} className="rcv-spin" /> Building…</>
              : rerunStatus === 'passed'
              ? <><CheckCircle2 size={13} /> Build Passed</>
              : <><Play size={13} /> Re-Run Build</>}
          </button>
        </div>
      </div>

      {/* ── Summary bar ── */}
      <div className="rcw-report-summary">
        <span className="rcw-report-stat rcw-report-stat--err">
          <XCircle size={12} />
          {displayTotal} error{displayTotal !== 1 ? 's' : ''}
          {displayTotal > compileErrors.length && (
            <span className="rcw-report-stat-note"> (showing first {compileErrors.length})</span>
          )}
        </span>
        <span className="rcw-report-stat-sep">·</span>
        <span className="rcw-report-stat">
          <FileCode size={12} />
          {sortedFiles.length} file{sortedFiles.length !== 1 ? 's' : ''} affected
        </span>
        {rerunStatus === 'passed' && compileErrors.length === 0 && (
          <>
            <span className="rcw-report-stat-sep">·</span>
            <span className="rcw-report-stat rcw-report-stat--pass">
              <CheckCircle2 size={12} /> Build clean
            </span>
          </>
        )}
      </div>

      {/* ── No-errors state ── */}
      {compileErrors.length === 0 && (
        <div className="rcw-report-empty">
          <CheckCircle2 size={36} />
          <span>No compile errors — build is clean</span>
        </div>
      )}

      {/* ── Per-file error sections ── */}
      <div className="rcw-report-body">
        {sortedFiles.map(([filename, errors]) => (
          <div key={filename} className="rcw-report-file-block">

            <div className="rcw-report-file-header">
              <FaJava size={13} className="rcw-fi-java" />
              <span className="rcw-report-file-name">{filename}</span>
              <span className="rcw-report-file-count">
                {errors.length} error{errors.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="rcw-report-error-list">
              {errors
                .slice()
                .sort((a, b) => a.line - b.line)
                .map((err, i) => (
                  <div key={i} className="rcw-report-error-row">
                    <XCircle size={11} className="rcw-report-err-icon" />
                    <span className="rcw-report-err-loc">:{err.line}</span>
                    <span className="rcw-report-err-msg">{err.message}</span>
                    <button
                      className="rcw-report-fix-btn"
                      onClick={() => onFixError(err)}
                      title="Open AI Fix for this error"
                    >
                      <Sparkles size={11} /> Fix
                    </button>
                  </div>
                ))}
            </div>

          </div>
        ))}
      </div>

    </div>
  )
}
